import React, { useState, useEffect, useCallback, useRef } from "react";
import api from "../api";
import { MarkdownEditor, ImageUploadZone } from "./ProductEditor";
import ImageEditorModal from "./ImageEditorModal";
import VideoTrimmer from "./VideoTrimmer";
import DistrictSelect from "./DistrictSelect";
import ProductGridEditor, { OptionCategoryPreview, BUTTON_PRESETS as GRID_BUTTON_PRESETS } from "./ProductGridEditor";
import { isAdminRole } from "../utils/roles";
const DesignCanvas = React.lazy(() => import("./DesignCanvas"));

/* ── ErrorBoundary: prevents white-screen on render crashes ── */
class SectionErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(err, info) { console.error("[SectionErrorBoundary]", err, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div className="px-6 py-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          ⚠️ 이 섹션을 렌더링할 수 없습니다: {String(this.state.error?.message || "알 수 없는 오류").slice(0, 200)}
          <button onClick={() => this.setState({ hasError: false, error: null })} className="ml-2 text-xs underline text-red-500">재시도</button>
        </div>
      );
    }
    return this.props.children;
  }
}

/* ── Safe string: prevent rendering objects/arrays directly in JSX ── */
const safeStr = (v) => (v == null ? "" : typeof v === "object" ? JSON.stringify(v) : String(v));

/* ── LoopVideo: handles loop/once/N-times playback with transitions ── */
function LoopVideo({ src, className, loop = "loop", transition = "cut" }) {
  const ref = React.useRef(null);
  const overlayRef = React.useRef(null);
  const countRef = React.useRef(0);
  const maxPlays = loop === "loop" ? Infinity : loop === "once" ? 1 : parseInt(loop, 10) || Infinity;
  const isInfinite = maxPlays === Infinity;
  React.useEffect(() => { countRef.current = 0; }, [src, loop]);

  const doTransition = React.useCallback(() => {
    const el = overlayRef.current;
    if (!el) return;
    if (transition === "fade-black") {
      el.style.opacity = "1"; el.style.background = "#000";
      setTimeout(() => { el.style.opacity = "0"; }, 300);
    } else if (transition === "fade-white") {
      el.style.opacity = "1"; el.style.background = "#fff";
      setTimeout(() => { el.style.opacity = "0"; }, 300);
    } else if (transition === "crossfade") {
      if (ref.current) { ref.current.style.opacity = "0"; }
      setTimeout(() => { if (ref.current) ref.current.style.opacity = "1"; }, 50);
    }
  }, [transition]);

  const onEnded = React.useCallback(() => {
    countRef.current += 1;
    if (countRef.current < maxPlays && ref.current) {
      doTransition();
      ref.current.currentTime = 0;
      ref.current.play().catch(() => {});
    }
  }, [maxPlays, doTransition]);
  const needsOverlay = transition && transition !== "cut";
  return (
    <div className="relative inline-block">
      <video ref={ref} src={src} className={`${className} transition-opacity duration-300`} muted autoPlay playsInline loop={isInfinite && transition === "cut"} onEnded={isInfinite && transition === "cut" ? undefined : onEnded} />
      {needsOverlay && <div ref={overlayRef} className="absolute inset-0 pointer-events-none opacity-0 rounded-[inherit]" style={{ transition: "opacity 0.3s ease-in-out" }} />}
    </div>
  );
}

/* Font size mapping for preview */
const FONT_SIZE_MAP = {
  sm: { title: "text-lg", desc: "text-xs" },
  md: { title: "text-xl", desc: "text-sm" },
  lg: { title: "text-2xl", desc: "text-sm" },
  xl: { title: "text-3xl md:text-4xl", desc: "text-base" },
  "2xl": { title: "text-4xl md:text-5xl", desc: "text-lg" },
};

/* ═══════════════════════════════════════════════════════════
   상세페이지 편집기 + 실시간 미리보기
   - 새로 작성 / 기존 수정 모두 지원
   - 대표 이미지: URL 입력 + 파일 업로드 모두 지원
   - 모든 필드 편집 가능
   - 우측 실시간 미리보기 패널
   ═══════════════════════════════════════════════════════════ */

const card = "bg-white rounded-2xl border border-gray-200/80 shadow-sm";
const btn = "px-4 py-2 rounded-xl text-sm font-semibold transition-all disabled:opacity-40";
const inp = "w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none";
const label = "block text-xs font-semibold text-gray-500 mb-1";

/* ── Industry-specific store categories ── */
const STORE_CATS_MAP = {
  사무기기: ["고속인쇄기", "디자인용복합기", "사무용복합기", "소형복합기", "프린터", "토너소모품", "기타"],
};
const SUB_CATS_MAP = {
  사무기기: ["토너", "드럼", "현상기", "정착기", "부품", "종이"],
};
const STORE_CATS = STORE_CATS_MAP["사무기기"]; // default fallback
const SUB_CATS = SUB_CATS_MAP["사무기기"];
const CUSTOM_BRAND_VALUE = "__custom_brand__";
const OFFICE_BRAND_OPTIONS = [
  { value: "FUJIFILM", label: "FUJIFILM" },
  { value: "FUJIXEROX", label: "FUJIXEROX" },
  { value: "삼성전자", label: "삼성전자" },
  { value: "HP (한국HP)", label: "HP (한국HP)" },
  { value: "캐논 (Canon)", label: "캐논 (Canon)" },
  { value: "브라더 (Brother)", label: "브라더 (Brother)" },
  { value: "신도리코 (SINDOH)", label: "신도리코 (SINDOH)" },
  { value: "제록스 (Xerox)", label: "제록스 (Xerox)" },
  { value: "교세라 (Kyocera)", label: "교세라 (Kyocera)" },
  { value: "렉스마크 (Lexmark)", label: "렉스마크 (Lexmark)" },
  { value: "RICOH", label: "RICOH" },
  { value: "EPSON", label: "EPSON" },
  { value: "OKI", label: "OKI" },
];

function BrandSelect({ value, onChange, className = inp, placeholder = "브랜드 선택" }) {
  const currentValue = value || "";
  const hasCurrent = currentValue && !OFFICE_BRAND_OPTIONS.some(b => b.value === currentValue);
  const handleChange = (e) => {
    const next = e.target.value;
    if (next === CUSTOM_BRAND_VALUE) {
      const custom = window.prompt("브랜드명을 입력하세요", hasCurrent ? currentValue : "");
      if (custom !== null) onChange(custom.trim());
      return;
    }
    onChange(next);
  };
  return (
    <select value={currentValue} onChange={handleChange} className={className}>
      <option value="">{placeholder}</option>
      {hasCurrent && <option value={currentValue}>{currentValue} (현재값)</option>}
      {OFFICE_BRAND_OPTIONS.map(brand => (
        <option key={brand.value} value={brand.value}>{brand.label}</option>
      ))}
      <option value={CUSTOM_BRAND_VALUE}>직접 입력...</option>
    </select>
  );
}

const parseOptionQuantity = (name) => {
  const match = String(name || "").replace(/[,\s]/g, "").match(/(\d+)/);
  return match ? Number(match[1]) : 1;
};

const isOutputCountOptionGroup = (group) => {
  const text = `${group?.group_name || ""} ${group?.group_description || ""}`;
  if (!/(출력\s*매수|출력매수|흑백|칼라|컬러|매수)/i.test(text)) return false;
  return (group?.options || []).some(option => /[0-9][0-9,\s]*\s*매/.test(String(option?.name || "")));
};

const getOptionGroupPriceType = (group) => {
  if (group?.price_type) return group.price_type;
  return isOutputCountOptionGroup(group) ? "per_unit" : "fixed";
};

const formatOptionPriceLabel = (group, option, unitPrice) => {
  const price = Number(unitPrice);
  if (!(price > 0)) return option?.price_label || "";
  const amount = getOptionGroupPriceType(group) === "per_unit" ? price * parseOptionQuantity(option?.name) : price;
  return `${getOptionGroupPriceType(group) === "per_unit" ? "합계 " : ""}${Math.round(amount).toLocaleString()}원`;
};

const updateOptionUnitPrices = (group, unitPrice) => {
  const price = Number(String(unitPrice ?? "").replace(/,/g, ""));
  if (!Number.isFinite(price) || price < 0) return group;
  const priceType = getOptionGroupPriceType(group);
  return {
    ...group,
    price_type: priceType,
    options: (group.options || []).map(option => {
      const nextOption = { ...option, unit_price: price };
      return { ...nextOption, price_label: formatOptionPriceLabel({ ...group, price_type: priceType }, nextOption, price) };
    }),
  };
};

const multiplyOptionGroupPrices = (group, factor) => {
  const multiplier = Number(String(factor ?? "").replace(/,/g, ""));
  if (!Number.isFinite(multiplier) || multiplier <= 0) return group;
  const priceType = getOptionGroupPriceType(group);
  return {
    ...group,
    price_type: priceType,
    options: (group.options || []).map(option => {
      const current = Number(option?.unit_price);
      if (!(current > 0)) return option;
      const nextPrice = Math.round(current * multiplier);
      const nextOption = { ...option, unit_price: nextPrice };
      return { ...nextOption, price_label: formatOptionPriceLabel({ ...group, price_type: priceType }, nextOption, nextPrice) };
    }),
  };
};

const SECTION_TEMPLATES = {
  design_canvas: { type: "design_canvas", title: "디자인 캔버스", canvas_json: null, dataUrl: "" },
  rich_html: { type: "rich_html", title: "", content: "" },
  text: { type: "text", title: "", content: "" },
  feature_highlight: { type: "feature_highlight", title: "주요 특징", items: [{ icon: "⚡", title: "", description: "", highlight_number: "" }] },
  spec_table: { type: "spec_table", title: "상세 사양", columns: ["항목", "사양"], rows: [["", ""]] },
  product_card: { type: "product_card", product_name: "", tagline: "", description: "", image_url: "", features: [], highlight_spec: { value: "", label: "" } },
  rental_plans: {
    type: "rental_plans", title: "렌탈 요금제", plans: [{
      plan_no: "01", plan_name: "기본형", model_label: "", monthly_price: 0,
      deposit: 200000, included_pages: "", color_pages: "", contract_period: "3년",
      options: "", extra_bw_price: 15, extra_color_price: 80, note: ""
    }]
  },
  rental_calculator: { type: "rental_calculator", title: "예상 비용 계산", description: "월간 예상 출력매수를 입력하면 비용을 비교할 수 있습니다.", note: "" },
  cta: { type: "cta", title: "지금 문의하세요", description: "", button_text: "견적 문의", button_link: "/consultation" },
  /* ── 판매 가격표 (엑셀: 판매 복사기) ── */
  sale_pricing: {
    type: "sale_pricing", title: "판매 가격표",
    products: [{ product_name: "", brand: "", body_price: 0, delivery_seoul: 0, delivery_other: 0, fax_kit: 0, image_url: "" }]
  },
  /* ── 렌탈 가격표 (엑셀: 렌탈 복사기) ── */
  rental_pricing: {
    type: "rental_pricing", title: "렌탈 요금표",
    products: [{
      product_name: "", brand: "", body_price: 0, delivery_seoul: 0, delivery_other: 0, fax_kit: 0,
      periods: { 12: 0, 24: 0, 36: 0, 48: 0, 60: 0 },
      color_per_page: 0, bw_per_page: 0, color_pages: 50, bw_pages: 1000, image_url: "",
    }]
  },
  /* ── 토너/소모품 정보 (엑셀: 토너) ── */
  toner_spec: {
    type: "toner_spec", title: "토너/소모품 정보",
    products: [{ product_name: "", brand: "", genuine_type: "정품", ink_type: "토너", color: "", model_number: "", market_price: 0, image_url: "" }]
  },
  /* ── 비주얼 블록: 이미지+텍스트 (HTML 불필요) ── */
  image_text_block: {
    type: "image_text_block", title: "", description: "", image_url: "",
    video_url: "", video_loop: "loop", video_transition: "cut", image_position: "right", cta_text: "", cta_link: "",
    features: []
  },
  /* ── 비주얼 블록: 제목+설명 (HTML 불필요) ── */
  heading_text: {
    type: "heading_text", heading: "", subheading: "", description: "",
    align: "center", bg_style: "white"
  },
  /* ── 옵션 선택기 (프리미엄 스타일 구성기) ── */
  product_options: {
    type: "product_options", title: "원하는 옵션을 선택하세요.",
    option_groups: [{
      group_name: "모델", group_type: "card",
      options: [{ name: "", description: "", price_modifier: 0, image_url: "" }]
    }]
  },
  /* ── 프리미엄 쇼케이스: 이미지(좌) + 옵션(우) 2단 레이아웃 ── */
  product_showcase: {
    type: "product_showcase", title: "",
    images: [""],
    option_groups: [
      { group_name: "색상", group_type: "swatch", options: [{ name: "", color_hex: "#000000", image_url: "" }] },
      { group_name: "저장 용량", group_type: "list", options: [{ name: "", price_label: "" }] },
    ],
    price_label: "", sub_info: ["무료 배송", "매장에서 픽업"],
  },
  /* ── 4분면 그리드 레이아웃 ── */
  product_grid: {
    type: "product_grid", gridLayout: "2x1",
    cells: [{ blocks: [] }, { blocks: [] }]
  },
  /* ── 4분면 (2×2) 그리드 ── */
  product_grid_2x2: {
    type: "product_grid", gridLayout: "2x2",
    cells: [{ blocks: [] }, { blocks: [] }, { blocks: [] }, { blocks: [] }]
  },
  /* ── 식품: 영양성분표 ── */
  food_nutrition: {
    type: "spec_table", title: "영양성분표",
    columns: ["항목", "함량", "% 일일 권장량"],
    rows: [
      ["열량", "", ""], ["탄수화물", "", ""], ["당류", "", ""],
      ["단백질", "", ""], ["지방", "", ""], ["포화지방", "", ""],
      ["트랜스지방", "", ""], ["콜레스테롤", "", ""],
      ["나트륨", "", ""], ["식이섬유", "", ""]
    ]
  },
  /* ── 식품: 원산지/이력 정보 ── */
  food_origin: {
    type: "spec_table", title: "원산지 · 이력 정보",
    columns: ["항목", "상세"],
    rows: [
      ["원산지", ""], ["생산자", ""], ["인증", ""],
      ["유통기한", ""], ["보관방법", ""],
      ["알레르기 성분", ""], ["GMO 여부", "비해당"]
    ]
  },
  /* ── 식품: 특징 하이라이트 (채식) ── */
  food_features_veg: {
    type: "feature_highlight", title: "채식 특징",
    items: [
      { icon: "🌱", title: "100% 식물성", description: "동물성 원료 무첨가", highlight_number: "" },
      { icon: "🌿", title: "유기농 인증", description: "국내 유기농 인증 획득", highlight_number: "" },
      { icon: "♻️", title: "친환경 포장", description: "생분해성 포장재 사용", highlight_number: "" },
      { icon: "🧑‍🍳", title: "간편 조리", description: "5분 이내 간편 조리", highlight_number: "" },
    ]
  },
  /* ── 식품: 특징 하이라이트 (육류) ── */
  food_features_meat: {
    type: "feature_highlight", title: "프리미엄 육류 특징",
    items: [
      { icon: "🥩", title: "1++ 등급", description: "최상급 마블링", highlight_number: "" },
      { icon: "🐄", title: "국내산 한우", description: "이력번호 추적 가능", highlight_number: "" },
      { icon: "❄️", title: "신선 냉장 배송", description: "도축 후 48시간 이내", highlight_number: "" },
      { icon: "🛡️", title: "HACCP 인증", description: "위생 안전 관리 인증", highlight_number: "" },
    ]
  },
  /* ── 식품: 특징 하이라이트 (젓갈) ── */
  food_features_jeotgal: {
    type: "feature_highlight", title: "전통 젓갈 특징",
    items: [
      { icon: "🧂", title: "전통 방식 숙성", description: "천일염으로 자연 숙성", highlight_number: "" },
      { icon: "🦐", title: "국내산 원료", description: "서해안 직접 조달", highlight_number: "" },
      { icon: "🏺", title: "무첨가 발효", description: "화학첨가물 제로", highlight_number: "" },
      { icon: "👨‍🍳", title: "장인 손맛", description: "3대 가업 비법 전수", highlight_number: "" },
    ]
  },
};

const SECTION_LABELS = {
  rich_html: "✏️ 자유 편집", text: "텍스트", feature_highlight: "특징 하이라이트",
  spec_table: "스펙 테이블", product_card: "제품 카드",
  rental_plans: "렌탈 요금제", rental_calculator: "비용 계산기", cta: "CTA 버튼",
  sale_pricing: "💰 판매가격표", rental_pricing: "📋 렌탈가격표", toner_spec: "🔴 토너/소모품",
  image_text_block: "🖼️ 이미지+텍스트", heading_text: "📝 제목+설명", product_options: "⚙️ 옵션 선택기",
  product_showcase: "📱 쇼케이스 (이미지+옵션)",
  product_grid: "🔲 그리드 레이아웃",
  food_nutrition: "🥗 영양성분표", food_origin: "📍 원산지정보",
  food_features_veg: "🌱 채식 특징", food_features_meat: "🥩 육류 특징",
  food_features_jeotgal: "🦐 젓갈 특징",
  design_canvas: "🎨 디자인 캔버스",
};

const COPIER_SPEC_PRESET_ROWS = [
  ["제품 규격", "A3 컬러 복합기"],
  ["기능", "복사, 프린트, 스캔, 송신 (팩스 옵션)"],
  ["출력 속도", "분당 70매 (컬러/흑백)"],
  ["스캔 속도", "분당 135매 (컬러/흑백)"],
  ["해상도", "1,200×2,400 dpi"],
  ["조작 패널", "10.1인치 컬러 터치스크린"],
  ["용지 무게", "52~300 gsm"],
  ["용지 크기", "100×148 mm ~ 320×457 mm"],
  ["맥 지원", "옵션"],
  ["크기 (W x D x H)", "658 x 720 x 1,119 mm"],
];

const getSpecColumns = (sec) => {
  const rawRows = Array.isArray(sec?.rows) ? sec.rows : [];
  const maxRowLength = rawRows.reduce((max, row) => {
    if (Array.isArray(row)) return Math.max(max, row.length);
    if (row && typeof row === "object") return Math.max(max, Object.values(row).length);
    return max;
  }, 0);
  const base = Array.isArray(sec?.columns) && sec.columns.length
    ? sec.columns
    : Array.isArray(sec?.headers) && sec.headers.length
      ? sec.headers
      : ["항목", "사양"];
  const colCount = Math.max(base.length, maxRowLength, 2);
  return Array.from({ length: colCount }, (_, i) => safeStr(base[i] || (i === 0 ? "항목" : i === 1 ? "사양" : `열 ${i + 1}`)));
};

const getSpecRows = (sec, colCount) => {
  const rawRows = Array.isArray(sec?.rows) ? sec.rows : [];
  return rawRows.map((row) => {
    const cells = Array.isArray(row) ? row : Object.values(row || {});
    return Array.from({ length: colCount }, (_, i) => safeStr(cells[i] || ""));
  });
};

function SpecTableForm({ sec, onPatch }) {
  const columns = getSpecColumns(sec);
  const rows = getSpecRows(sec, columns.length);
  const simpleMode = columns.length <= 2;

  const patchColumns = (nextColumns, nextRows = rows) => onPatch({ columns: nextColumns, rows: nextRows });
  const patchRows = (nextRows) => onPatch({ rows: nextRows });
  const updateCell = (rowIdx, colIdx, value) => {
    const nextRows = rows.map((row, i) => i === rowIdx ? row.map((cell, j) => j === colIdx ? value : cell) : row);
    patchRows(nextRows);
  };
  const moveRow = (rowIdx, direction) => {
    const to = rowIdx + direction;
    if (to < 0 || to >= rows.length) return;
    const nextRows = [...rows];
    [nextRows[rowIdx], nextRows[to]] = [nextRows[to], nextRows[rowIdx]];
    patchRows(nextRows);
  };
  const addRow = () => patchRows([...rows, Array(columns.length).fill("")]);
  const removeRow = (rowIdx) => patchRows(rows.filter((_, i) => i !== rowIdx));
  const applyPreset = () => {
    const hasContent = rows.some(row => row.some(cell => String(cell || "").trim()));
    if (hasContent && !window.confirm("현재 표 내용을 복합기 기본 사양 행으로 바꿀까요?")) return;
    onPatch({ title: sec.title || "상세 사양", columns: ["항목", "사양"], rows: COPIER_SPEC_PRESET_ROWS });
  };
  const convertToSimple = () => {
    const nextRows = rows.map(row => [row[0] || "", row[1] || ""]);
    onPatch({ columns: ["항목", "사양"], rows: nextRows.length ? nextRows : [["", ""]] });
  };

  return (
    <div className="space-y-3">
      <div>
        <label className={label}>표 제목</label>
        <input value={sec.title || ""} onChange={e => onPatch({ title: e.target.value })}
          placeholder="상세 사양" className={inp} />
      </div>

      <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-3 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div>
            <p className="text-xs font-bold text-emerald-800">상세 사양 폼</p>
            <p className="text-[11px] text-emerald-700">화면에 보이는 표를 행 단위로 편집합니다.</p>
          </div>
          <button onClick={applyPreset}
            className="px-2.5 py-1.5 rounded-lg bg-white border border-emerald-200 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-100">
            복합기 기본 행
          </button>
        </div>

        {!simpleMode && (
          <div className="rounded-lg bg-white border border-amber-200 p-3 flex items-center justify-between gap-3">
            <p className="text-[12px] text-amber-700">이 표는 열이 3개 이상이라 고급 표로 편집 중입니다.</p>
            <button onClick={convertToSimple}
              className="px-2.5 py-1.5 rounded-lg bg-amber-600 text-white text-[11px] font-semibold hover:bg-amber-700">
              2열 사양표로 전환
            </button>
          </div>
        )}

        {simpleMode ? (
          <>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[11px] font-semibold text-gray-500">왼쪽 제목</label>
                <input value={columns[0] || "항목"} onChange={e => patchColumns([e.target.value, columns[1] || "사양"])}
                  className="w-full rounded-lg border border-emerald-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-300" />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-gray-500">오른쪽 제목</label>
                <input value={columns[1] || "사양"} onChange={e => patchColumns([columns[0] || "항목", e.target.value])}
                  className="w-full rounded-lg border border-emerald-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-300" />
              </div>
            </div>

            <div className="space-y-2">
              {rows.map((row, rowIdx) => (
                <div key={rowIdx} className="rounded-xl border border-gray-200 bg-white p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-bold text-gray-500">행 #{rowIdx + 1}</span>
                    <div className="flex items-center gap-1">
                      <button onClick={() => moveRow(rowIdx, -1)} disabled={rowIdx === 0}
                        className="px-2 py-1 rounded bg-gray-50 text-[11px] text-gray-500 hover:bg-gray-100 disabled:opacity-30">위</button>
                      <button onClick={() => moveRow(rowIdx, 1)} disabled={rowIdx === rows.length - 1}
                        className="px-2 py-1 rounded bg-gray-50 text-[11px] text-gray-500 hover:bg-gray-100 disabled:opacity-30">아래</button>
                      <button onClick={() => removeRow(rowIdx)}
                        className="px-2 py-1 rounded bg-red-50 text-[11px] font-semibold text-red-500 hover:bg-red-100">삭제</button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-[0.9fr_1.5fr] gap-2">
                    <input value={row[0] || ""} onChange={e => updateCell(rowIdx, 0, e.target.value)}
                      placeholder="예: 출력 속도" className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-300" />
                    <textarea value={row[1] || ""} onChange={e => updateCell(rowIdx, 1, e.target.value)}
                      placeholder="예: 분당 70매 (컬러/흑백)" rows={2}
                      className="rounded-lg border border-gray-200 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-300" />
                  </div>
                </div>
              ))}
            </div>
            <button onClick={addRow}
              className="w-full py-2 rounded-xl border-2 border-dashed border-emerald-300 bg-white text-xs font-semibold text-emerald-700 hover:bg-emerald-50">
              + 사양 행 추가
            </button>
          </>
        ) : (
          <div className="space-y-3">
            <div className="flex gap-2 flex-wrap">
              {columns.map((col, colIdx) => (
                <input key={colIdx} value={col} onChange={e => {
                  const nextColumns = columns.map((item, i) => i === colIdx ? e.target.value : item);
                  patchColumns(nextColumns);
                }} className="w-28 rounded-lg border border-gray-200 px-3 py-2 text-xs font-semibold" />
              ))}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse bg-white">
                <tbody>
                  {rows.map((row, rowIdx) => (
                    <tr key={rowIdx}>
                      {columns.map((_, colIdx) => (
                        <td key={colIdx} className="border border-gray-200 p-1">
                          <input value={row[colIdx] || ""} onChange={e => updateCell(rowIdx, colIdx, e.target.value)}
                            className="w-full min-w-[100px] px-2 py-1 outline-none" />
                        </td>
                      ))}
                      <td className="border border-gray-200 p-1 text-center">
                        <button onClick={() => removeRow(rowIdx)} className="text-red-400">삭제</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button onClick={addRow} className="text-[12px] text-blue-600 font-medium hover:underline">+ 행 추가</button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── 식품 페이지 프리셋 (한 번에 여러 섹션 추가) ── */
const FOOD_PAGE_PRESETS = {
  vegetarian: {
    label: "🌱 채식 상품", description: "채식/비건 식품 전용 프리셋",
    theme: "warm",
    sections: ["food_features_veg", "image_text_block", "food_nutrition", "food_origin", "cta"],
  },
  meat: {
    label: "🥩 정육/축산", description: "프리미엄 육류 전용 프리셋",
    theme: "bold",
    sections: ["food_features_meat", "image_text_block", "food_nutrition", "food_origin", "cta"],
  },
  jeotgal: {
    label: "🦐 젓갈/발효", description: "젓갈·장류·발효식품 프리셋",
    theme: "warm",
    sections: ["food_features_jeotgal", "image_text_block", "food_nutrition", "food_origin", "cta"],
  },
  fruit: {
    label: "🍎 과일/채소", description: "신선 과일·채소 프리셋",
    theme: "warm",
    sections: ["food_features_veg", "image_text_block", "food_nutrition", "food_origin", "cta"],
  },
  seafood: {
    label: "🐟 수산물", description: "신선 수산물 프리셋",
    theme: "bold",
    sections: ["food_features_jeotgal", "image_text_block", "food_nutrition", "food_origin", "cta"],
  },
  product_option: {
    label: "🛒 상품+옵션", description: "상품 이미지 + 옵션 선택기",
    sections: ["image_text_block", "product_options"],
  },
  grid_2x2: {
    label: "📐 4분면 배치", description: "2×2 그리드 (4칸 레이아웃)",
    sections: ["product_grid_2x2"],
  },
};

/* ── Blank page template ── */
/* ── Theme presets per category ── */
const PAGE_THEMES = {
  minimal:   { key: "minimal",     label: "프리미엄 미니멀", icon: "✨", desc: "깔끔한 여백, 대형 타이포, 프리미엄" },
  corporate: { key: "corporate", label: "기업 신뢰형",  icon: "🏢", desc: "블루톤, 데이터 중심, 스펙 강조" },
  bold:      { key: "bold",      label: "Bold 임팩트",  icon: "💥", desc: "강렬한 색상, 큰 숫자, 파워풀" },
  warm:      { key: "warm",      label: "따뜻한 내추럴", icon: "🌿", desc: "따뜻한 톤, 자연친화, 친근한" },
  tech:      { key: "tech",      label: "테크 다크",    icon: "🌑", desc: "다크 배경, 네온 포인트, 하이테크" },
  catalog:   { key: "catalog",   label: "카탈로그 실용", icon: "📋", desc: "정보 밀도 높음, 비교표 중심" },
  luxury:    { key: "luxury",    label: "럭셔리 프리미엄", icon: "✨", desc: "골드톤, 고급감, 우아한" },
};

/* Auto-suggest theme from category/mode */
function suggestTheme(category = "", mode = "") {
  const cat = (category + " " + mode).toLowerCase();
  if (/토너|소모품|부품/.test(cat)) return "catalog";
  if (/채소|과일|유기농|식품|식량/.test(cat)) return "warm";
  if (/정육|축산|수산/.test(cat)) return "bold";
  if (/방산|전차|화포|드론|무인/.test(cat)) return "tech";
  if (/소프트웨어|saas|솔루션|사이버/.test(cat)) return "corporate";
  if (/우주|위성|발사/.test(cat)) return "tech";
  if (/디지털인쇄/.test(cat)) return "luxury";
  return "minimal";
}

/* ── Compatibility Panel (호환 제품 매핑) ── */
function CompatibilityPanel({ pageId }) {
  const [compats, setCompats] = useState([]);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/admin/product-pages/${pageId}/compatibility`);
      setCompats(data);
    } catch {}
  }, [pageId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (searchQ.length < 1) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get(`/api/admin/product-pages/search-for-compat?q=${encodeURIComponent(searchQ)}&exclude_id=${pageId}`);
        // Filter out already-linked
        const existIds = new Set(compats.map(c => c.id));
        setSearchResults(data.filter(d => !existIds.has(d.id)));
      } catch {}
      setSearching(false);
    }, 300);
    return () => clearTimeout(t);
  }, [searchQ, pageId, compats]);

  const addCompat = async (compatId) => {
    try {
      await api.post(`/api/admin/product-pages/${pageId}/compatibility`, { compatible_id: compatId });
      setSearchQ("");
      setSearchResults([]);
      load();
    } catch {}
  };

  const removeCompat = async (compatId) => {
    try {
      await api.delete(`/api/admin/product-pages/${pageId}/compatibility/${compatId}`);
      load();
    } catch {}
  };

  return (
    <div className="space-y-3">
      <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">
        🔗 호환 제품 <div className="flex-1 border-t border-gray-200" />
        <span className="text-[10px] font-normal text-gray-400">{compats.length}개</span>
      </h4>
      {/* Search */}
      <div className="relative">
        <input
          value={searchQ} onChange={e => setSearchQ(e.target.value)}
          placeholder="호환 제품 검색 (모델명, 제목, 브랜드)"
          className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:ring-1 focus:ring-indigo-400 focus:border-indigo-400"
        />
        {searching && <div className="absolute right-3 top-2.5 text-[10px] text-gray-400">검색중...</div>}
        {searchResults.length > 0 && (
          <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl max-h-60 overflow-y-auto">
            {searchResults.map(r => (
              <button key={r.id} onClick={() => addCompat(r.id)}
                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-indigo-50 text-left transition">
                {r.image_url ? (
                  <img src={r.image_url} alt="" className="w-8 h-8 object-contain rounded bg-gray-50 shrink-0" />
                ) : (
                  <div className="w-8 h-8 bg-gray-100 rounded shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-medium text-gray-900 truncate">{r.model_name || r.name}</p>
                  <p className="text-[10px] text-gray-500">{r.brand} · {r.store_category || '미분류'}{r.color_type ? ` · ${r.color_type === 'color' ? '칼라' : '흑백'}` : ''}</p>
                </div>
                <span className="text-indigo-500 text-[10px] font-semibold shrink-0">+ 추가</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {/* Listed */}
      {compats.length > 0 && (
        <div className="space-y-1.5 max-h-48 overflow-y-auto">
          {compats.map(c => (
            <div key={c.id} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg group">
              {c.image_url ? (
                <img src={c.image_url} alt="" className="w-7 h-7 object-contain rounded bg-white shrink-0" />
              ) : (
                <div className="w-7 h-7 bg-gray-200 rounded shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-medium text-gray-800 truncate">{c.model_name || c.name}</p>
                <p className="text-[10px] text-gray-500">{c.brand} · {c.store_category || '미분류'}{c.color_type ? ` · ${c.color_type === 'color' ? '칼라' : '흑백'}` : ''}</p>
              </div>
              <button onClick={() => removeCompat(c.id)}
                className="opacity-0 group-hover:opacity-100 p-1 text-red-400 hover:text-red-600 transition">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function blankPage() {
  return {
    title: "", subtitle: "", brand: "", category: "", store_category: "",
    sub_category: "", color_type: "", sale_type: "sale", district: "", price: 0, rental_price: 0, cost_price: 0,
    hero_image: "", hero_html: "", model_name: "", theme: "minimal",
    sections_json: [], specs_json: {},
    seo_title: "", seo_description: "", status: "draft",
    items: [],
  };
}

/* ── Hero Image Uploader (large drag-drop zone + URL input) ── */
function HeroImageUploader({ imageUrl, onUrlChange }) {
  const [uploading, setUploading] = useState(false);
  const [removingBg, setRemovingBg] = useState(false);
  const [bgMode, setBgMode] = useState("u2net");
  const [dragOver, setDragOver] = useState(false);
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const res = await api.post("/api/admin/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUrlChange(res.data.url);
    } catch (err) {
      alert("이미지 업로드 실패: " + (err.response?.data?.error || err.message));
    } finally {
      setUploading(false);
    }
  };

  const removeBg = async () => {
    if (!imageUrl || !imageUrl.startsWith("/api/uploads/")) return;
    setRemovingBg(true);
    try {
      const { data } = await api.post("/api/admin/remove-bg", { image_url: imageUrl, mode: bgMode }, { timeout: 120000 });
      onUrlChange(data.url);
    } catch (err) {
      alert("배경 제거 실패: " + (err.response?.data?.error || err.message));
    } finally {
      setRemovingBg(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) upload(file);
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith("image/")) {
        upload(item.getAsFile());
        break;
      }
    }
  };

  return (
    <div className="space-y-3">
      {/* URL input row */}
      <div className="flex items-end gap-2 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <label className={label}>이미지 URL (직접 입력도 가능)</label>
          <input value={imageUrl} onChange={e => onUrlChange(e.target.value)}
            className={inp} placeholder="/api/uploads/product-pages/xxx.png 또는 외부 URL" />
        </div>
        {imageUrl && imageUrl.startsWith("/api/uploads/") && (
          <div className="flex items-end gap-1">
            <select value={bgMode} onChange={e => setBgMode(e.target.value)}
              className="border rounded-xl px-2 py-2.5 text-[11px] bg-emerald-50 border-emerald-200">
              <option value="u2net">U2-Net</option>
              <option value="gemini">Gemini AI</option>
            </select>
            <button onClick={removeBg} disabled={removingBg}
              className="px-3 py-2.5 rounded-xl text-sm font-semibold bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200 transition whitespace-nowrap disabled:opacity-50">
              {removingBg ? <><span className="animate-spin inline-block">⏳</span> 처리중...</> : "✂️ 배경 제거"}
            </button>
          </div>
        )}
        {imageUrl && (
          <button onClick={(e) => { e.stopPropagation(); setEditing(true); }}
            className="px-3 py-2.5 rounded-xl text-sm font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 transition whitespace-nowrap">
            ✏️ 편집
          </button>
        )}
        {imageUrl && (
          <button onClick={() => onUrlChange("")}
            className="px-3 py-2.5 rounded-xl text-sm text-red-500 hover:bg-red-50 transition">✕</button>
        )}
      </div>

      {/* Large drag-drop zone */}
      <div
        onDrop={handleDrop}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onPaste={handlePaste}
        onClick={() => inputRef.current?.click()}
        tabIndex={0}
        className={`relative border-2 border-dashed rounded-2xl p-6 text-center cursor-pointer transition-all min-h-[200px] flex flex-col items-center justify-center
          ${dragOver ? "border-blue-500 bg-blue-50 scale-[1.01]" : imageUrl ? "border-gray-200 bg-gray-50" : "border-gray-300 hover:border-blue-400 hover:bg-blue-50/30"}`}
      >
        <input ref={inputRef} type="file" accept="image/*" className="hidden"
          onChange={(e) => upload(e.target.files?.[0])} />

        {uploading ? (
          <div className="text-blue-600">
            <div className="w-10 h-10 border-3 border-blue-300 border-t-blue-600 rounded-full animate-spin mx-auto mb-3" />
            <p className="text-sm font-medium">업로드 중...</p>
          </div>
        ) : imageUrl ? (
          <div className="space-y-3">
            <img src={imageUrl} alt="대표 이미지"
              className="max-h-48 object-contain rounded-xl mx-auto"
              onError={e => { e.target.style.display = "none"; }} />
            <p className="text-xs text-gray-400">클릭하거나 새 이미지를 드래그해서 변경</p>
          </div>
        ) : (
          <>
            <svg className="w-12 h-12 text-gray-300 mb-3" fill="none" stroke="currentColor" strokeWidth={1} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21Z" />
            </svg>
            <p className="text-sm font-medium text-gray-600 mb-1">이미지를 드래그하거나 클릭하여 업로드</p>
            <p className="text-xs text-gray-400">PNG, JPG, GIF, WebP · 최대 10MB · 클립보드 붙여넣기도 가능</p>
          </>
        )}

        {dragOver && (
          <div className="absolute inset-0 bg-blue-500/10 rounded-2xl flex items-center justify-center">
            <p className="text-lg font-bold text-blue-600">이미지를 놓으세요</p>
          </div>
        )}
      </div>
      {editing && imageUrl && (
        <ImageEditorModal imageUrl={imageUrl} onSave={(url) => { onUrlChange(url); setEditing(false); }} onClose={() => setEditing(false)} />
      )}
    </div>
  );
}

/* ── Item Image Uploader (compact drag-drop for product items) ── */
function ItemImageUploader({ imageUrl, onUrlChange }) {
  const [uploading, setUploading] = useState(false);
  const [removingBg, setRemovingBg] = useState(false);
  const [bgMode, setBgMode] = useState("u2net");
  const [editing, setEditing] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const res = await api.post("/api/admin/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUrlChange(res.data.url);
    } catch (err) {
      alert("이미지 업로드 실패: " + (err.response?.data?.error || err.message));
    } finally {
      setUploading(false);
    }
  };

  const removeBg = async () => {
    if (!imageUrl || !imageUrl.startsWith("/api/uploads/")) return;
    setRemovingBg(true);
    try {
      const { data } = await api.post("/api/admin/remove-bg", { image_url: imageUrl, mode: bgMode }, { timeout: 120000 });
      onUrlChange(data.url);
    } catch (err) {
      alert("배경 제거 실패: " + (err.response?.data?.error || err.message));
    } finally {
      setRemovingBg(false);
    }
  };

  return (
    <div className="flex items-start gap-2 flex-wrap">
      <div className="flex-1 min-w-[200px]">
        <input value={imageUrl} onChange={e => onUrlChange(e.target.value)}
          className={inp} placeholder="이미지 URL 또는 드래그하여 업로드" />
      </div>
      {imageUrl && (
        <button onClick={() => setEditing(true)}
          className="w-20 h-20 border-2 border-blue-300 rounded-xl flex flex-col items-center justify-center cursor-pointer transition shrink-0 bg-blue-50 hover:bg-blue-100 text-blue-700"
          title="편집">
          <span className="text-lg">✏️</span><span className="text-[9px] font-bold mt-0.5">편집</span>
        </button>
      )}
      {imageUrl && imageUrl.startsWith("/api/uploads/") && (
        <div className="flex flex-col items-center gap-1">
          <select value={bgMode} onChange={e => setBgMode(e.target.value)}
            className="w-20 border rounded-lg px-1 py-0.5 text-[9px] bg-emerald-50 border-emerald-200">
            <option value="u2net">U2-Net</option>
            <option value="gemini">Gemini</option>
          </select>
          <button onClick={removeBg} disabled={removingBg}
            className="w-20 h-14 border-2 border-emerald-300 rounded-xl flex flex-col items-center justify-center cursor-pointer transition shrink-0 bg-emerald-50 hover:bg-emerald-100 disabled:opacity-50 text-emerald-700"
            title="배경 제거">
            {removingBg ? <span className="animate-spin text-lg">⏳</span> : <><span className="text-lg">✂️</span><span className="text-[9px] font-bold mt-0.5">배경제거</span></>}
          </button>
        </div>
      )}
      <div
        onDrop={(e) => { e.preventDefault(); setDragOver(false); upload(e.dataTransfer?.files?.[0]); }}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onClick={() => inputRef.current?.click()}
        className={`w-20 h-20 border-2 border-dashed rounded-xl flex items-center justify-center cursor-pointer transition shrink-0
          ${dragOver ? "border-blue-500 bg-blue-50" : imageUrl ? "border-gray-200 bg-gray-50" : "border-gray-300 hover:border-blue-400"}`}
      >
        <input ref={inputRef} type="file" accept="image/*" className="hidden"
          onChange={(e) => upload(e.target.files?.[0])} />
        {uploading ? (
          <span className="animate-spin text-blue-500">⏳</span>
        ) : imageUrl ? (
          <img src={imageUrl} alt="" className="w-full h-full object-contain rounded-lg p-0.5"
            onError={e => { e.target.style.display = "none"; }} />
        ) : (
          <svg className="w-6 h-6 text-gray-300" fill="none" stroke="currentColor" strokeWidth={1.2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
          </svg>
        )}
      </div>
      {editing && imageUrl && (
        <ImageEditorModal imageUrl={imageUrl} onSave={(url) => { onUrlChange(url); setEditing(false); }} onClose={() => setEditing(false)} />
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   SectionEditorInline — Lightweight section editor for unified canvas mode drawer
   ══════════════════════════════════════════════════════════════ */
function SectionEditorInline({ sec, idx, onUpdate, inp, card, onSyncRental, onSyncOptionsToRental, onSyncPlans, onGenerateRental, onGenerateOptionsFromRental }) {
  if (!sec) return null;
  const u = (field, val) => onUpdate(idx, { [field]: val });
  const ub = (field, val) => onUpdate(idx, { ...sec, [field]: val });

  const InputRow = ({ label, field, placeholder, multiline }) => (
    <div className="mb-3">
      <label className="text-xs font-semibold text-gray-500 mb-1 block">{label}</label>
      {multiline ? (
        <textarea value={sec[field] || ""} onChange={e => u(field, e.target.value)} className={inp + " min-h-[80px]"} placeholder={placeholder} />
      ) : (
        <input value={sec[field] || ""} onChange={e => u(field, e.target.value)} className={inp} placeholder={placeholder} />
      )}
    </div>
  );

  switch (sec.type) {
    case "image_text_block":
      return (
        <div className="space-y-3">
          <InputRow label="제목" field="title" placeholder="섹션 제목" />
          <InputRow label="설명" field="description" placeholder="설명 텍스트" multiline />
          <InputRow label="이미지 URL" field="image_url" placeholder="https://..." />
          <InputRow label="영상 URL" field="video_url" placeholder="MP4 URL (선택)" />
          <InputRow label="CTA 텍스트" field="cta_text" placeholder="버튼 텍스트" />
          <InputRow label="CTA 링크" field="cta_link" placeholder="/페이지경로" />
          <div className="mb-3">
            <label className="text-xs font-semibold text-gray-500 mb-1 block">이미지 위치</label>
            <select value={sec.image_position || "right"} onChange={e => u("image_position", e.target.value)} className={inp}>
              <option value="right">오른쪽</option><option value="left">왼쪽</option>
              <option value="top">위</option><option value="bottom">아래</option>
              <option value="background">배경</option>
            </select>
          </div>
          {/* Features */}
          <div>
            <label className="text-xs font-semibold text-gray-500 mb-1 block">특징 목록</label>
            {(sec.features || []).map((f, fi) => (
              <div key={fi} className="flex gap-1 mb-1">
                <input value={f} onChange={e => { const fs = [...(sec.features || [])]; fs[fi] = e.target.value; u("features", fs); }} className={inp + " flex-1"} placeholder="특징" />
                <button onClick={() => { const fs = [...(sec.features || [])]; fs.splice(fi, 1); u("features", fs); }} className="px-2 text-red-400 hover:text-red-600 text-xs">✕</button>
              </div>
            ))}
            <button onClick={() => u("features", [...(sec.features || []), ""])} className="text-xs text-blue-500 hover:underline mt-1">+ 특징 추가</button>
          </div>
        </div>
      );

    case "product_details":
    case "feature_highlight":
      return (
        <div className="space-y-3">
          <InputRow label="섹션 제목" field="title" placeholder="주요 특징" />
          {(sec.items || []).map((item, ii) => (
            <div key={ii} className={`${card} p-3 space-y-2`}>
              <div className="flex justify-between items-center">
                <span className="text-xs font-semibold text-gray-600">항목 #{ii + 1}</span>
                <button onClick={() => { const items = [...(sec.items || [])]; items.splice(ii, 1); u("items", items); }} className="text-xs text-red-400 hover:text-red-600">삭제</button>
              </div>
              <input value={item.icon || ""} onChange={e => { const items = [...(sec.items || [])]; items[ii] = { ...items[ii], icon: e.target.value }; u("items", items); }} className={inp} placeholder="아이콘 (이모지)" />
              <input value={item.title || ""} onChange={e => { const items = [...(sec.items || [])]; items[ii] = { ...items[ii], title: e.target.value }; u("items", items); }} className={inp} placeholder="제목" />
              <textarea value={item.description || ""} onChange={e => { const items = [...(sec.items || [])]; items[ii] = { ...items[ii], description: e.target.value }; u("items", items); }} className={inp + " min-h-[50px]"} placeholder="설명" />
              <input value={item.highlight_number || ""} onChange={e => { const items = [...(sec.items || [])]; items[ii] = { ...items[ii], highlight_number: e.target.value }; u("items", items); }} className={inp} placeholder="하이라이트 수치 (선택)" />
            </div>
          ))}
          <button onClick={() => u("items", [...(sec.items || []), { icon: "⚡", title: "", description: "", highlight_number: "" }])}
            className="w-full py-2 rounded-lg border-2 border-dashed border-gray-300 text-xs text-gray-500 hover:border-blue-400 hover:text-blue-600 transition">+ 항목 추가</button>
        </div>
      );

    case "spec_table":
      return (
        <SpecTableForm sec={sec} onPatch={(patch) => onUpdate(idx, patch)} />
      );

    case "sale_pricing":
      return (
        <div className="space-y-3">
          <InputRow label="섹션 제목" field="title" placeholder="판매가격표" />
          {(sec.products || []).map((p, pi) => (
            <div key={pi} className={`${card} p-3 space-y-2`}>
              <div className="flex justify-between"><span className="text-xs font-semibold">상품 #{pi + 1}</span>
                <button onClick={() => { const prods = [...(sec.products || [])]; prods.splice(pi, 1); u("products", prods); }} className="text-xs text-red-400">삭제</button></div>
              <input value={p.product_name || ""} onChange={e => { const prods = [...(sec.products || [])]; prods[pi] = { ...prods[pi], product_name: e.target.value }; u("products", prods); }} className={inp} placeholder="상품명" />
              <BrandSelect value={p.brand || ""} onChange={brand => { const prods = [...(sec.products || [])]; prods[pi] = { ...prods[pi], brand }; u("products", prods); }} className={inp} />
              <input type="number" value={p.body_price ?? ""} onChange={e => { const prods = [...(sec.products || [])]; prods[pi] = { ...prods[pi], body_price: e.target.value === "" ? "" : +e.target.value }; u("products", prods); }} className={inp} placeholder="본체가격" />
              <input value={p.image_url || ""} onChange={e => { const prods = [...(sec.products || [])]; prods[pi] = { ...prods[pi], image_url: e.target.value }; u("products", prods); }} className={inp} placeholder="이미지 URL" />
            </div>
          ))}
          <button onClick={() => u("products", [...(sec.products || []), { product_name: "", brand: "", body_price: 0, image_url: "" }])}
            className="w-full py-2 rounded-lg border-2 border-dashed border-gray-300 text-xs text-gray-500 hover:border-blue-400 hover:text-blue-600 transition">+ 상품 추가</button>
        </div>
      );

    case "rental_pricing": {
      const rentalProducts = sec.products || [];
      const columns = sec.custom_columns || [
        { key: "deposit_text", label: "보증금" },
        { key: "output_desc", label: "기본 출력매수" },
        { key: "contract_period", label: "약정기간" },
        { key: "options_desc", label: "선택사항" },
      ];
      return (
        <div className="space-y-3">
          <InputRow label="섹션 제목" field="title" placeholder="맞춤 렌탈 프로그램" />
          <div><label className="text-[9px] text-gray-400">하단 참고문구</label><input value={sec.footer_note || ""} onChange={e => u("footer_note", e.target.value)} className={inp} placeholder="* 사무환경에 알맞게 옵션 추가 협의 가능" /></div>
          {rentalProducts.map((p, pi) => (
            <div key={pi} className={`${card} p-3 space-y-2`}>
              <div className="flex justify-between"><span className="text-xs font-semibold">플랜 #{pi + 1}</span>
                <button onClick={() => { const prods = [...rentalProducts]; prods.splice(pi, 1); u("products", prods); }} className="text-xs text-red-400">삭제</button></div>
              <input value={p.product_name || ""} onChange={e => { const prods = [...rentalProducts]; prods[pi] = { ...prods[pi], product_name: e.target.value }; u("products", prods); }} className={inp} placeholder="제품명 (분당31매)" />
              <div className="grid grid-cols-2 gap-1">
                <div><label className="text-[9px] text-gray-400">브랜드</label><BrandSelect value={p.brand || ""} onChange={brand => { const prods = [...rentalProducts]; prods[pi] = { ...prods[pi], brand }; u("products", prods); }} className={inp} /></div>
                <div><label className="text-[9px] text-purple-700 font-bold">월 렌탈료</label><input type="number" value={p.monthly_total ?? ""} onChange={e => { const prods = [...rentalProducts]; prods[pi] = { ...prods[pi], monthly_total: e.target.value === "" ? "" : +e.target.value }; u("products", prods); }} className={inp} placeholder="50000" /></div>
              </div>
              {columns.map((col, ci) => (
                <div key={ci}><label className="text-[9px] text-gray-400">{col.label}</label>
                  {col.key === "output_desc" ? (
                    <textarea value={(p.col_values || {})[col.key] || ""} onChange={e => { const prods = [...rentalProducts]; prods[pi] = { ...prods[pi], col_values: { ...(prods[pi].col_values || {}), [col.key]: e.target.value } }; u("products", prods); }} className={`${inp} resize-none`} rows={2} placeholder={"흑백 1,000매 (합계 10,000원)\n칼라 500매 (합계 50,000원)"} />
                  ) : (
                    <input value={(p.col_values || {})[col.key] || ""} onChange={e => { const prods = [...rentalProducts]; prods[pi] = { ...prods[pi], col_values: { ...(prods[pi].col_values || {}), [col.key]: e.target.value } }; u("products", prods); }} className={inp} placeholder={col.label} />
                  )}
                </div>
              ))}
            </div>
          ))}
          <button onClick={() => u("products", [...rentalProducts, { product_name: "", brand: "", monthly_total: 0, col_values: {} }])}
            className="w-full py-2 rounded-lg border-2 border-dashed border-gray-300 text-xs text-gray-500 hover:border-blue-400 hover:text-blue-600 transition">+ 플랜 추가</button>
          {onSyncRental && (
            <button onClick={() => onSyncRental(idx)}
              className="w-full py-2 rounded-lg text-xs font-semibold bg-purple-100 text-purple-700 hover:bg-purple-200 transition">🔗 옵션 선택기에 연동</button>
          )}
          {onSyncOptionsToRental && (
            <button onClick={() => onSyncOptionsToRental(idx)}
              className="w-full py-2 rounded-lg text-xs font-semibold bg-orange-100 text-orange-700 hover:bg-orange-200 transition">🔗 옵션 단가 → 출력매수 자동입력</button>
          )}
          {onGenerateOptionsFromRental && (
            <button onClick={() => onGenerateOptionsFromRental(idx)}
              className="w-full py-2 rounded-lg text-xs font-semibold bg-teal-100 text-teal-700 hover:bg-teal-200 transition">📊 이 표로 옵션 자동생성</button>
          )}
        </div>
      );
    }

    case "heading_text":
      return (
        <div className="space-y-3">
          <InputRow label="대제목" field="heading" placeholder="제목" />
          <InputRow label="소제목" field="subheading" placeholder="소제목" />
          <InputRow label="설명" field="description" placeholder="설명" multiline />
          <div>
            <label className="text-xs font-semibold text-gray-500 mb-1 block">정렬</label>
            <select value={sec.align || "center"} onChange={e => u("align", e.target.value)} className={inp}>
              <option value="left">왼쪽</option><option value="center">가운데</option><option value="right">오른쪽</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 mb-1 block">배경 스타일</label>
            <select value={sec.bg_style || "light"} onChange={e => u("bg_style", e.target.value)} className={inp}>
              <option value="light">밝은 배경</option><option value="dark">어두운 배경</option><option value="gradient">그라데이션</option>
            </select>
          </div>
        </div>
      );

    case "cta":
      return (
        <div className="space-y-3">
          <InputRow label="제목" field="title" placeholder="CTA 제목" />
          <InputRow label="설명" field="description" placeholder="설명 문구" />
          <InputRow label="버튼 텍스트" field="button_text" placeholder="지금 문의하기" />
          <InputRow label="버튼 링크" field="button_link" placeholder="/contact" />
        </div>
      );

    case "product_card":
      return (
        <div className="space-y-3">
          <InputRow label="제품명" field="product_name" placeholder="제품명" />
          <InputRow label="태그라인" field="tagline" placeholder="한줄 캐치프레이즈" />
          <InputRow label="설명" field="description" placeholder="제품 설명" multiline />
          <InputRow label="이미지 URL" field="image_url" placeholder="https://..." />
        </div>
      );

    case "toner_spec":
      return (
        <div className="space-y-3">
          <InputRow label="섹션 제목" field="title" placeholder="토너/소모품" />
          {(sec.products || []).map((p, pi) => (
            <div key={pi} className={`${card} p-3 space-y-2`}>
              <div className="flex justify-between"><span className="text-xs font-semibold">소모품 #{pi + 1}</span>
                <button onClick={() => { const prods = [...(sec.products || [])]; prods.splice(pi, 1); u("products", prods); }} className="text-xs text-red-400">삭제</button></div>
              <input value={p.product_name || ""} onChange={e => { const prods = [...(sec.products || [])]; prods[pi] = { ...prods[pi], product_name: e.target.value }; u("products", prods); }} className={inp} placeholder="제품명" />
              <input value={p.model_number || ""} onChange={e => { const prods = [...(sec.products || [])]; prods[pi] = { ...prods[pi], model_number: e.target.value }; u("products", prods); }} className={inp} placeholder="모델번호" />
              <input value={p.color || ""} onChange={e => { const prods = [...(sec.products || [])]; prods[pi] = { ...prods[pi], color: e.target.value }; u("products", prods); }} className={inp} placeholder="색상" />
              <input type="number" value={p.market_price ?? ""} onChange={e => { const prods = [...(sec.products || [])]; prods[pi] = { ...prods[pi], market_price: e.target.value === "" ? "" : +e.target.value }; u("products", prods); }} className={inp} placeholder="시장가" />
            </div>
          ))}
          <button onClick={() => u("products", [...(sec.products || []), { product_name: "", model_number: "", color: "", market_price: 0 }])}
            className="w-full py-2 rounded-lg border-2 border-dashed border-gray-300 text-xs text-gray-500 hover:border-blue-400 hover:text-blue-600 transition">+ 소모품 추가</button>
        </div>
      );

    case "rental_plans":
      return (
        <div className="space-y-3">
          <InputRow label="섹션 제목" field="title" placeholder="렌탈 요금제" />
          {(sec.plans || []).map((plan, pi) => (
            <div key={pi} className={`${card} p-3 space-y-2`}>
              <div className="flex justify-between"><span className="text-xs font-semibold">요금제 #{pi + 1}</span>
                <button onClick={() => { const plans = [...(sec.plans || [])]; plans.splice(pi, 1); u("plans", plans); }} className="text-xs text-red-400">삭제</button></div>
              <input value={plan.plan_name || ""} onChange={e => { const plans = [...(sec.plans || [])]; plans[pi] = { ...plans[pi], plan_name: e.target.value }; u("plans", plans); }} className={inp} placeholder="요금제명" />
              <input type="number" value={plan.monthly_price ?? ""} onChange={e => { const plans = [...(sec.plans || [])]; plans[pi] = { ...plans[pi], monthly_price: e.target.value === "" ? "" : +e.target.value }; u("plans", plans); }} className={inp} placeholder="월 렌탈료 (원)" />
              <input type="number" value={plan.deposit ?? ""} onChange={e => { const plans = [...(sec.plans || [])]; plans[pi] = { ...plans[pi], deposit: e.target.value === "" ? "" : +e.target.value }; u("plans", plans); }} className={inp} placeholder="보증금 (원)" />
              <input value={plan.contract_period || ""} onChange={e => { const plans = [...(sec.plans || [])]; plans[pi] = { ...plans[pi], contract_period: e.target.value }; u("plans", plans); }} className={inp} placeholder="계약기간 (예: 3년)" />
              <input value={plan.included_pages || ""} onChange={e => { const plans = [...(sec.plans || [])]; plans[pi] = { ...plans[pi], included_pages: e.target.value }; u("plans", plans); }} className={inp} placeholder="흑백 출력매수" />
              <input value={plan.color_pages || ""} onChange={e => { const plans = [...(sec.plans || [])]; plans[pi] = { ...plans[pi], color_pages: e.target.value }; u("plans", plans); }} className={inp} placeholder="컬러 출력매수" />
              <input value={plan.options || ""} onChange={e => { const plans = [...(sec.plans || [])]; plans[pi] = { ...plans[pi], options: e.target.value }; u("plans", plans); }} className={inp} placeholder="선택사항 (팬스, 추가트레이 등)" />
            </div>
          ))}
          <button onClick={() => u("plans", [...(sec.plans || []), { plan_name: "", monthly_price: 0, deposit: 200000, contract_period: "", included_pages: "", color_pages: "", options: "" }])}
            className="w-full py-2 rounded-lg border-2 border-dashed border-gray-300 text-xs text-gray-500 hover:border-blue-400 hover:text-blue-600 transition">+ 요금제 추가</button>
        </div>
      );

    case "rental_calculator":
      return (
        <div className="space-y-3">
          <InputRow label="제목" field="title" placeholder="비용 계산기" />
          <InputRow label="설명" field="description" placeholder="설명" multiline />
          <InputRow label="비고" field="note" placeholder="참고사항" />
        </div>
      );

    case "product_showcase": {
      const scImgs = sec.images || [""];
      const scGrps = sec.option_groups || [];
      const multiplyScGroup = (gi) => {
        const factor = window.prompt("옵션 단가에 곱할 배수를 입력하세요", "1");
        if (factor == null) return;
        const gs = [...scGrps];
        gs[gi] = multiplyOptionGroupPrices(gs[gi], factor);
        u("option_groups", gs);
      };
      return (
        <div className="space-y-3">
          <InputRow label="제품명" field="title" placeholder="iPhone Air" />
          <InputRow label="가격 라벨" field="price_label" placeholder="₩1,590,000부터" />
          <div className="text-[10px] font-semibold text-gray-500 mb-1">이미지 URL</div>
          {scImgs.map((img, ii) => (
            <div key={ii} className="flex gap-1">
              <input value={img || ""} onChange={e => { const a = [...scImgs]; a[ii] = e.target.value; u("images", a); }} className={inp + " flex-1"} placeholder="이미지 URL" />
              <button onClick={() => u("images", scImgs.filter((_, k) => k !== ii))} className="text-xs text-red-400 px-1">✕</button>
            </div>
          ))}
          <button onClick={() => u("images", [...scImgs, ""])} className="text-xs text-blue-500 hover:underline">+ 이미지</button>
          <div className="text-[10px] font-semibold text-gray-500 mb-1 mt-2">옵션 그룹</div>
          <div className="max-h-[400px] overflow-y-auto space-y-2 pr-1">
          {scGrps.map((grp, gi) => (
            <div key={gi} className={`${card} p-3 space-y-2`}>
              <div className="flex justify-between"><span className="text-xs font-semibold">{grp.group_name || `그룹 ${gi+1}`}</span>
                <button onClick={() => { const gs = [...scGrps]; gs.splice(gi, 1); u("option_groups", gs); }} className="text-xs text-red-400">삭제</button></div>
              <input value={grp.group_name || ""} onChange={e => { const gs = [...scGrps]; gs[gi] = { ...gs[gi], group_name: e.target.value }; u("option_groups", gs); }} className={inp} placeholder="그룹명" />
              <div className="flex gap-1">
                <select value={grp.group_type || "swatch"} onChange={e => { const gs = [...scGrps]; gs[gi] = { ...gs[gi], group_type: e.target.value }; u("option_groups", gs); }} className={inp + " flex-1"}>
                  <option value="swatch">컬러 스와치</option><option value="list">리스트</option><option value="button">버튼</option>
                </select>
                <select value={getOptionGroupPriceType(grp)} onChange={e => { const gs = [...scGrps]; gs[gi] = { ...gs[gi], price_type: e.target.value }; u("option_groups", gs); }} className={inp + " flex-1"}>
                  <option value="per_unit">단가×수량</option><option value="fixed">고정금액</option>
                </select>
                <button onClick={() => multiplyScGroup(gi)} className="px-3 rounded-xl text-xs font-semibold bg-orange-100 text-orange-700 hover:bg-orange-200 transition">
                  일괄 곱하기
                </button>
              </div>
              {(grp.group_type === "button" || grp.group_type === "list") && getOptionGroupPriceType(grp) === "per_unit" && (
                <div className="rounded-xl border border-orange-100 bg-orange-50 p-2">
                  <label className="text-[10px] font-semibold text-orange-700">매수당 단가(원/매)</label>
                  <input
                    type="number"
                    value={(grp.options || []).find(o => Number(o?.unit_price) > 0)?.unit_price ?? ""}
                    onChange={e => { const gs = [...scGrps]; gs[gi] = updateOptionUnitPrices(gs[gi], e.target.value); u("option_groups", gs); }}
                    className={inp + " mt-1 text-right text-orange-700"}
                    placeholder="예: 흑백 10 / 칼라 100"
                  />
                </div>
              )}
              {(grp.options || []).map((opt, oi) => (
                <div key={oi} className="flex gap-1 items-center">
                  {grp.group_type === "swatch" && <input type="color" value={opt.color_hex || "#000"} onChange={e => { const gs = [...scGrps]; gs[gi] = { ...gs[gi], options: gs[gi].options.map((o, j) => j === oi ? { ...o, color_hex: e.target.value } : o) }; u("option_groups", gs); }} className="w-6 h-6 rounded-full border-0" />}
                  <input value={opt.name || ""} onChange={e => { const gs = [...scGrps]; const next = { ...opt, name: e.target.value }; if (Number(next.unit_price) > 0) next.price_label = formatOptionPriceLabel(gs[gi], next, next.unit_price); gs[gi] = { ...gs[gi], options: gs[gi].options.map((o, j) => j === oi ? next : o) }; u("option_groups", gs); }} className={inp + " flex-1"} placeholder="옵션명" />
                  {grp.group_type === "list" && <input value={opt.price_label || ""} onChange={e => { const gs = [...scGrps]; gs[gi] = { ...gs[gi], options: gs[gi].options.map((o, j) => j === oi ? { ...o, price_label: e.target.value } : o) }; u("option_groups", gs); }} className={inp + " w-28"} placeholder="가격 라벨" />}
                  {(grp.group_type === "button" || grp.group_type === "list") && (
                    <input type="number" value={opt.unit_price ?? ""} onChange={e => {
                      const value = e.target.value === "" ? null : Number(e.target.value);
                      const gs = [...scGrps];
                      const next = { ...opt, unit_price: value };
                      if (value > 0) next.price_label = formatOptionPriceLabel(gs[gi], next, value);
                      gs[gi] = { ...gs[gi], price_type: getOptionGroupPriceType(gs[gi]), options: gs[gi].options.map((o, j) => j === oi ? next : o) };
                      u("option_groups", gs);
                    }} className={inp + " w-24 text-right text-orange-600"} placeholder={getOptionGroupPriceType(grp) === "fixed" ? "월 기본료" : "단가"} />
                  )}
                  <button onClick={() => { const gs = [...scGrps]; gs[gi] = { ...gs[gi], options: gs[gi].options.filter((_, j) => j !== oi) }; u("option_groups", gs); }} className="text-xs text-red-400 px-1">✕</button>
                </div>
              ))}
              <button onClick={() => { const gs = [...scGrps]; gs[gi] = { ...gs[gi], options: [...(gs[gi].options || []), { name: "", color_hex: "#ccc", price_label: "" }] }; u("option_groups", gs); }}
                className="text-xs text-blue-500 hover:underline">+ 선택지</button>
            </div>
          ))}
          </div>
          <div className="flex gap-1 flex-wrap mt-2">
            {[{ n: "🎨 컬러스와치", gn: "색상", t: "swatch" }, { n: "📋 리스트", gn: "옵션", t: "list" }, { n: "💰 가격", gn: "가격", t: "button" }].map(p => (
              <button key={p.n} onClick={() => u("option_groups", [...scGrps, { group_name: p.gn, group_type: p.t, options: [{ name: "", color_hex: "#ccc", price_label: "" }] }])}
                className="px-2 py-1 rounded-lg text-[10px] font-medium bg-sky-100 text-sky-700 hover:bg-sky-200">{p.n}</button>
            ))}
          </div>
          {onGenerateRental && (
            <button onClick={() => onGenerateRental(idx)}
              className="w-full py-2 rounded-lg text-xs font-semibold bg-purple-100 text-purple-700 hover:bg-purple-200 transition mt-2">📊 이 옵션으로 렌탈표 자동생성</button>
          )}
          <div className="grid grid-cols-2 gap-2 mt-2">
            <div>
              <label className="text-[10px] text-gray-500">CTA 버튼 텍스트</label>
              <input value={sec.cta_text || ""} onChange={e => u("cta_text", e.target.value)} className={inp} placeholder="📞 견적문의" />
            </div>
            <div>
              <label className="text-[10px] text-gray-500">CTA 링크</label>
              <input value={sec.cta_link || ""} onChange={e => u("cta_link", e.target.value)} className={inp} placeholder="/consultation" />
            </div>
          </div>
        </div>
      );
    }

    case "product_options":
      return (
        <div className="space-y-3">
          <InputRow label="섹션 제목" field="title" placeholder="옵션 선택기" />
          {(sec.option_groups || []).map((grp, gi) => (
            <div key={gi} className={`${card} p-3 space-y-2`}>
              <div className="flex justify-between"><span className="text-xs font-semibold">그룹 #{gi + 1}</span>
                <button onClick={() => { const gs = [...(sec.option_groups || [])]; gs.splice(gi, 1); u("option_groups", gs); }} className="text-xs text-red-400">삭제</button></div>
              <input value={grp.group_name || ""} onChange={e => { const gs = [...(sec.option_groups || [])]; gs[gi] = { ...gs[gi], group_name: e.target.value }; u("option_groups", gs); }} className={inp} placeholder="그룹명 (예: 색상)" />
              <select value={grp.group_type || "card"} onChange={e => { const gs = [...(sec.option_groups || [])]; gs[gi] = { ...gs[gi], group_type: e.target.value }; u("option_groups", gs); }} className={inp}>
                <option value="card">카드</option><option value="button">버튼</option><option value="color">색상</option><option value="checkbox">체크박스</option>
              </select>
              {(grp.options || []).map((opt, oi) => (
                <div key={oi} className="flex gap-1">
                  <input value={opt.name || ""} onChange={e => { const gs = [...(sec.option_groups || [])]; gs[gi] = { ...gs[gi], options: gs[gi].options.map((o, j) => j === oi ? { ...o, name: e.target.value } : o) }; u("option_groups", gs); }} className={inp + " flex-1"} placeholder="옵션명" />
                  <button onClick={() => { const gs = [...(sec.option_groups || [])]; gs[gi] = { ...gs[gi], options: gs[gi].options.filter((_, j) => j !== oi) }; u("option_groups", gs); }} className="text-xs text-red-400 px-1">✕</button>
                </div>
              ))}
              <button onClick={() => { const gs = [...(sec.option_groups || [])]; gs[gi] = { ...gs[gi], options: [...(gs[gi].options || []), { name: "", description: "", price_modifier: 0 }] }; u("option_groups", gs); }}
                className="text-xs text-blue-500 hover:underline">+ 옵션 추가</button>
            </div>
          ))}
          <button onClick={() => u("option_groups", [...(sec.option_groups || []), { group_name: "", group_type: "card", options: [] }])}
            className="w-full py-2 rounded-lg border-2 border-dashed border-gray-300 text-xs text-gray-500 hover:border-blue-400 hover:text-blue-600 transition">+ 옵션 그룹 추가</button>
        </div>
      );

    case "rich_html":
    case "text":
      return (
        <div className="space-y-3">
          <InputRow label="제목" field="title" placeholder="제목" />
          <InputRow label="내용" field="content" placeholder="HTML 또는 텍스트 내용" multiline />
        </div>
      );

    default:
      return (
        <div className="space-y-3">
          <InputRow label="제목" field="title" placeholder="제목" />
          {sec.description !== undefined && <InputRow label="설명" field="description" placeholder="설명" multiline />}
          {sec.content !== undefined && <InputRow label="내용" field="content" placeholder="내용" multiline />}
          <div className="mt-3 p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500 mb-2">전체 데이터 (JSON)</p>
            <textarea value={JSON.stringify(sec, null, 2)}
              onChange={e => { try { const v = JSON.parse(e.target.value); onUpdate(idx, v); } catch {} }}
              className="w-full font-mono text-[10px] bg-gray-100 rounded p-2 min-h-[120px] border border-gray-200" />
          </div>
        </div>
      );
  }
}

/* ══════════════════════════════════════════════════════════════
   Main Editor Component
   ══════════════════════════════════════════════════════════════ */
export default function ProductPageEditor({ pageId, onClose, onSaved, setToast }) {
  const [form, setForm] = useState(blankPage());
  const [loading, setLoading] = useState(false);
  const [showDesignCanvas, setShowDesignCanvas] = useState(false);
  const [editingCanvasIdx, setEditingCanvasIdx] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [collapsedSections, setCollapsedSections] = useState(new Set());
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiFieldLoading, setAiFieldLoading] = useState("");
  const [showQuickStart, setShowQuickStart] = useState(false);
  const [showHeroAdvanced, setShowHeroAdvanced] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const isNew = !pageId;
  const [presenting, setPresenting] = useState(false);
  /* ── Side-by-side: Editor (left) + Live Preview (right, togglable) ── */
  const [editingSectionDrawerIdx, setEditingSectionDrawerIdx] = useState(null);
  const [canvasJSON, setCanvasJSON] = useState(null); // persistent canvas state
  const [showLivePreview, setShowLivePreview] = useState(true); // right-side live preview toggle
  const [showAiPrompt, setShowAiPrompt] = useState(false); // AI 프롬프트 패널
  const [aiPromptText, setAiPromptText] = useState(""); // AI 프롬프트 입력 텍스트
  const [aiPromptFiles, setAiPromptFiles] = useState([]); // AI 프롬프트 파일(들)
  const [showUrlAnalyze, setShowUrlAnalyze] = useState(false); // URL 분석 패널
  const [analyzeUrl, setAnalyzeUrl] = useState(""); // URL 입력
  const [urlAnalyzing, setUrlAnalyzing] = useState(false); // URL 분석 중
  const [showHtmlPaste, setShowHtmlPaste] = useState(false); // HTML 직접 붙여넣기 모드
  const [pasteHtml, setPasteHtml] = useState(""); // 붙여넣기된 HTML
  const [showAiStats, setShowAiStats] = useState(false); // AI 통계 패널
  const [aiStats, setAiStats] = useState(null); // AI 통계 데이터
  const [showPublishModal, setShowPublishModal] = useState(false); // 발행 몰 선택 모달

  // 관리자 상태 체크
  const isAdmin = (() => { try { const u = JSON.parse(localStorage.getItem('user') || '{}'); return isAdminRole(u.role) || u.is_admin; } catch { return false; } })();

  /* ── Form ref for latest state access in callbacks ── */
  const formRef = useRef(form);
  useEffect(() => { formRef.current = form; }, [form]);

  /* ── Undo / Redo ── */
  const historyRef = useRef([]);
  const historyIdxRef = useRef(-1);
  const skipHistoryRef = useRef(false);
  const pushHistory = useCallback((state) => {
    if (skipHistoryRef.current) { skipHistoryRef.current = false; return; }
    const arr = historyRef.current;
    // Trim future if we branched
    if (historyIdxRef.current < arr.length - 1) {
      historyRef.current = arr.slice(0, historyIdxRef.current + 1);
    }
    historyRef.current.push(JSON.stringify(state));
    if (historyRef.current.length > 80) historyRef.current.shift();
    historyIdxRef.current = historyRef.current.length - 1;
  }, []);
  const undo = useCallback(() => {
    if (historyIdxRef.current <= 0) return;
    historyIdxRef.current -= 1;
    skipHistoryRef.current = true;
    setForm(JSON.parse(historyRef.current[historyIdxRef.current]));
  }, []);
  const redo = useCallback(() => {
    if (historyIdxRef.current >= historyRef.current.length - 1) return;
    historyIdxRef.current += 1;
    skipHistoryRef.current = true;
    setForm(JSON.parse(historyRef.current[historyIdxRef.current]));
  }, []);
  // Push to history on form change (debounced)
  const historyTimerRef = useRef(null);
  useEffect(() => {
    if (historyTimerRef.current) clearTimeout(historyTimerRef.current);
    historyTimerRef.current = setTimeout(() => pushHistory(form), 500);
    return () => clearTimeout(historyTimerRef.current);
  }, [form, pushHistory]);
  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) { e.preventDefault(); undo(); }
      if ((e.metaKey || e.ctrlKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) { e.preventDefault(); redo(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo]);

  /* ── Auto-save to localStorage ── */
  const storageKey = pageId ? `ppeditor_${pageId}` : 'ppeditor_new';
  // Save to localStorage on form change (debounced)
  const autoSaveTimerRef = useRef(null);
  useEffect(() => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      try { localStorage.setItem(storageKey, JSON.stringify(form)); } catch {}
    }, 1000);
    return () => clearTimeout(autoSaveTimerRef.current);
  }, [form, storageKey]);
  // Warn before unload
  useEffect(() => {
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);
  // Recover from localStorage on mount
  const [showRecovery, setShowRecovery] = useState(false);
  const recoveredRef = useRef(null);
  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) { recoveredRef.current = JSON.parse(saved); setShowRecovery(true); }
    } catch {}
  }, [storageKey]);

  /* ── Hero visual helpers ── */
  const heroVisual = form.specs_json?._hero_visual || {};
  const setHeroVisual = (patch) => {
    const newVisual = { ...heroVisual, ...patch };
    const newSpecs = { ...(form.specs_json || {}), _hero_visual: newVisual };
    // Auto-generate hero_html from visual fields
    const hl = newVisual.headline || form.title || "";
    const sub = newVisual.description || form.subtitle || "";
    const ctaText = newVisual.cta_text || "";
    const ctaLink = newVisual.cta_link || "/consultation";
    const layout = newVisual.layout || "center";
    const bgMap = { white: "bg-white", "light-gray": "bg-gray-50", "gradient-blue": "bg-gradient-to-br from-blue-50 to-indigo-100", "gradient-purple": "bg-gradient-to-br from-purple-50 to-pink-50", dark: "bg-gray-900 text-white" };
    const bgClass = bgMap[newVisual.bg_style || "white"] || "bg-white";
    const isDark = newVisual.bg_style === "dark";
    const textColor = isDark ? "text-white" : "text-gray-900";
    const subColor = isDark ? "text-gray-300" : "text-gray-600";
    let html = "";
    if (layout === "center") {
      const imgTag = form.hero_image ? `<div class="mt-8 flex justify-center"><img src="${form.hero_image}" alt="" class="max-h-[400px] object-contain" /></div>` : "";
      html = `<div class="${bgClass} py-12 px-6 text-center"><h1 class="text-3xl md:text-5xl font-bold ${textColor} leading-tight">${hl}</h1>${sub ? `<p class="mt-4 text-lg ${subColor} max-w-2xl mx-auto">${sub}</p>` : ""}${ctaText ? `<div class="mt-6"><a href="${ctaLink}" class="inline-block px-8 py-3 bg-blue-600 text-white font-semibold rounded-full hover:bg-blue-700 transition">${ctaText}</a></div>` : ""}${imgTag}</div>`;
    } else {
      const imgSide = form.hero_image ? `<img src="${form.hero_image}" alt="" class="w-full max-w-md mx-auto object-contain" />` : "";
      const textSide = `<div><h1 class="text-2xl md:text-4xl font-bold ${textColor} leading-tight">${hl}</h1>${sub ? `<p class="mt-3 text-base ${subColor}">${sub}</p>` : ""}${ctaText ? `<div class="mt-5"><a href="${ctaLink}" class="inline-block px-6 py-2.5 bg-blue-600 text-white font-semibold rounded-full hover:bg-blue-700 transition text-sm">${ctaText}</a></div>` : ""}</div>`;
      html = `<div class="${bgClass} py-10 px-6"><div class="max-w-5xl mx-auto flex flex-col md:flex-row items-center gap-8">${layout === "left" ? textSide + imgSide : imgSide + textSide}</div></div>`;
    }
    setForm(prev => ({ ...prev, specs_json: newSpecs, hero_html: html }));
  };

  /* ── Load existing page ── */
  useEffect(() => {
    if (!pageId) return;
    setLoading(true);
    api.get(`/api/admin/product-pages/${pageId}`)
      .then(({ data }) => {
        // Parse sections_json if string
        let sj = data.sections_json;
        if (typeof sj === "string") try { sj = JSON.parse(sj); } catch { sj = []; }
        if (!Array.isArray(sj)) sj = [];
        setForm({ ...data, sections_json: sj });
        // Restore canvas JSON from saved unified canvas section
        const saved = sj.find(s => s.type === "design_canvas" && s._isUnifiedCanvas);
        if (saved?.canvas_json) setCanvasJSON(saved.canvas_json);
      })
      .catch(() => setToast("페이지 로드 실패"))
      .finally(() => setLoading(false));
  }, [pageId, setToast]);

  /* ── Save ── */
  const handleSave = useCallback(async (andPublish = false) => {
    const currentForm = formRef.current;
    if (!currentForm.title?.trim()) { setToast("타이틀은 필수입니다"); return; }
    setSaving(true);
    try {
      const payload = { ...currentForm };
      if (andPublish) {
        payload.status = "published";
        if (!payload.mall_type) { setToast("발행 위치(일반몰/사업자몰)를 선택해주세요"); setSaving(false); return; }
      }
      // Strip temp fields from sections before saving (preserve _isUnifiedCanvas)
      if (Array.isArray(payload.sections_json)) {
        payload.sections_json = payload.sections_json.map(sec => {
          const clean = { ...sec };
          Object.keys(clean).forEach(k => { if (k.startsWith("_") && k !== "_isUnifiedCanvas") delete clean[k]; });
          return clean;
        });
      }
      // Remove read-only server fields
      delete payload.created_at; delete payload.updated_at; delete payload.published_at;
      delete payload.deleted_at; delete payload.is_deleted; delete payload.id;

      // Cancel pending auto-save timer before clearing localStorage
      if (autoSaveTimerRef.current) { clearTimeout(autoSaveTimerRef.current); autoSaveTimerRef.current = null; }

      if (isNew) {
        const { data } = await api.post("/api/admin/product-pages/create", payload);
        setToast(andPublish ? `✅ 발행 완료 (ID: ${data.id})` : `✅ 초안 보관 완료 (ID: ${data.id})`);
        try { localStorage.removeItem(storageKey); } catch {}
        onSaved?.(data);
      } else {
        await api.put(`/api/admin/product-pages/${pageId}`, payload);
        setToast(andPublish ? "✅ 공개 업데이트 완료" : "✅ 초안 보관 완료");
        try { localStorage.removeItem(storageKey); } catch {}
        onSaved?.({ id: pageId });
      }

      /* 렌탈 요금제 섹션이 있으면 자동으로 DB 동기화 */
      const secs = Array.isArray(payload.sections_json) ? payload.sections_json : [];
      const rentalSec = secs.find(s => s.type === "rental_plans");
      if (rentalSec && rentalSec.plans && rentalSec.plans.length > 0) {
        try {
          await api.post("/api/admin/rental-plans/save-from-section", {
            plans: rentalSec.plans,
            page_title: currentForm.title || "",
            brand: currentForm.brand || "",
            model_name: currentForm.model_name || "",
          });
          setToast(prev => prev ? prev + " | 약정 DB 동기화 완료" : "✅ 약정 DB 동기화 완료");
        } catch { /* 약정 동기화 실패해도 페이지 저장은 성공 */ }
      }
    } catch (e) {
      setToast(e.response?.data?.error || "처리 실패");
    }
    setSaving(false);
  }, [isNew, pageId, setToast, onSaved]);

  /* ── Sections helpers ── */
  const sections = Array.isArray(form.sections_json) ? form.sections_json : [];

  /** Cross-reference product_showcase → rental_pricing col_values (mutates arr in place) */
  const _crossRefShowcaseToRental = (arr, sc) => {
    const rentalIdx = arr.findIndex(s => s?.type === "rental_pricing");
    if (rentalIdx < 0) return;
    const rentalSec = { ...arr[rentalIdx] };
    const products = (rentalSec.products || []).map(p => ({ ...p, col_values: { ...(p.col_values || {}) } }));
    const phs = new Set(["선택 하세요", "선택하세요", "-", "", "없음", "문의"]);
    // sub_info → 보증금/약정기간 추출
    let scDep = "", scCont = "", scOut = "", scOpt = "";
    for (const info of (sc.sub_info || [])) {
      const s = typeof info === "string" ? info : JSON.stringify(info);
      const lo = s.toLowerCase().replace(/\s/g, "");
      if (lo.includes("보증금")) scDep = s.includes(":") ? s.split(":").pop().trim() : s.replace(/.*보증금\s*/i, "").trim() || s;
      else if (lo.includes("약정") || lo.includes("계약기간")) scCont = s.includes(":") ? s.split(":").pop().trim() : s.replace(/.*(?:약정|계약기간)\s*/i, "").trim() || s;
      else if (lo.includes("출력매수") || lo.includes("기본매수")) scOut = s.includes(":") ? s.split(":").pop().trim() : s;
      else if (lo.includes("팩스") || lo.includes("선택사항") || lo.includes("옵션")) scOpt = s.includes(":") ? s.split(":").pop().trim() : s;
    }
    // option_groups → 흑백/칼라 분리 그룹 + 합쳐진 그룹 탐지
    let bwGrp = null, clGrp = null, combGrp = null;
    for (const og of (sc.option_groups || [])) {
      const gn = (og?.group_name || "").toLowerCase();
      const on = (og?.options || []).map(o => (o?.name || "").toLowerCase()).join(" ");
      const txt = gn + " " + on;
      const hasBw = /흑백|b&w|mono/.test(txt);
      const hasCl = /칼라|컬러|color/.test(txt);
      const hasOut = /매수|매|출력/.test(txt);
      if (hasBw && !hasCl && hasOut) bwGrp = og;
      else if (hasCl && !hasBw && hasOut) clGrp = og;
      else if ((hasBw || hasCl) && hasOut) combGrp = og;
    }
    products.forEach((p, pi) => {
      const cv = p.col_values;
      if (phs.has((cv.deposit_text || "").trim()) && scDep) cv.deposit_text = scDep;
      if (phs.has((cv.contract_period || "").trim()) && scCont) cv.contract_period = scCont;
      if (phs.has((cv.output_desc || "").trim())) {
        if (bwGrp || clGrp) {
          const parts = [];
          if (bwGrp) {
            const bwOpts = bwGrp.options || [];
            const bwO = bwOpts[pi] || bwOpts[bwOpts.length - 1];
            if (bwO) {
              const qty = (bwO.name || "").includes("매") ? bwO.name : bwO.name + "매";
              const u = Number(bwO.unit_price) || 0;
              parts.push("흑백 " + qty + (u ? ` (합계 ${u.toLocaleString()}원)` : ""));
            }
          }
          if (clGrp) {
            const clOpts = clGrp.options || [];
            const clO = clOpts[pi] || clOpts[clOpts.length - 1];
            if (clO) {
              const qty = (clO.name || "").includes("매") ? clO.name : clO.name + "매";
              const u = Number(clO.unit_price) || 0;
              parts.push("칼라 " + qty + (u ? ` (합계 ${u.toLocaleString()}원)` : ""));
            }
          }
          if (parts.length) cv.output_desc = parts.join("\n");
        } else if (combGrp) {
          const outOpts = combGrp.options || [];
          const opt = outOpts[pi] || outOpts[outOpts.length - 1];
          if (opt) cv.output_desc = opt.name || cv.output_desc;
        } else if (scOut) cv.output_desc = scOut;
      }
      if (phs.has((cv.options_desc || "").trim()) && scOpt) cv.options_desc = scOpt;
    });
    rentalSec.products = products;
    arr[rentalIdx] = rentalSec;
  };

  /* ── URL 분석 시 옵션 → 렌탈표 자동 생성 (순수 배열 조작, setForm 안 씀) ── */
  const _autoGenRentalFromShowcase = (arr, sc, meta) => {
    const groups = sc.option_groups || [];
    if (groups.length === 0) return;

    let bwGroup = null, colorGroup = null, periodGroup = null, faxGroup = null, comboGroup = null;
    for (const g of groups) {
      const gn = (g.group_name || "").toLowerCase();
      const on = (g.options || []).map(o => (o?.name || "").toLowerCase()).join(" ");
      const txt = gn + " " + on;
      // 결합형: 흑백+칼라 모두 포함
      if (/흑백|b&w|mono/.test(txt) && /칼라|컬러|color/.test(txt)) comboGroup = g;
      else if (/흑백|b&w|mono/.test(txt) && /매수|매|출력/.test(txt)) bwGroup = g;
      else if (/칼라|컬러|color/.test(txt) && /매수|매|출력/.test(txt)) colorGroup = g;
      else if (/기간|약정|렌탈/.test(gn)) periodGroup = g;
      else if (/팩스|fax|선택/.test(gn)) faxGroup = g;
    }

    // 결합형 옵션 파싱: "흑백 2,000매 / 칼라 200매"
    let bwOpts = [], colorOpts = [];
    if (comboGroup && !bwGroup && !colorGroup) {
      const comboPat = /흑백\s*([0-9,]+)\s*매.*?(?:칼라|컬러|color)\s*([0-9,]+)\s*매/i;
      for (const opt of (comboGroup.options || [])) {
        const name = opt?.name || String(opt || "");
        const m = comboPat.exec(name);
        if (m) {
          bwOpts.push(m[1].replace(/,/g, "") + "매");
          colorOpts.push(m[2].replace(/,/g, "") + "매");
        }
      }
    } else {
      bwOpts = bwGroup ? (bwGroup.options || []).map(o => String(o.name ?? "")) : [];
      colorOpts = colorGroup ? (colorGroup.options || []).map(o => String(o.name ?? "")) : [];
    }
    if (bwOpts.length === 0 && colorOpts.length === 0) return;

    const periodOpt = periodGroup ? ((periodGroup.options || [])[0]?.name || "3년") : "3년";
    const faxOpt = faxGroup ? ((faxGroup.options || [])[0]?.name || "팩스별도") : "팩스별도";

    // sub_info에서 보증금 추출
    let depositText = "없음";
    for (const info of (sc.sub_info || [])) {
      const s = typeof info === "string" ? info : String(info);
      if (/보증금/i.test(s)) { depositText = s.includes(":") ? s.split(":").pop().trim() : s.replace(/.*보증금\s*/i, "").trim() || s; break; }
    }

    const planCount = Math.min(Math.max(bwOpts.length, colorOpts.length, 1), 5);
    const planLabels = ["기본형", "표준형", "프리미엄", "비즈니스", "엔터프라이즈"];
    const productName = meta?.model_name || form.model_name || form.title || "제품명";
    const brandName = meta?.brand || form.brand || "";

    const mn = (productName).toUpperCase();
    let basePrice = 50000;
    if (/C325|C2450|M2040|P285|L3550|L8|MFC/.test(mn)) basePrice = 30000;
    else if (/C2060|C2263|C2265|C2560/.test(mn)) basePrice = 60000;
    else if (/C3060|C3061|C3560/.test(mn)) basePrice = 80000;
    else if (/C3567|C4570|C4571/.test(mn)) basePrice = 100000;
    else if (/C5573|C6673|C7773/.test(mn)) basePrice = 300000;
    const priceStep = Math.max(Math.round(basePrice * 0.3 / 10000) * 10000, 10000);

    const srcGroup = bwGroup || comboGroup;
    const bwUnitPrice = srcGroup?.options?.[0]?.unit_price ? Number(srcGroup.options[0].unit_price) : 10;
    const colorUnitPrice = (colorGroup || comboGroup)?.options?.[0]?.unit_price ? Number((colorGroup || comboGroup).options[0].unit_price) : 100;

    const rentalProducts = [];
    for (let i = 0; i < planCount; i++) {
      const bw = String(bwOpts[i] || bwOpts[bwOpts.length - 1] || "");
      const cl = String(colorOpts[i] || colorOpts[colorOpts.length - 1] || "");
      const label = planLabels[i] || `플랜${i + 1}`;
      const monthly = basePrice + (priceStep * i);
      const descParts = [];
      if (bw) descParts.push(`흑백 ${bw}${bw.includes("매") ? "" : "매"} (합계 ${bwUnitPrice.toLocaleString()}원)`);
      if (cl) descParts.push(`칼라 ${cl}${cl.includes("매") ? "" : "매"} (합계 ${colorUnitPrice.toLocaleString()}원)`);
      rentalProducts.push({
        product_name: `${productName} (${label})`,
        brand: brandName,
        monthly_total: monthly,
        col_values: { deposit_text: depositText, output_desc: descParts.join("\n") || "문의", contract_period: periodOpt, options_desc: faxOpt },
        image_url: "",
      });
    }

    const scIdx = arr.indexOf(sc);
    arr.splice(scIdx + 1, 0, {
      type: "rental_pricing",
      title: `${productName} 맞춤 렌탈 플랜`,
      footer_note: "* 사무환경에 알맞게 옵션 추가 협의 가능 (부가세 별도)",
      custom_columns: [
        { key: "deposit_text", label: "보증금" },
        { key: "output_desc", label: "기본 출력매수" },
        { key: "contract_period", label: "약정기간" },
        { key: "options_desc", label: "선택사항" },
      ],
      products: rentalProducts,
      _linked_showcase: true,
    });
  };

  const updateSection = (idx, patch) => {
    setForm(prev => {
      const arr = [...(prev.sections_json || [])];
      arr[idx] = { ...arr[idx], ...patch };
      // ── Auto-sync: product_showcase → rental_pricing ──
      const updatedSec = arr[idx];
      if (updatedSec?.type === "product_showcase" && ("sub_info" in patch || "option_groups" in patch)) {
        if (arr.some(s => s?.type === "rental_pricing")) {
          _crossRefShowcaseToRental(arr, updatedSec);
        }
      }
      return { ...prev, sections_json: arr };
    });
  };
  const removeSection = (idx) => setForm(prev => ({ ...prev, sections_json: (prev.sections_json || []).filter((_, i) => i !== idx) }));
  const moveSection = (idx, dir) => {
    setForm(prev => {
      const arr = [...(prev.sections_json || [])]; const to = idx + dir;
      if (to < 0 || to >= arr.length) return prev;
      [arr[idx], arr[to]] = [arr[to], arr[idx]];
      return { ...prev, sections_json: arr };
    });
    // Update collapsed indices to follow moved sections
    const newCollapsed = new Set();
    for (const c of collapsedSections) {
      if (c === idx) newCollapsed.add(idx + dir);
      else if (c === idx + dir) newCollapsed.add(idx);
      else newCollapsed.add(c);
    }
    setCollapsedSections(newCollapsed);
  };
  const addSection = (type) =>
    setForm(prev => ({ ...prev, sections_json: [...(prev.sections_json || []), JSON.parse(JSON.stringify(SECTION_TEMPLATES[type] || SECTION_TEMPLATES.text))] }));

  /* ── 렌탈 요금표 → 옵션 선택기 연동 ── */
  const syncRentalToOptions = (rentalSectionIdx) => {
    setForm(prev => {
      const secs = prev.sections_json || [];
      const rentalSec = secs[rentalSectionIdx];
      if (!rentalSec || rentalSec.type !== "rental_pricing") return prev;
      const rentalProducts = rentalSec.products || [];
      const rentalPeriods = rentalSec.custom_periods || [12, 24, 36, 48, 60];
      if (rentalProducts.length === 0) { setToast("렌탈 상품을 먼저 추가하세요."); return prev; }
      const periodGroup = { group_name: "약정 기간", group_type: "button", options: rentalPeriods.map(m => ({ name: `${m}개월`, description: "", price_modifier: 0, _period: m })) };
      const productGroup = rentalProducts.length > 1 ? { group_name: "모델 선택", group_type: "card", options: rentalProducts.map(p => ({ name: p.product_name || "제품", description: p.brand || "", price_modifier: 0, image_url: p.image_url || "" })) } : null;
      const groups = productGroup ? [productGroup, periodGroup] : [periodGroup];
      const existingOptIdx = secs.findIndex(s => s.type === "product_options");
      const arr = [...secs];
      if (existingOptIdx >= 0) {
        arr[existingOptIdx] = { ...arr[existingOptIdx], option_groups: groups, title: "렌탈 옵션 선택" };
      } else {
        arr.push({ ...JSON.parse(JSON.stringify(SECTION_TEMPLATES.product_options || {})), type: "product_options", title: "렌탈 옵션 선택", option_groups: groups });
      }
      return { ...prev, sections_json: arr };
    });
    setToast("✅ 옵션 선택기 연동 완료!");
  };

  /* ── 옵션 선택기 → 렌탈 요금표 output_desc 자동 구성 ── */
  const syncOptionsToRental = (rentalSectionIdx) => {
    setForm(prev => {
      const secs = prev.sections_json || [];
      const rentalSec = secs[rentalSectionIdx];
      if (!rentalSec || rentalSec.type !== "rental_pricing") return prev;
      // 쇼케이스 또는 옵션 섹션에서 unit_price가 있는 옵션 그룹 찾기
      const optSec = secs.find(s => s.type === "product_showcase" || s.type === "product_options");
      if (!optSec) { setToast("옵션 선택기 섹션을 먼저 추가하세요."); return prev; }
      const groups = optSec.option_groups || [];
      const pricedGroups = groups.filter(g => (g.options || []).some(o => Number(o.unit_price) > 0));
      if (pricedGroups.length === 0) { setToast("단가(unit_price)가 설정된 옵션이 없습니다."); return prev; }
      // 각 그룹의 첫 번째 옵션을 기본값으로 사용하여 output_desc 구성
      const lines = pricedGroups.map(g => {
        const firstOpt = (g.options || []).find(o => Number(o.unit_price) > 0);
        if (!firstOpt) return null;
        const qty = (firstOpt.name || "").replace(/[,\s]/g, "").match(/(\d+)/);
        const qtyNum = qty ? Number(qty[1]) : 0;
        const qtyStr = qtyNum > 0 ? qtyNum.toLocaleString() + "매" : firstOpt.name;
        const totalAmt = Number(firstOpt.unit_price) * (qtyNum || 1);
        return `${g.group_name} ${qtyStr} (합계 ${totalAmt.toLocaleString()}원)`;
      }).filter(Boolean);
      const outputDesc = lines.join("\n");
      // 모든 렌탈 플랜의 output_desc 업데이트
      const arr = [...secs];
      const updatedProducts = (rentalSec.products || []).map(p => ({
        ...p,
        col_values: { ...(p.col_values || {}), output_desc: outputDesc }
      }));
      arr[rentalSectionIdx] = { ...rentalSec, products: updatedProducts };
      return { ...prev, sections_json: arr };
    });
    setToast("✅ 옵션 단가 → 렌탈표 출력매수 연동 완료!");
  };

  /* ── 옵션 그룹 → 렌탈 요금표 자동 생성 (매수 옵션 조합 → 플랜) ── */
  const generateRentalFromOptions = (showcaseSectionIdx) => {
    let usedFallback = false;
    setForm(prev => {
      const secs = prev.sections_json || [];
      const scSec = secs[showcaseSectionIdx];
      if (!scSec) return prev;
      const groups = scSec.option_groups || [];
      if (groups.length === 0) { setToast("옵션 그룹을 먼저 추가하세요."); return prev; }

      // 그룹 분류: 흑백, 칼라, 기간, 팩스 — group_name + option내용 모두 검사
      let bwGroup = null, colorGroup = null, periodGroup = null, faxGroup = null, comboGroup = null;
      for (const g of groups) {
        const gn = (g.group_name || "").toLowerCase();
        const on = (g.options || []).map(o => (o?.name || String(o || "")).toLowerCase()).join(" ");
        const txt = gn + " " + on;
        // 하나의 그룹에 흑백+칼라 모두 포함 (결합형: "흑백 2,000매 / 칼라 200매")
        if (/흑백|b&w|mono/.test(txt) && /칼라|컬러|color/.test(txt)) { comboGroup = g; }
        else if (/흑백|b&w|mono/.test(txt)) bwGroup = g;
        else if (/칼라|컬러|color/.test(txt)) colorGroup = g;
        else if (/기간|약정|렌탈/.test(txt)) periodGroup = g;
        else if (/팩스|fax|선택/.test(txt)) faxGroup = g;
      }

      // 결합형 옵션 파싱: "흑백 2,000매 / 칼라 200매" → bw/color 분리
      let bwOpts = [], colorOpts = [];
      let parsedCombos = [];
      if (comboGroup && !bwGroup && !colorGroup) {
        const comboPat = /흑백\s*([0-9,]+)\s*매.*?(?:칼라|컬러|color)\s*([0-9,]+)\s*매/i;
        for (const opt of (comboGroup.options || [])) {
          const name = opt?.name || String(opt || "");
          const m = comboPat.exec(name);
          if (m) {
            bwOpts.push(m[1].replace(/,/g, "") + "매");
            colorOpts.push(m[2].replace(/,/g, "") + "매");
            parsedCombos.push({ bw: m[1].replace(/,/g, "") + "매", color: m[2].replace(/,/g, "") + "매", raw: name });
          } else {
            // 숫자만 있는 경우 그대로
            bwOpts.push(name);
            colorOpts.push("");
          }
        }
      } else {
        bwOpts = bwGroup ? (bwGroup.options || []).map(o => String(o.name ?? "")) : [];
        colorOpts = colorGroup ? (colorGroup.options || []).map(o => String(o.name ?? "")) : [];
      }

      const periodOpt = periodGroup ? ((periodGroup.options || [])[0]?.name || "3년") : "3년";
      const faxOpt = faxGroup ? ((faxGroup.options || [])[0]?.name || "팩스별도") : "팩스별도";

      // 매수 옵션이 없으면 일반 옵션에서도 시도 (그룹명 무관하게 옵션 이름에 매수 정보가 있는지)
      if (bwOpts.length === 0 && colorOpts.length === 0) {
        for (const g of groups) {
          const opts = g.options || [];
          for (const opt of opts) {
            const name = (opt?.name || String(opt || "")).toLowerCase();
            if (/\d+\s*매/.test(name) || /흑백|칼라|컬러/.test(name)) {
              comboGroup = g;
              break;
            }
          }
          if (comboGroup) break;
        }
        if (comboGroup) {
          const comboPat = /흑백\s*([0-9,]+)\s*매.*?(?:칼라|컬러|color)\s*([0-9,]+)\s*매/i;
          for (const opt of (comboGroup.options || [])) {
            const name = opt?.name || String(opt || "");
            const m = comboPat.exec(name);
            if (m) {
              bwOpts.push(m[1].replace(/,/g, "") + "매");
              colorOpts.push(m[2].replace(/,/g, "") + "매");
            }
          }
        }
      }

      // 매수 옵션이 없으면 모델 기반 기본 플랜 3개 생성
      let useFallback = false;
      if (bwOpts.length === 0 && colorOpts.length === 0) {
        useFallback = true;
        usedFallback = true;
        bwOpts = ["2,000매", "5,000매", "10,000매"];
        colorOpts = ["200매", "500매", "1,000매"];
      }

      // 플랜 수: 가장 많은 옵션 수 기준 (최소 1, 최대 5)
      const planCount = Math.min(Math.max(bwOpts.length, colorOpts.length, 1), 5);
      const planLabels = ["기본형", "표준형", "프리미엄", "비즈니스", "엔터프라이즈"];
      const productName = prev.model_name || prev.title || "제품명";
      const brandName = prev.brand || "";

      // 모델명 기반 기본 가격 추정
      const mn = (prev.model_name || "").toUpperCase();
      let basePrice = 50000;
      if (/C325|C2450|M2040|P285|L3550|L8|MFC/.test(mn)) basePrice = 30000;
      else if (/C2060|C2263|C2265|C2560/.test(mn)) basePrice = 60000;
      else if (/C3060|C3061|C3560/.test(mn)) basePrice = 80000;
      else if (/C3567|C4570|C4571/.test(mn)) basePrice = 100000;
      else if (/C5573|C6673|C7773/.test(mn)) basePrice = 300000;
      const priceStep = Math.max(Math.round(basePrice * 0.3 / 10000) * 10000, 10000);

      // 추가 단가 (unit_price에서 추출하거나 기본값)
      const srcGroup = bwGroup || comboGroup;
      const bwUnitPrice = srcGroup?.options?.[0]?.unit_price ? Number(srcGroup.options[0].unit_price) : 10;
      const colorUnitPrice = (colorGroup || comboGroup)?.options?.[0]?.unit_price ? Number((colorGroup || comboGroup).options[0].unit_price) : 100;

      const products = [];
      for (let i = 0; i < planCount; i++) {
        const bw = String(bwOpts[i] || bwOpts[bwOpts.length - 1] || "");
        const cl = String(colorOpts[i] || colorOpts[colorOpts.length - 1] || "");
        const label = planLabels[i] || `플랜${String(i + 1).padStart(2, "0")}`;
        const monthly = basePrice + (priceStep * i);

        // output_desc 구성
        const descParts = [];
        if (bw) descParts.push(`흑백 ${bw}${String(bw).includes("매") ? "" : "매"} (합계 ${bwUnitPrice.toLocaleString()}원)`);
        if (cl) descParts.push(`칼라 ${cl}${String(cl).includes("매") ? "" : "매"} (합계 ${colorUnitPrice.toLocaleString()}원)`);

        products.push({
          product_name: `${productName} (${label})`,
          brand: brandName,
          monthly_total: monthly,
          col_values: {
            deposit_text: "없음",
            output_desc: descParts.join("\n") || "문의",
            contract_period: periodOpt,
            options_desc: faxOpt,
          },
          image_url: "",
        });
      }

      const newRentalSec = {
        type: "rental_pricing",
        title: `${productName} 맞춤 렌탈 플랜`,
        footer_note: "* 사무환경에 알맞게 옵션 추가 협의 가능 (부가세 별도)",
        custom_columns: [
          { key: "deposit_text", label: "보증금" },
          { key: "output_desc", label: "기본 출력매수" },
          { key: "contract_period", label: "약정기간" },
          { key: "options_desc", label: "선택사항" },
        ],
        products,
      };

      const arr = [...secs];
      const existingIdx = arr.findIndex(s => s.type === "rental_pricing");
      if (existingIdx >= 0) {
        arr[existingIdx] = newRentalSec;
      } else {
        arr.splice(showcaseSectionIdx + 1, 0, newRentalSec);
      }
      return { ...prev, sections_json: arr };
    });
    setToast(usedFallback
      ? "✅ 기본 렌탈 요금표 생성 완료! (매수/가격을 실제 값으로 수정하세요)"
      : "✅ 옵션 조합으로 렌탈 요금표 생성 완료! (가격은 편집기에서 수정 가능)");
  };

  /* ── 렌탈 요금표 → 쇼케이스 옵션 자동 생성 (표 분석 → 옵션 그룹 역생성) ── */
  const generateOptionsFromRental = (rentalSectionIdx) => {
    setForm(prev => {
      const secs = prev.sections_json || [];
      const rentalSec = secs[rentalSectionIdx];
      if (!rentalSec || rentalSec.type !== "rental_pricing") return prev;
      const products = rentalSec.products || [];
      if (products.length === 0) { setToast("렌탈 플랜을 먼저 추가하세요."); return prev; }

      const columns = rentalSec.custom_columns || [
        { key: "deposit_text", label: "보증금" },
        { key: "output_desc", label: "기본 출력매수" },
        { key: "contract_period", label: "약정기간" },
        { key: "options_desc", label: "선택사항" },
      ];

      // ── 1. 출력매수 파싱 → 흑백/칼라 옵션 그룹 추출 ──
      const bwSet = new Map(); // name → { name, unit_price }
      const colorSet = new Map();
      const periodSet = new Set();
      const faxSet = new Set();

      for (const p of products) {
        const cv = p.col_values || {};
        // 출력매수 파싱: "흑백 - 1,000매 (합계 10,000원)\n칼라 - 500매 (합계 50,000원)"
        const outputDesc = cv.output_desc || "";
        for (const line of outputDesc.split(/[\n;]/).map(l => l.trim()).filter(Boolean)) {
          const isBw = /흑백|b&w|mono/i.test(line);
          const isColor = /칼라|컬러|color/i.test(line);
          // 매수 추출
          const qtyMatch = line.match(/[\d,]+\s*매/);
          const qty = qtyMatch ? qtyMatch[0].replace(/\s/g, "") : "";
          // 합계 금액 추출 (기존 장당 형식도 호환)
          const unitMatch = line.match(/장당\s*([\d,]+)\s*원|합계\s*([\d,]+)\s*원/);
          const unitPrice = unitMatch ? Number((unitMatch[1] || unitMatch[2]).replace(/,/g, "")) : 0;
          if (isBw && qty) bwSet.set(qty, { name: qty, unit_price: unitPrice, price_label: unitPrice > 0 ? `합계 ${unitPrice.toLocaleString()}원` : "" });
          if (isColor && qty) colorSet.set(qty, { name: qty, unit_price: unitPrice, price_label: unitPrice > 0 ? `합계 ${unitPrice.toLocaleString()}원` : "" });
        }
        // 약정기간
        const period = cv.contract_period || "";
        if (period.trim()) periodSet.add(period.trim());
        // 선택사항
        const opt = cv.options_desc || "";
        if (opt.trim()) faxSet.add(opt.trim());
      }

      // ── 2. 옵션 그룹 구성 ──
      const groups = [];
      if (bwSet.size > 0) {
        groups.push({
          group_name: "흑백 출력매수",
          group_type: "list",
          group_description: "월 흑백 기본 출력 매수를 선택하세요.",
          options: [...bwSet.values()],
        });
      }
      if (colorSet.size > 0) {
        groups.push({
          group_name: "칼라 출력매수",
          group_type: "list",
          group_description: "월 칼라 기본 출력 매수를 선택하세요.",
          options: [...colorSet.values()],
        });
      }
      if (periodSet.size > 0) {
        groups.push({
          group_name: "약정기간",
          group_type: "button",
          group_description: "원하는 약정 기간을 선택하세요.",
          options: [...periodSet].map(p => ({ name: p, price_label: "" })),
        });
      }
      if (faxSet.size > 0) {
        groups.push({
          group_name: "선택사항",
          group_type: "button",
          group_description: "추가 옵션을 선택하세요.",
          options: [...faxSet].map(f => ({ name: f, price_label: "" })),
        });
      }

      if (groups.length === 0) {
        // 매수 파싱 실패 시, 플랜별 product_name을 옵션으로
        groups.push({
          group_name: "렌탈 플랜",
          group_type: "list",
          group_description: "원하는 렌탈 플랜을 선택하세요.",
          options: products.map(p => ({
            name: p.product_name || "플랜",
            price_label: p.monthly_total ? `월 ${Number(p.monthly_total).toLocaleString()}원` : "",
          })),
        });
      }

      // ── 3. product_showcase 섹션에 반영 ──
      const arr = [...secs];
      const existingIdx = arr.findIndex(s => s.type === "product_showcase");
      if (existingIdx >= 0) {
        arr[existingIdx] = { ...arr[existingIdx], option_groups: groups, title: arr[existingIdx].title || prev.model_name || "제품 옵션" };
      } else {
        const newShowcase = {
          type: "product_showcase",
          title: prev.model_name || prev.title || "제품 옵션",
          price_label: products[0]?.monthly_total ? `월 ${Number(products[0].monthly_total).toLocaleString()}원부터` : "",
          images: [prev.hero_image || ""].filter(Boolean),
          option_groups: groups,
          sub_info: [],
        };
        // 렌탈표 바로 앞에 삽입
        arr.splice(rentalSectionIdx, 0, newShowcase);
      }

      // ── 4. 연동 메타 저장 (양방향 추적용) ──
      // _linked_rental_idx 플래그로 추후 자동 동기화 지원
      const scIdx = arr.findIndex(s => s.type === "product_showcase");
      const rnIdx = arr.findIndex(s => s.type === "rental_pricing");
      if (scIdx >= 0) arr[scIdx] = { ...arr[scIdx], _linked_rental: true };
      if (rnIdx >= 0) arr[rnIdx] = { ...arr[rnIdx], _linked_showcase: true };

      return { ...prev, sections_json: arr };
    });
    setToast("✅ 렌탈표 → 옵션 그룹 자동 생성 완료! (쇼케이스 섹션에서 확인하세요)");
  };

  /* ── 렌탈 요금제(rental_plans) → 쇼케이스 옵션 연동 ── */
  const syncPlansToShowcase = (plansSectionIdx) => {
    setForm(prev => {
      const secs = prev.sections_json || [];
      const plansSec = secs[plansSectionIdx];
      if (!plansSec || plansSec.type !== "rental_plans") return prev;
      const plans = plansSec.plans || [];
      if (plans.length === 0) { setToast("요금제를 먼저 추가하세요."); return prev; }
      const optionGroup = {
        group_name: "요금제",
        group_type: "list",
        group_description: "비즈니스 규모에 맞는 최적의 요금제를 선택하세요.",
        options: plans.map(p => ({
          name: p.plan_name || p.model_name || "요금제",
          price_label: p.monthly_price ? `월 ${Number(p.monthly_price).toLocaleString()}원` : "",
          color_hex: "#000",
          image_url: "",
        }))
      };
      const arr = [...secs];
      const scIdx = arr.findIndex(s => s.type === "product_showcase");
      if (scIdx >= 0) {
        const existing = arr[scIdx].option_groups || [];
        const planGroupIdx = existing.findIndex(g => g.group_name === "요금제");
        const newGroups = [...existing];
        if (planGroupIdx >= 0) { newGroups[planGroupIdx] = optionGroup; } else { newGroups.push(optionGroup); }
        arr[scIdx] = { ...arr[scIdx], option_groups: newGroups };
      } else {
        const optIdx = arr.findIndex(s => s.type === "product_options");
        if (optIdx >= 0) {
          const existing = arr[optIdx].option_groups || [];
          const planGroupIdx = existing.findIndex(g => g.group_name === "요금제");
          const newGroups = [...existing];
          if (planGroupIdx >= 0) { newGroups[planGroupIdx] = optionGroup; } else { newGroups.push(optionGroup); }
          arr[optIdx] = { ...arr[optIdx], option_groups: newGroups };
        }
      }
      return { ...prev, sections_json: arr };
    });
    setToast("✅ 요금제 → 옵션 연동 완료!");
  };

  const applyFoodPreset = (presetKey) => {
    const preset = FOOD_PAGE_PRESETS[presetKey];
    if (!preset) return;
    const newSections = preset.sections.map(
      type => JSON.parse(JSON.stringify(SECTION_TEMPLATES[type] || SECTION_TEMPLATES.text))
    );
    setForm(prev => ({
      ...prev,
      theme: preset.theme || prev.theme,
      sections_json: [...(prev.sections_json || []), ...newSections],
    }));
    setToast(`✅ ${preset.label} 프리셋 적용! (${newSections.length}개 섹션 추가)`);
  };
  const toggleExpand = (idx) => {
    const s = new Set(collapsedSections);
    s.has(idx) ? s.delete(idx) : s.add(idx);
    setCollapsedSections(s);
  };

  /* ── Items helpers ── */
  const items = Array.isArray(form.items) ? form.items : [];
  const addItem = () => setForm({ ...form, items: [...items, { product_name: "", model: "", brand: "", category: "", sale_type: "sale", price: 0, rental_price: 0, image_url: "", description: "" }] });
  const updateItem = (idx, patch) => {
    const arr = [...items]; arr[idx] = { ...arr[idx], ...patch };
    setForm({ ...form, items: arr });
  };
  const removeItem = (idx) => setForm({ ...form, items: items.filter((_, i) => i !== idx) });

  /* ── AI Generation helpers ── */
  const aiContext = { brand: form.brand, model_name: form.model_name, category: form.category, title: form.title, subtitle: form.subtitle, hero_image: form.hero_image, sections_json: form.sections_json, sale_type: form.sale_type, price: form.price, rental_price: form.rental_price, theme: form.theme || "minimal" };

  const handleAiGenerate = async (description = "") => {
    if (!form.brand && !form.model_name && !form.category && !description) {
      setToast("브랜드, 모델명, 카테고리 중 하나 이상 입력 후 AI 생성하세요.");
      return;
    }
    setAiGenerating(true);
    try {
      const { data } = await api.post("/api/admin/product-pages/ai-generate", {
        ...form, description,
      }, { timeout: 60000 });
      const r = data.result;
      setForm(prev => ({
        ...prev,
        title: r.title || prev.title,
        subtitle: r.subtitle || prev.subtitle,
        model_name: r.model_name || prev.model_name,
        hero_html: r.hero_html || prev.hero_html,
        sections_json: Array.isArray(r.sections) ? r.sections : prev.sections_json,
        seo_title: r.seo_title || prev.seo_title,
        seo_description: r.seo_description || prev.seo_description,
      }));
      setToast("✅ AI 콘텐츠 생성 완료!");
    } catch (e) {
      setToast(e.response?.data?.error || "AI 생성 실패");
    }
    setAiGenerating(false);
  };

  const handleAiField = async (field) => {
    setAiFieldLoading(field);
    try {
      const { data } = await api.post("/api/admin/product-pages/ai-field", {
        field, current_value: form[field] || "", context: aiContext,
      }, { timeout: 30000 });
      setForm(prev => ({ ...prev, [field]: data.value }));
      setToast(`✨ ${field} AI 생성 완료`);
    } catch (e) {
      setToast(e.response?.data?.error || "AI 생성 실패");
    }
    setAiFieldLoading("");
  };

  const handleAiSection = async (sectionType, instruction = "") => {
    setAiFieldLoading(`section_${sectionType}`);
    try {
      const { data } = await api.post("/api/admin/product-pages/ai-section", {
        section_type: sectionType, context: { ...aiContext, instruction },
      }, { timeout: 30000 });
      if (data.section) {
        setForm(prev => {
          const existing = prev.sections_json || [];
          const idx = existing.findIndex(s => s.type === sectionType);
          if (idx >= 0) {
            // Replace existing section of the same type in-place
            const updated = [...existing];
            updated[idx] = data.section;
            return { ...prev, sections_json: updated };
          }
          return { ...prev, sections_json: [...existing, data.section] };
        });
        setToast(`✨ ${SECTION_LABELS[sectionType] || sectionType} 섹션 AI 생성 완료`);
      }
    } catch (e) {
      setToast(e.response?.data?.error || "AI 섹션 생성 실패");
    }
    setAiFieldLoading("");
  };

  /* ── AI 프롬프트: 텍스트/이미지/파일 → 섹션 자동 생성 (없으면 추가, 있으면 교체) ── */
  const handleAiPrompt = async () => {
    if (!aiPromptText && aiPromptFiles.length === 0) { setToast("텍스트나 파일을 입력하세요."); return; }
    setAiFieldLoading("ai_prompt");
    try {
      const fd = new FormData();
      fd.append("prompt", aiPromptText || "");
      fd.append("context", JSON.stringify(aiContext));
      aiPromptFiles.forEach((f, i) => fd.append("files", f));
      const { data } = await api.post("/api/admin/product-pages/ai-prompt", fd, {
        timeout: 120000,
        headers: { "Content-Type": "multipart/form-data" },
      });
      const newSections = data.sections && Array.isArray(data.sections) ? data.sections : data.section ? [data.section] : [];
      // Auto-fill fields from AI response
      if (data.auto_fill) {
        setForm(prev => {
          const updates = {};
          if (data.auto_fill.hero_image && !prev.hero_image) updates.hero_image = data.auto_fill.hero_image;
          if (data.auto_fill.title && !prev.title) updates.title = data.auto_fill.title;
          if (data.auto_fill.subtitle && !prev.subtitle) updates.subtitle = data.auto_fill.subtitle;
          if (data.auto_fill.brand && !prev.brand) updates.brand = data.auto_fill.brand;
          if (data.auto_fill.model_name && !prev.model_name) updates.model_name = data.auto_fill.model_name;
          return Object.keys(updates).length > 0 ? { ...prev, ...updates } : prev;
        });
      }
      if (newSections.length > 0) {
        setForm(prev => {
          const existing = [...(prev.sections_json || [])];
          const replaced = new Set();
          const updated = new Set();
          // 같은 type의 기존 섹션이 있으면 교체, 없으면 추가
          for (const ns of newSections) {
            const t = ns.type;
            const idx = existing.findIndex(s => s.type === t);
            if (idx >= 0) {
              existing[idx] = ns;
              replaced.add(t);
            } else {
              existing.push(ns);
              updated.add(t);
            }
          }
          const msgs = [];
          if (replaced.size > 0) msgs.push(`${[...replaced].join(", ")} 교체`);
          if (updated.size > 0) msgs.push(`${[...updated].join(", ")} 추가`);
          setToast(`✅ ${msgs.join(", ")} 완료!`);
          // ── Cross-reference: showcase ↔ rental_pricing 연동 ──
          const sc = existing.find(s => s.type === "product_showcase");
          if (sc) _crossRefShowcaseToRental(existing, sc);
          return { ...prev, sections_json: existing };
        });
      }
      setAiPromptText("");
      setAiPromptFiles([]);
      setShowAiPrompt(false);
    } catch (e) {
      setToast(e.response?.data?.error || "AI 프롬프트 처리 실패");
    }
    setAiFieldLoading("");
  };

  /* ── URL 분석 → 상세페이지 자동 생성 (에이전트 탐색 + 하네스 엔지니어링) ── */
  const handleUrlAnalyze = async () => {
    if (!analyzeUrl.trim()) { setToast("URL을 입력하세요."); return; }
    setUrlAnalyzing(true);
    try {
      const payload = {
        url: analyzeUrl.trim(),
        model_name: form.model_name || "",
        sale_type: form.sale_type || "rental",
        theme: form.theme || "minimal",
      };
      if (pasteHtml.trim()) {
        payload.raw_html = pasteHtml.trim();
      }
      const { data } = await api.post("/api/admin/product-pages/ai-analyze-url", payload, { timeout: 120000 });
      const r = data.result || {};
      // Sanitize sections to prevent React render crashes
      const sanitizedSections = Array.isArray(r.sections) ? r.sections.map(sec => {
        if (!sec || typeof sec !== "object") return { type: "text", title: "", content: String(sec || "") };
        const s = { ...sec };
        // Ensure features are plain strings
        if (s.features && Array.isArray(s.features)) s.features = s.features.map(f => typeof f === "object" ? (f.title || f.name || JSON.stringify(f)) : String(f || ""));
        // Ensure spec_table rows are arrays
        if (s.type === "spec_table" && Array.isArray(s.rows)) s.rows = s.rows.map(row => Array.isArray(row) ? row.map(c => safeStr(c)) : Object.values(row || {}).map(c => safeStr(c)));
        // Ensure items have string fields
        if (Array.isArray(s.items)) s.items = s.items.map(item => typeof item === "object" && item ? { ...item, title: safeStr(item.title), description: safeStr(item.description), icon: safeStr(item.icon) } : { title: safeStr(item), description: "", icon: "⚡" });
        return s;
      }) : null;

      // ── Cross-reference: showcase ↔ rental_pricing 연동 ──
      if (sanitizedSections) {
        const sc = sanitizedSections.find(s => s.type === "product_showcase");
        if (sc) {
          _crossRefShowcaseToRental(sanitizedSections, sc);
          // rental_pricing 섹션이 없으면 옵션에서 자동 생성 (렌탈 sale_type일 때)
          if ((form.sale_type || "rental") === "rental" && !sanitizedSections.some(s => s.type === "rental_pricing")) {
            _autoGenRentalFromShowcase(sanitizedSections, sc, r);
          }
        }
      }

      // ── Extract price / rental_price from sections ──
      let extractedPrice = 0, extractedRentalPrice = 0;
      if (sanitizedSections) {
        for (const sec of sanitizedSections) {
          if (sec.type === "sale_pricing" && Array.isArray(sec.products)) {
            const p0 = sec.products[0];
            if (p0) extractedPrice = Number(p0.body_price || p0.price || 0) || 0;
          }
          if (sec.type === "rental_pricing" && Array.isArray(sec.products)) {
            const p0 = sec.products[0];
            if (p0) extractedRentalPrice = Number(p0.monthly_total || p0.monthly_price || 0) || 0;
          }
        }
      }
      // Fallback: extract from specs_json
      if (!extractedPrice && r.specs_json && typeof r.specs_json === "object") {
        for (const cat of Object.values(r.specs_json)) {
          if (typeof cat !== "object") continue;
          for (const [k, v] of Object.entries(cat)) {
            if (/가격|판매가|할인가/.test(k) && !extractedPrice) {
              const m = String(v).replace(/[^0-9]/g, "");
              if (m) extractedPrice = Number(m) || 0;
            }
            if (/월.*렌탈|렌탈료/.test(k) && !extractedRentalPrice) {
              const m = String(v).replace(/[^0-9]/g, "");
              if (m) extractedRentalPrice = Number(m) || 0;
            }
          }
        }
      }

      setForm(prev => ({
        ...prev,
        title: safeStr(r.title) || prev.title,
        subtitle: safeStr(r.subtitle) || prev.subtitle,
        brand: safeStr(r.brand) || prev.brand,
        model_name: safeStr(r.model_name) || prev.model_name,
        category: safeStr(r.category) || prev.category,
        hero_image: safeStr(r.hero_image) || prev.hero_image,
        hero_html: safeStr(r.hero_html) || prev.hero_html,
        sections_json: sanitizedSections || prev.sections_json,
        seo_title: safeStr(r.seo_title) || prev.seo_title,
        seo_description: safeStr(r.seo_description) || prev.seo_description,
        specs_json: (r.specs_json && typeof r.specs_json === "object") ? r.specs_json : prev.specs_json,
        price: extractedPrice || prev.price,
        rental_price: extractedRentalPrice || prev.rental_price,
      }));
      const visionInfo = data.vision_images_analyzed > 0 ? ` | 이미지 ${data.vision_images_analyzed}장 비전 분석` : '';
      setToast(`✅ URL 분석 완료! (${(data.latency_ms / 1000).toFixed(1)}초${visionInfo})`);
      setAnalyzeUrl("");
      setShowUrlAnalyze(false);
      setShowHtmlPaste(false);
      setPasteHtml("");
    } catch (e) {
      const errData = e.response?.data;
      if (errData?.blocked) {
        setShowHtmlPaste(true);
        setToast("⚠️ 봇 차단으로 URL 접근 불가 — 아래 HTML 붙여넣기로 분석할 수 있습니다");
      } else {
        setToast(errData?.error || "URL 분석 실패");
      }
    }
    setUrlAnalyzing(false);
  };

  /* ── HTML 익스포트 ── */
  const handleExportHTML = () => {
    const previewEl = document.querySelector("[data-preview-renderer]");
    if (!previewEl) { setToast("미리보기가 표시되지 않았습니다."); return; }
    const htmlContent = `<!DOCTYPE html>
<html lang="ko">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>${form.title || "상세페이지"}</title>
<script src="https://cdn.tailwindcss.com"><\/script>
<style>body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI','Noto Sans KR',sans-serif;margin:0}</style>
</head>
<body>
${previewEl.outerHTML}
</body>
</html>`;
    const blob = new Blob([htmlContent], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${form.title || "product-page"}.html`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setToast("✅ HTML 파일 다운로드 완료!");
  };

  /* ── 이미지 익스포트 (초고화질 캡처) ── */
  const handleExportImage = async () => {
    const previewEl = document.querySelector("[data-preview-renderer]");
    if (!previewEl) { setToast("미리보기가 표시되지 않았습니다."); return; }
    setToast("📸 초고화질 이미지 캡처 중...");
    try {
      const { default: html2canvas } = await import("html2canvas");
      const canvas = await html2canvas(previewEl, {
        useCORS: true,
        scale: 4,
        backgroundColor: "#ffffff",
        logging: false,
        imageTimeout: 30000,
        allowTaint: false,
      });
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `${form.title || "product-page"}_4x.png`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setToast("✅ 초고화질 이미지 다운로드 완료!");
      }, "image/png");
    } catch (e) {
      setToast("이미지 캡처 실패: html2canvas를 설치하세요 (npm i html2canvas)");
    }
  };

  /* ── AI 통계 로드 ── */
  const loadAiStats = async (days = 30) => {
    try {
      const { data } = await api.get(`/api/admin/ai-prompt-stats?days=${days}`);
      setAiStats(data);
    } catch (e) {
      setToast("AI 통계 로드 실패");
    }
  };

  /* ── Quick-start templates (multi-mode: 원소스 멀티유즈) ── */
  const QUICK_TEMPLATES = [
    /* ── 복합기/사무기기 모드 ── */
    { key: "color_mfp_rental", label: "컬러복합기 렌탈", icon: "🖨️", mode: "사무기기", brand: "", category: "컬러복합기", sale_type: "rental",
      preset_sections: ["image_text_block", "product_options", "rental_pricing", "feature_highlight", "spec_table", "cta"] },
    { key: "bw_mfp_rental", label: "흑백복합기 렌탈", icon: "🖨️", mode: "사무기기", brand: "", category: "흑백복합기", sale_type: "rental",
      preset_sections: ["image_text_block", "product_options", "rental_pricing", "feature_highlight", "spec_table", "cta"] },
    { key: "printer_sale", label: "프린터 판매", icon: "🖨️", mode: "사무기기", brand: "", category: "프린터", sale_type: "sale",
      preset_sections: ["image_text_block", "sale_pricing", "feature_highlight", "spec_table", "cta"] },
    { key: "digital_press", label: "고속인쇄기", icon: "🏭", mode: "사무기기", brand: "", category: "고속인쇄기", sale_type: "sale",
      preset_sections: ["image_text_block", "sale_pricing", "feature_highlight", "spec_table", "cta"] },
    { key: "toner", label: "토너/소모품", icon: "🔴", mode: "사무기기", brand: "", category: "토너", sale_type: "sale", store_category: "토너소모품",
      preset_sections: ["heading_text", "toner_spec", "spec_table", "cta"] },
    { key: "large_mfp", label: "대형복합기 렌탈", icon: "📠", mode: "사무기기", brand: "", category: "대형복합기", sale_type: "rental",
      preset_sections: ["image_text_block", "product_options", "rental_pricing", "feature_highlight", "spec_table", "cta"] },
    { key: "sale_copier", label: "복사기 판매", icon: "🏷️", mode: "사무기기", brand: "", category: "복사기", sale_type: "sale",
      preset_sections: ["image_text_block", "product_options", "sale_pricing", "feature_highlight", "spec_table", "cta"] },
  ];
  const MODE_LABELS = [
    { key: "사무기기", icon: "🖨️" },
  ];
  const [templateMode, setTemplateMode] = useState("사무기기");

  const applyQuickTemplate = (tmpl) => {
    const presetSections = (tmpl.preset_sections || []).map(
      type => JSON.parse(JSON.stringify(SECTION_TEMPLATES[type] || SECTION_TEMPLATES.text))
    );
    setForm(prev => ({
      ...prev,
      category: tmpl.category,
      sale_type: tmpl.sale_type,
      store_category: tmpl.store_category || "",
      theme: suggestTheme(tmpl.category, tmpl.mode),
      sections_json: presetSections.length > 0 ? presetSections : prev.sections_json,
    }));
    setShowQuickStart(false);
    setShowPreview(true);
  };

  /* ── AI Sparkle button component ── */
  const AiBtn = ({ field, size = "sm", className = "" }) => (
    <button
      onClick={(e) => { e.preventDefault(); handleAiField(field); }}
      disabled={!!aiFieldLoading}
      className={`inline-flex items-center gap-0.5 text-purple-500 hover:text-purple-700 hover:bg-purple-50 rounded-lg transition disabled:opacity-40 ${size === "sm" ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-1 text-xs"} ${className}`}
      title="AI로 생성"
    >
      {aiFieldLoading === field ? <span className="animate-spin">⏳</span> : "✨"} AI
    </button>
  );

  if (loading) return <div className="p-12 text-center text-gray-400">로딩 중...</div>;

  /* Section header helper */
  const SectionHead = ({ icon, title }) => (
    <div className="flex items-center gap-2.5 mt-8 mb-3 first:mt-0">
      <span className="text-lg">{icon}</span>
      <h2 className="text-sm font-bold text-gray-800 uppercase tracking-wider whitespace-nowrap">{title}</h2>
      <div className="flex-1 border-t border-gray-200" />
    </div>
  );

  const handlePrimaryPublishAction = () => {
    const currentMallType = formRef.current.mall_type || form.mall_type;
    if (!currentMallType) {
      setShowPublishModal(true);
      return;
    }
    handleSave(true);
  };

  const primaryPublishLabel = form.status === "published" ? "공개 업데이트" : "발행";

  return (
    <div className="h-[calc(100vh-48px)] flex flex-col bg-gray-100">
      {/* ── Recovery banner ── */}
      {showRecovery && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-between gap-3 shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-amber-600">⚠️</span>
            <p className="text-xs text-amber-700">저장되지 않은 편집 내용이 있습니다</p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button onClick={() => { setForm(recoveredRef.current); setShowRecovery(false); setToast("✅ 복구 완료"); }}
              className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-amber-600 text-white hover:bg-amber-700">복구</button>
            <button onClick={() => { setShowRecovery(false); try { localStorage.removeItem(storageKey); } catch {} }}
              className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-gray-100 text-gray-600 hover:bg-gray-200">무시</button>
          </div>
        </div>
      )}

      {/* ── Photoshop-style Top Bar ── */}
      <div className="h-11 flex items-center justify-between px-3 bg-white border-b border-gray-200 shrink-0 z-30 gap-2">
        {/* Left: Back + Title */}
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 shrink-0" title="돌아가기">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/></svg>
          </button>
          <input value={form.title || ""} onChange={e => setForm({...form, title: e.target.value})}
            className="text-sm font-bold text-gray-900 bg-transparent border-0 outline-none focus:ring-0 min-w-0 flex-1 truncate placeholder-gray-400"
            placeholder={isNew ? "새 상세페이지 — 타이틀 입력" : "상세페이지 편집"} />
        </div>

        {/* Center: key controls */}
        <div className="flex items-center gap-1.5">
          <button onClick={undo} disabled={historyIdxRef.current <= 0} title="실행취소"
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a5 5 0 015 5v2M3 10l4-4m-4 4l4 4"/></svg>
          </button>
          <button onClick={redo} disabled={historyIdxRef.current >= historyRef.current.length - 1} title="다시실행"
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10H11a5 5 0 00-5 5v2m15-7l-4-4m4 4l-4 4"/></svg>
          </button>
          <div className="w-px h-5 bg-gray-200 mx-0.5" />
        </div>

        {/* Right: Action buttons */}
        <div className="flex items-center gap-1.5 shrink-0">
          <button onClick={() => setShowLivePreview(!showLivePreview)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${showLivePreview ? "bg-blue-100 text-blue-700 ring-1 ring-blue-300" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
            👁️ 미리보기
          </button>
          <button onClick={() => setShowDesignCanvas(true)}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-100 text-gray-600 hover:bg-violet-50 hover:text-violet-700 transition-all">
            🎨 디자인 캔버스
          </button>
          <button onClick={() => setShowQuickStart(!showQuickStart)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${showQuickStart ? "bg-amber-100 text-amber-700 ring-1 ring-amber-300" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
            🎨 프리셋
          </button>
          <button onClick={() => handleAiGenerate()} disabled={aiGenerating || !!aiFieldLoading}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gradient-to-r from-purple-500 to-indigo-600 text-white hover:from-purple-600 hover:to-indigo-700 disabled:opacity-50">
            {aiGenerating ? "⏳ AI..." : "✨ AI"}
          </button>
          <button onClick={() => setShowAiPrompt(!showAiPrompt)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${showAiPrompt ? "bg-pink-100 text-pink-700 ring-1 ring-pink-300" : "bg-gray-100 text-gray-600 hover:bg-pink-50 hover:text-pink-600"}`}>
            🐱 AI 프롬프트
          </button>
          <button onClick={() => setShowUrlAnalyze(!showUrlAnalyze)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${showUrlAnalyze ? "bg-cyan-100 text-cyan-700 ring-1 ring-cyan-300" : "bg-gray-100 text-gray-600 hover:bg-cyan-50 hover:text-cyan-600"}`}>
            🔗 URL 분석
          </button>
          <button onClick={() => { setShowAiStats(!showAiStats); if (!showAiStats && !aiStats) loadAiStats(); }}
            className={`px-2 py-1.5 rounded-lg text-xs font-semibold transition-all ${showAiStats ? "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-300" : "bg-gray-100 text-gray-600 hover:bg-emerald-50 hover:text-emerald-600"}`}
            style={{ display: isAdmin ? undefined : 'none' }}>
            📊
          </button>
          <button onClick={() => setPresenting(true)}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gradient-to-r from-orange-500 to-red-500 text-white hover:from-orange-600 hover:to-red-600">
            📺
          </button>
          <div className="w-px h-5 bg-gray-200 mx-0.5" />
          <button onClick={handlePrimaryPublishAction} disabled={saving}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-green-600 text-white hover:bg-green-700 disabled:opacity-50">
            {saving ? "처리 중..." : primaryPublishLabel}
          </button>
          <div className="w-px h-5 bg-gray-200 mx-0.5" />
          <button onClick={handleExportHTML} title="HTML 다운로드"
            className="px-2 py-1.5 rounded-lg text-xs font-semibold bg-gray-100 text-gray-600 hover:bg-orange-50 hover:text-orange-600 transition-all">
            📄 HTML
          </button>
          <button onClick={handleExportImage} title="이미지 캡처"
            className="px-2 py-1.5 rounded-lg text-xs font-semibold bg-gray-100 text-gray-600 hover:bg-teal-50 hover:text-teal-600 transition-all">
            🖼️ PNG
          </button>
        </div>
      </div>

      {/* AI Generation overlay */}
      {aiGenerating && (
        <div className="absolute inset-0 z-50 bg-white/80 backdrop-blur-sm flex items-center justify-center">
          <div className="text-center"><span className="text-4xl animate-bounce block mb-2">✨</span><p className="text-sm font-semibold text-purple-600">AI가 페이지를 생성하고 있습니다...</p></div>
        </div>
      )}

      {/* ── Publish Mall Type Modal ── */}
      {showPublishModal && (
        <div className="fixed inset-0 z-[9999] bg-black/40 backdrop-blur-sm flex items-center justify-center" onClick={() => setShowPublishModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-[340px] max-w-[90vw]" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-900 mb-1">발행 위치 선택</h3>
            <p className="text-sm text-gray-500 mb-5">어디에 발행할지 선택해주세요</p>
            <div className="flex gap-3">
              <button onClick={() => { setForm(p => ({ ...p, mall_type: "general" })); formRef.current.mall_type = "general"; setShowPublishModal(false); setTimeout(() => handleSave(true), 50); }}
                className="flex-1 py-3 rounded-xl border-2 border-blue-500 bg-blue-50 text-blue-700 font-bold text-sm hover:bg-blue-100 transition">
                🏪 일반몰
              </button>
              <button onClick={() => { setForm(p => ({ ...p, mall_type: "b2b" })); formRef.current.mall_type = "b2b"; setShowPublishModal(false); setTimeout(() => handleSave(true), 50); }}
                className="flex-1 py-3 rounded-xl border-2 border-purple-500 bg-purple-50 text-purple-700 font-bold text-sm hover:bg-purple-100 transition">
                🏢 사업자몰
              </button>
            </div>
            <button onClick={() => setShowPublishModal(false)} className="w-full mt-3 py-2 text-sm text-gray-400 hover:text-gray-600">취소</button>
          </div>
        </div>
      )}

      {/* ── Quick-start Preset Panel (드롭다운) ── */}
      {showQuickStart && (
        <div className="bg-white border-b border-gray-200 shadow-md shrink-0 z-20 px-4 py-3 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-bold text-gray-800 text-sm">🎨 빠른 시작 — 원소스 멀티유즈</h3>
              <p className="text-[10px] text-gray-500">업종 모드를 선택하고 템플릿으로 시작하세요</p>
            </div>
            <button onClick={() => setShowQuickStart(false)} className="p-1 rounded-lg hover:bg-gray-100 text-gray-400 text-xs">✕</button>
          </div>
          <div className="flex gap-2 flex-wrap">
            {MODE_LABELS.map(m => (
              <button key={m.key} onClick={() => setTemplateMode(m.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${templateMode === m.key ? "bg-gray-900 text-white shadow" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
                {m.icon} {m.key}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-8 gap-2">
            {QUICK_TEMPLATES.filter(t => t.mode === templateMode).map(t => (
              <button key={t.key} onClick={() => applyQuickTemplate(t)}
                className={`p-2.5 rounded-xl border-2 text-center transition hover:border-purple-400 hover:bg-purple-50/50 ${form.category === t.category && form.sale_type === t.sale_type ? "border-purple-500 bg-purple-50" : "border-gray-200"}`}>
                <span className="text-xl block mb-0.5">{t.icon}</span>
                <span className="text-[10px] font-semibold text-gray-700 block truncate">{t.label}</span>
              </button>
            ))}
          </div>
          {/* 식품 프리셋 */}
          <div className="flex gap-1.5 flex-wrap border-t border-gray-100 pt-2">
            <span className="text-[10px] text-green-600 font-semibold self-center mr-1">🍽️ 식품 프리셋:</span>
            {Object.entries(FOOD_PAGE_PRESETS).map(([key, preset]) => (
              <button key={key} onClick={() => { applyFoodPreset(key); setShowQuickStart(false); }}
                title={preset.description}
                className="px-2.5 py-1 rounded-lg text-[10px] font-semibold bg-green-50 text-green-700 hover:bg-green-100 border border-green-200 transition">
                {preset.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── AI 프롬프트 패널 (고양이 언어모델) ── */}
      {showAiPrompt && (
        <div className="bg-gradient-to-r from-pink-50 to-purple-50 border-b border-pink-200 shadow-md shrink-0 z-20 px-4 py-3 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-bold text-gray-800 text-sm">🐱 AI 프롬프트 — 고양이 언어모델</h3>
              <p className="text-[10px] text-gray-500">제품 사양표 이미지/파일 업로드 또는 텍스트 입력 → AI가 섹션 자동 생성</p>
            </div>
            <button onClick={() => setShowAiPrompt(false)} className="p-1 rounded-lg hover:bg-gray-100 text-gray-400 text-xs">✕</button>
          </div>
          <div className="flex gap-3">
            <div className="flex-1 space-y-2">
              <textarea value={aiPromptText} onChange={e => setAiPromptText(e.target.value)}
                placeholder={"예시:\n• 제품 사양을 표로 정리해줘\n• 이 이미지에서 스펙 추출해서 비교표 만들어줘\n• 렌탈 요금표를 프리미엄 스타일로 만들어줘\n• 색상 옵션: 블루, 골드, 화이트, 블랙 — 쇼케이스 섹션 만들어줘"}
                className="w-full rounded-xl border border-pink-200 px-4 py-3 text-sm focus:ring-2 focus:ring-pink-400 focus:outline-none min-h-[80px] bg-white" />
              <div className="flex items-center gap-3 flex-wrap">
                <label className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white border border-pink-200 text-xs text-pink-700 cursor-pointer hover:bg-pink-50 transition">
                  📎 파일/이미지 첨부
                  <input type="file" accept="image/*,.xlsx,.xls,.csv,.pdf,.txt" className="hidden" multiple
                    onChange={e => { if (e.target.files.length) setAiPromptFiles(prev => [...prev, ...Array.from(e.target.files)]); e.target.value = ""; }} />
                </label>
                {aiPromptFiles.map((f, i) => (
                  <span key={i} className="text-xs text-gray-600 flex items-center gap-1 bg-white px-2 py-0.5 rounded-full border">
                    {f.type?.startsWith("image/") ? "🖼️" : "📄"} {f.name.length > 15 ? f.name.slice(0, 12) + "..." : f.name}
                    <button onClick={() => setAiPromptFiles(prev => prev.filter((_, j) => j !== i))} className="text-red-400 ml-0.5 hover:text-red-600">✕</button>
                  </span>
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              <button onClick={handleAiPrompt} disabled={!!aiFieldLoading}
                className="px-5 py-3 rounded-xl text-sm font-bold bg-gradient-to-r from-pink-500 to-purple-600 text-white hover:from-pink-600 hover:to-purple-700 disabled:opacity-50 shadow-lg transition-all">
                {aiFieldLoading === "ai_prompt" ? "⏳ 생성 중..." : "🐱 AI 생성"}
              </button>
              <div className="text-[9px] text-gray-400 text-center">
                spec_table, product_showcase,<br/>rental_pricing 등 자동 판별
              </div>
            </div>
          </div>
          <div className="flex gap-1.5 flex-wrap">
            <span className="text-[10px] text-pink-600 font-semibold self-center mr-1">빠른 프롬프트:</span>
            {[
              "제품 사양 비교표 만들어줘",
              "프리미엄 쇼케이스 섹션 만들어줘",
              "렌탈 요금표를 깔끔하게 정리해줘",
              "주요 특징 하이라이트 4개 만들어줘",
              "CTA 버튼 섹션 추가해줘",
            ].map(q => (
              <button key={q} onClick={() => { setAiPromptText(q); }}
                className="px-2 py-0.5 rounded-lg text-[10px] font-medium bg-white border border-pink-200 text-pink-700 hover:bg-pink-100 transition">
                {q}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── URL 분석 패널 (에이전트 탐색 + 하네스 엔지니어링) ── */}
      {showUrlAnalyze && (
        <div className="bg-gradient-to-r from-cyan-50 to-blue-50 border-b border-cyan-200 shadow-md shrink-0 z-20 px-4 py-3 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-bold text-gray-800 text-sm">🔗 URL 분석 → 상세페이지 자동 생성</h3>
              <p className="text-[10px] text-gray-500">제품 페이지 URL을 입력하면 에이전트가 HTML 스크래핑 + 이미지 비전 분석으로 상세페이지를 자동 생성합니다</p>
            </div>
            <button onClick={() => setShowUrlAnalyze(false)} className="p-1 rounded-lg hover:bg-gray-100 text-gray-400 text-xs">✕</button>
          </div>
          <div className="flex gap-3 items-end">
            <div className="flex-1 space-y-2">
              <input value={analyzeUrl} onChange={e => setAnalyzeUrl(e.target.value)}
                placeholder="https://ikoffices.com/product/... 또는 제조사 제품 페이지 URL"
                className="w-full rounded-xl border border-cyan-200 px-4 py-3 text-sm focus:ring-2 focus:ring-cyan-400 focus:outline-none bg-white"
                onKeyDown={e => { if (e.key === "Enter") handleUrlAnalyze(); }} />
              <div className="flex items-center gap-2 text-[10px] text-gray-500">
                <span className="font-semibold text-cyan-600">동작 원리:</span>
                <span>① 에이전트가 URL 방문·스크래핑</span>
                <span>→</span>
                <span>② 스펙·가격·옵션 추출</span>
                <span>→</span>
                <span>③ 상세페이지 이미지 비전 분석</span>
                <span>→</span>
                <span>④ AI 하네스 엔지니어링으로 상세페이지 생성</span>
              </div>
            </div>
            <button onClick={handleUrlAnalyze} disabled={urlAnalyzing}
              className="px-6 py-3 rounded-xl text-sm font-bold bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:from-cyan-600 hover:to-blue-700 disabled:opacity-50 shadow-lg transition-all shrink-0">
              {urlAnalyzing ? "⏳ 이미지 분석 + 페이지 생성 중..." : "🔗 URL 분석 생성"}
            </button>
          </div>
          <div className="flex gap-1.5 flex-wrap">
            <span className="text-[10px] text-cyan-600 font-semibold self-center mr-1">모델명 오버라이드:</span>
            <input value={form.model_name || ""} onChange={e => setForm({...form, model_name: e.target.value})}
              placeholder="모델명 수정 (비워두면 자동 추출)"
              className="text-xs border border-cyan-200 rounded-lg px-2 py-1 w-48 focus:ring-1 focus:ring-cyan-400 focus:outline-none" />
            <span className="text-[10px] text-gray-400 self-center ml-2">원본 모델명을 다른 이름으로 바꾸려면 입력하세요</span>
            <button onClick={() => setShowHtmlPaste(!showHtmlPaste)}
              className={`ml-auto px-2 py-1 rounded-lg text-[10px] font-semibold transition-all ${showHtmlPaste ? "bg-amber-100 text-amber-700 ring-1 ring-amber-300" : "bg-gray-100 text-gray-500 hover:bg-amber-50 hover:text-amber-600"}`}>
              📋 HTML 직접 입력
            </button>
          </div>
          {/* ── HTML 붙여넣기 (봇 차단 사이트용) ── */}
          {showHtmlPaste && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-amber-700">📋 HTML 직접 붙여넣기</span>
                <span className="text-[10px] text-amber-600">쿠팡·네이버 등 봇 차단 사이트는 브라우저에서 HTML을 복사해서 붙여넣으세요</span>
              </div>
              <div className="text-[10px] text-amber-600 bg-amber-100 rounded-lg px-3 py-1.5">
                💡 <strong>방법:</strong> 해당 URL을 브라우저에서 열기 → 우클릭 → "페이지 소스 보기" (Ctrl+U / ⌘+U) → 전체 선택 (Ctrl+A) → 복사 → 아래에 붙여넣기
              </div>
              <textarea value={pasteHtml} onChange={e => setPasteHtml(e.target.value)}
                placeholder={"<html>...\n여기에 HTML 소스를 붙여넣으세요..."}
                rows={4}
                className="w-full rounded-lg border border-amber-200 px-3 py-2 text-xs font-mono focus:ring-2 focus:ring-amber-400 focus:outline-none bg-white resize-y" />
              {pasteHtml.trim() && (
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-green-600 font-semibold">✅ HTML {(pasteHtml.length / 1024).toFixed(0)}KB 입력됨</span>
                  <button onClick={() => { setPasteHtml(""); }} className="text-[10px] text-red-500 hover:underline">지우기</button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── AI 통계 패널 ── */}
      {showAiStats && (
        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border-b border-emerald-200 shadow-md shrink-0 z-20 px-4 py-3 space-y-3 max-h-[50vh] overflow-y-auto">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-bold text-gray-800 text-sm">📊 고양이 언어모델 — 사용 통계</h3>
              <p className="text-[10px] text-gray-500">AI 프롬프트 사용량, 성공률, 섹션 타입별 분석</p>
            </div>
            <div className="flex items-center gap-2">
              {[7, 30, 90].map(d => (
                <button key={d} onClick={() => loadAiStats(d)}
                  className={`px-2 py-0.5 rounded text-[10px] font-semibold ${aiStats?.days === d ? "bg-emerald-600 text-white" : "bg-white text-emerald-700 border border-emerald-200 hover:bg-emerald-100"}`}>
                  {d}일
                </button>
              ))}
              <button onClick={() => setShowAiStats(false)} className="p-1 rounded-lg hover:bg-gray-100 text-gray-400 text-xs ml-2">✕</button>
            </div>
          </div>
          {!aiStats ? (
            <div className="text-center py-6 text-gray-400 text-sm">⏳ 통계 로딩 중...</div>
          ) : (
            <>
              {/* 요약 카드 */}
              <div className="grid grid-cols-4 gap-3">
                {[
                  { label: "총 사용", value: aiStats.summary?.total || 0, icon: "🐱", color: "text-pink-600" },
                  { label: "성공률", value: aiStats.summary?.total ? `${Math.round((aiStats.summary.success_count / aiStats.summary.total) * 100)}%` : "0%", icon: "✅", color: "text-emerald-600" },
                  { label: "총 섹션 생성", value: aiStats.summary?.total_sections || 0, icon: "📦", color: "text-blue-600" },
                  { label: "평균 응답시간", value: aiStats.summary?.avg_latency ? `${(aiStats.summary.avg_latency / 1000).toFixed(1)}초` : "-", icon: "⚡", color: "text-amber-600" },
                ].map((c, i) => (
                  <div key={i} className="bg-white rounded-xl p-3 border border-gray-200 text-center">
                    <span className="text-lg block">{c.icon}</span>
                    <div className={`text-lg font-bold ${c.color}`}>{c.value}</div>
                    <div className="text-[10px] text-gray-500">{c.label}</div>
                  </div>
                ))}
              </div>
              {/* 일별 차트 (텍스트 기반) */}
              {aiStats.daily && aiStats.daily.length > 0 && (
                <div className="bg-white rounded-xl p-3 border border-gray-200">
                  <h4 className="text-xs font-bold text-gray-600 mb-2">📅 일별 사용량</h4>
                  <div className="flex items-end gap-1 h-16">
                    {aiStats.daily.map((d, i) => {
                      const maxC = Math.max(...aiStats.daily.map(x => x.count), 1);
                      const h = Math.max(4, (d.count / maxC) * 100);
                      return (
                        <div key={i} className="flex-1 flex flex-col items-center gap-0.5" title={`${d.date}: ${d.count}회`}>
                          <span className="text-[8px] text-gray-400">{d.count}</span>
                          <div className="w-full bg-emerald-400 rounded-t" style={{ height: `${h}%` }} />
                          {i % 5 === 0 && <span className="text-[7px] text-gray-400">{String(d.date).slice(5)}</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                {/* 섹션 타입별 */}
                {aiStats.section_type_stats && aiStats.section_type_stats.length > 0 && (
                  <div className="bg-white rounded-xl p-3 border border-gray-200">
                    <h4 className="text-xs font-bold text-gray-600 mb-2">🧩 섹션 타입별 생성</h4>
                    <div className="space-y-1">
                      {aiStats.section_type_stats.slice(0, 10).map((s, i) => (
                        <div key={i} className="flex items-center justify-between text-xs">
                          <span className="text-gray-700 truncate max-w-[140px]">{s.type}</span>
                          <div className="flex items-center gap-1">
                            <div className="w-16 bg-gray-100 rounded-full h-1.5">
                              <div className="bg-blue-500 rounded-full h-1.5" style={{ width: `${Math.min(100, (s.count / Math.max(1, aiStats.section_type_stats[0]?.count)) * 100)}%` }} />
                            </div>
                            <span className="text-gray-500 font-mono text-[10px] w-6 text-right">{s.count}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* 사용자별 */}
                {aiStats.user_stats && aiStats.user_stats.length > 0 && (
                  <div className="bg-white rounded-xl p-3 border border-gray-200">
                    <h4 className="text-xs font-bold text-gray-600 mb-2">👤 사용자별</h4>
                    <div className="space-y-1">
                      {aiStats.user_stats.map((u, i) => (
                        <div key={i} className="flex items-center justify-between text-xs">
                          <span className="text-gray-700 truncate max-w-[120px]">{u.name || u.email || "익명"}</span>
                          <span className="text-gray-500 font-mono text-[10px]">{u.count}회 ({u.success}성공)</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {/* 최근 로그 */}
              {aiStats.recent && aiStats.recent.length > 0 && (
                <div className="bg-white rounded-xl p-3 border border-gray-200">
                  <h4 className="text-xs font-bold text-gray-600 mb-2">📝 최근 프롬프트 로그</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-[10px]">
                      <thead>
                        <tr className="border-b border-gray-200 text-gray-500">
                          <th className="py-1 text-left">시간</th>
                          <th className="py-1 text-left">사용자</th>
                          <th className="py-1 text-left">프롬프트</th>
                          <th className="py-1 text-center">파일</th>
                          <th className="py-1 text-center">섹션</th>
                          <th className="py-1 text-center">타입</th>
                          <th className="py-1 text-center">시간(초)</th>
                          <th className="py-1 text-center">상태</th>
                        </tr>
                      </thead>
                      <tbody>
                        {aiStats.recent.slice(0, 20).map((r, i) => (
                          <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                            <td className="py-1 text-gray-400">{String(r.created_at).slice(5, 16)}</td>
                            <td className="py-1">{r.user_name || "-"}</td>
                            <td className="py-1 max-w-[200px] truncate" title={r.prompt_text}>{r.prompt_text?.slice(0, 40)}</td>
                            <td className="py-1 text-center">{r.file_name ? "📎" : "-"}</td>
                            <td className="py-1 text-center font-semibold">{r.sections_generated}</td>
                            <td className="py-1 text-center text-gray-500">{r.section_types?.slice(0, 25)}</td>
                            <td className="py-1 text-center">{r.latency_ms ? (r.latency_ms / 1000).toFixed(1) : "-"}</td>
                            <td className="py-1 text-center">{r.success ? "✅" : "❌"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {aiStats.summary?.total === 0 && (
                <div className="text-center py-6 text-gray-400 text-sm">아직 AI 프롬프트 사용 기록이 없습니다. 🐱 AI 프롬프트를 사용해보세요!</div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Main area: Side-by-side (Editor left + Live Preview right) ── */}
      <div className="flex-1 min-h-0 flex relative">
        {/* ── Left: Editor Panel (scrollable) ── */}
        <div className={`${showLivePreview ? "w-full lg:w-[420px] lg:min-w-[360px]" : "flex-1 max-w-3xl mx-auto"} bg-white border-r border-gray-200 overflow-y-auto shrink-0 transition-all`}>
          <div className="p-4 space-y-5">
              {/* ─── 기본 정보 ─── */}
              <div className="space-y-3">
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">📋 기본 정보 <div className="flex-1 border-t border-gray-200" /></h4>
                <div className="space-y-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1"><label className="text-xs font-semibold text-gray-500">타이틀 *</label><AiBtn field="title" /></div>
                    <input value={form.title || ""} onChange={e => setForm({...form, title: e.target.value})} className={inp} placeholder="예: Canon imageRUNNER ADVANCE C5560i" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1"><label className="text-xs font-semibold text-gray-500">부제목</label><AiBtn field="subtitle" /></div>
                    <input value={form.subtitle || ""} onChange={e => setForm({...form, subtitle: e.target.value})} className={inp} placeholder="예: 오피스 생산성의 새로운 기준" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={label}>모델명</label>
                      <input value={form.model_name || ""} onChange={e => setForm({...form, model_name: e.target.value})} className={inp} placeholder="C5560i" />
                    </div>
                    <div>
                      <label className={label}>브랜드</label>
                      <BrandSelect value={form.brand || ""} onChange={brand => setForm({...form, brand})} className={inp} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={label}>카테고리</label>
                      <input value={form.category || ""} onChange={e => setForm({...form, category: e.target.value})} className={inp} />
                    </div>
                    <div>
                      <label className={label}>스토어 분류</label>
                      <select value={form.store_category || ""} onChange={e => setForm({...form, store_category: e.target.value, sub_category: ""})} className={inp}>
                        <option value="">자동 분류</option>
                        {(STORE_CATS_MAP[templateMode] || STORE_CATS).map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={label}>판매유형</label>
                      <select value={form.sale_type || "sale"} onChange={e => setForm({...form, sale_type: e.target.value})} className={inp}>
                        <option value="sale">판매</option><option value="rental">렌탈</option><option value="both">판매+렌탈</option>
                      </select>
                    </div>
                    <div>
                      <label className={label}>지역</label>
                      <DistrictSelect value={form.district || ""} onChange={e => setForm({...form, district: e.target.value})} className={inp} placeholder="📍 지역" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={label}>상태</label>
                      <select value={form.status || "draft"} onChange={e => setForm({...form, status: e.target.value})} className={inp}>
                        <option value="draft">초안</option><option value="published">발행</option><option value="archived">보관</option>
                      </select>
                    </div>
                    {form.store_category !== "토너소모품" && (
                    <div>
                      <label className={label}>칼라/흑백</label>
                      <select value={form.color_type || ""} onChange={e => setForm({...form, color_type: e.target.value || null})} className={inp}>
                        <option value="">미지정</option>
                        <option value="color">칼라</option>
                        <option value="mono">흑백</option>
                      </select>
                    </div>
                    )}
                  </div>
                  {/* Theme */}
                  <div>
                    <label className={label}>페이지 테마</label>
                    <div className="flex gap-1 flex-wrap">
                      {Object.values(PAGE_THEMES).map(t => (
                        <button key={t.key} onClick={() => setForm(prev => ({...prev, theme: t.key}))}
                          className={`px-2 py-1 rounded-lg text-[10px] font-semibold border transition ${(form.theme || "minimal") === t.key ? "border-indigo-500 bg-indigo-100 text-indigo-700" : "border-gray-200 text-gray-500 hover:bg-gray-50"}`}>
                          {t.icon} {t.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  {/* Prices */}
                  <div className="grid grid-cols-3 gap-2 p-3 bg-red-50/50 rounded-xl">
                    <div>
                      <label className="text-[10px] font-semibold text-gray-500">원가</label>
                      <input type="number" value={form.cost_price ?? ""} onChange={e => setForm({...form, cost_price: e.target.value === "" ? "" : e.target.value === "" ? "" : parseInt(e.target.value)||0})} className={inp} />
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold text-gray-500">판매가</label>
                      <input type="number" value={form.price ?? ""} onChange={e => setForm({...form, price: e.target.value === "" ? "" : e.target.value === "" ? "" : parseInt(e.target.value)||0})} className={inp} />
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold text-gray-500">렌탈 월납</label>
                      <input type="number" value={form.rental_price ?? ""} onChange={e => setForm({...form, rental_price: e.target.value === "" ? "" : e.target.value === "" ? "" : parseInt(e.target.value)||0})} className={inp} />
                    </div>
                  </div>
                </div>
              </div>

              {/* ─── 호환 제품 매핑 ─── */}
              {!isNew && pageId && (
                <CompatibilityPanel pageId={pageId} />
              )}

              {/* ─── Hero 섹션 ─── */}
              <div className="space-y-3">
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">🎨 Hero 섹션 <div className="flex-1 border-t border-gray-200" /></h4>
                <div id="section-hero" className="space-y-3">
                  <HeroImageUploader imageUrl={form.hero_image || ""} onUrlChange={(url) => { setForm(prev => ({...prev, hero_image: url})); setHeroVisual({}); }} />
                  <div className="space-y-2">
                    <div>
                      <label className="text-[10px] text-gray-500">대형 제목</label>
                      <input value={heroVisual.headline || ""} onChange={e => setHeroVisual({ headline: e.target.value })} className={inp} placeholder="효율의 정석" />
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-500">설명 문구</label>
                      <textarea value={heroVisual.description || ""} onChange={e => setHeroVisual({ description: e.target.value })} rows={2} className={`${inp} resize-none`} />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[10px] text-gray-500">CTA 텍스트</label>
                        <input value={heroVisual.cta_text || ""} onChange={e => setHeroVisual({ cta_text: e.target.value })} className={inp} placeholder="문의하기" />
                      </div>
                      <div>
                        <label className="text-[10px] text-gray-500">CTA 링크</label>
                        <input value={heroVisual.cta_link || ""} onChange={e => setHeroVisual({ cta_link: e.target.value })} className={inp} placeholder="/consultation" />
                      </div>
                    </div>
                    <div className="flex gap-4 mt-1">
                      <label className="flex items-center gap-1.5 text-[10px] text-gray-500 cursor-pointer">
                        <input type="checkbox" checked={!!heroVisual.hide_cta} onChange={e => setHeroVisual({ hide_cta: e.target.checked })}
                          className="w-3.5 h-3.5 rounded border-gray-300 text-blue-500 focus:ring-0" />
                        CTA 버튼 숨기기
                      </label>
                      <label className="flex items-center gap-1.5 text-[10px] text-gray-500 cursor-pointer">
                        <input type="checkbox" checked={!!heroVisual.hide_price} onChange={e => setHeroVisual({ hide_price: e.target.checked })}
                          className="w-3.5 h-3.5 rounded border-gray-300 text-blue-500 focus:ring-0" />
                        가격 숨기기
                      </label>
                    </div>
                  </div>
                  <button onClick={() => setShowHeroAdvanced(!showHeroAdvanced)}
                    className="text-[10px] text-gray-400 hover:text-gray-600">{showHeroAdvanced ? "▼" : "▶"} HTML 직접 편집</button>
                  {showHeroAdvanced && <MarkdownEditor value={form.hero_html || ""} onChange={(v) => setForm({...form, hero_html: v})} contentFormat="html" onFormatChange={() => {}} />}
                </div>
              </div>

              {/* ─── 콘텐츠 섹션 관리 ─── */}
              <div className="space-y-3">
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">📄 콘텐츠 ({sections.length}) <div className="flex-1 border-t border-gray-200" /></h4>
                <div className="flex gap-1 flex-wrap">
                  {Object.entries(SECTION_LABELS).map(([type, lbl]) => (
                    <button key={type} onClick={() => addSection(type)}
                      className="px-2 py-1 rounded-lg text-[10px] font-semibold bg-gray-100 text-gray-600 hover:bg-blue-50 hover:text-blue-600 transition">+ {lbl}</button>
                  ))}
                </div>
                <div className="flex gap-1 flex-wrap">
                  <span className="text-[10px] text-purple-500 font-semibold self-center mr-1">✨ AI:</span>
                  {["feature_highlight", "spec_table", "rental_plans", "product_card", "sale_pricing", "rental_pricing", "toner_spec", "product_options", "cta"].map(type => (
                    <button key={type} onClick={() => handleAiSection(type)} disabled={!!aiFieldLoading}
                      className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-purple-50 text-purple-600 hover:bg-purple-100 disabled:opacity-40">
                      {aiFieldLoading === `section_${type}` ? "⏳" : "✨"} {SECTION_LABELS[type]}</button>
                  ))}
                </div>
                <div className="flex gap-1 flex-wrap">
                  <span className="text-[10px] text-green-600 font-semibold self-center mr-1">🍽️ 식품:</span>
                  {Object.entries(FOOD_PAGE_PRESETS).map(([key, preset]) => (
                    <button key={key} onClick={() => applyFoodPreset(key)}
                      title={preset.description}
                      className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-green-50 text-green-700 hover:bg-green-100 border border-green-200">
                      {preset.label}</button>
                  ))}
                </div>
                {/* Section list (compact) */}
                {sections.map((sec, i) => (
                  <div key={i} id={`section-editor-${i}`} className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
                    <div className="flex items-center justify-between px-3 py-2 cursor-pointer" onClick={() => toggleExpand(i)}>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-mono bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">{SECTION_LABELS[sec.type] || sec.type}</span>
                        <span className="text-xs font-medium text-gray-700 truncate max-w-[160px]">{sec.title || `섹션 ${i+1}`}</span>
                      </div>
                      <div className="flex items-center gap-0.5" onClick={e => e.stopPropagation()}>
                        <button onClick={() => setEditingSectionDrawerIdx(i)} title="상세 편집" className="w-5 h-5 rounded text-blue-500 hover:bg-blue-50 text-[10px] flex items-center justify-center">✏️</button>
                        <button onClick={() => moveSection(i, -1)} disabled={i === 0} className="w-5 h-5 rounded text-gray-400 hover:bg-gray-200 disabled:opacity-30 text-[10px]">↑</button>
                        <button onClick={() => moveSection(i, 1)} disabled={i === sections.length - 1} className="w-5 h-5 rounded text-gray-400 hover:bg-gray-200 disabled:opacity-30 text-[10px]">↓</button>
                        <button onClick={() => removeSection(i)} className="w-5 h-5 rounded text-red-400 hover:bg-red-50 text-[10px]">✕</button>
                      </div>
                    </div>
                    {!collapsedSections.has(i) && (
                      <div className="px-3 pb-3 border-t border-gray-200">
                        <SectionEditor sec={sec} index={i} updateSection={updateSection} form={form}
                          setToast={setToast}
                          onOpenCanvas={() => { setEditingCanvasIdx(i); setShowDesignCanvas(true); }}
                          onSyncRental={syncRentalToOptions}
                          onSyncOptionsToRental={syncOptionsToRental}
                          onSyncPlans={syncPlansToShowcase}
                          onGenerateRental={generateRentalFromOptions}
                          onGenerateOptionsFromRental={generateOptionsFromRental} />
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* ─── 제품 목록 ─── */}
              <div className="space-y-3">
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">📦 제품 ({items.length}) <div className="flex-1 border-t border-gray-200" />
                  <button onClick={addItem} className="px-2 py-1 rounded-lg text-[10px] font-semibold bg-blue-100 text-blue-700 hover:bg-blue-200">+ 추가</button>
                </h4>
                {items.map((it, i) => (
                  <div key={i} className="bg-gray-50 rounded-xl border border-gray-200 p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-gray-700">#{i+1} {it.product_name || "새 제품"}</span>
                      <button onClick={() => removeItem(i)} className="text-red-400 hover:text-red-600 text-[10px]">✕</button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <input value={it.product_name || ""} onChange={e => updateItem(i, { product_name: e.target.value })} className={inp} placeholder="제품명" />
                      <input value={it.model || ""} onChange={e => updateItem(i, { model: e.target.value })} className={inp} placeholder="모델명" />
                      <input type="number" value={it.price ?? ""} onChange={e => updateItem(i, { price: e.target.value === "" ? "" : parseInt(e.target.value)||0 })} className={inp} placeholder="판매가" />
                      <input type="number" value={it.rental_price ?? ""} onChange={e => updateItem(i, { rental_price: e.target.value === "" ? "" : parseInt(e.target.value)||0 })} className={inp} placeholder="렌탈가" />
                    </div>
                    <ItemImageUploader imageUrl={it.image_url || ""} onUrlChange={(url) => updateItem(i, { image_url: url })} />
                  </div>
                ))}
              </div>

              {/* ─── SEO ─── */}
              <div className="space-y-3">
                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">🔍 SEO <div className="flex-1 border-t border-gray-200" /></h4>
                <div>
                  <div className="flex items-center gap-2 mb-1"><label className="text-xs font-semibold text-gray-500">SEO 제목</label><AiBtn field="seo_title" /></div>
                  <input value={form.seo_title || ""} onChange={e => setForm({...form, seo_title: e.target.value})} className={inp} placeholder="검색 결과 제목" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1"><label className="text-xs font-semibold text-gray-500">SEO 설명</label><AiBtn field="seo_description" /></div>
                  <textarea value={form.seo_description || ""} onChange={e => setForm({...form, seo_description: e.target.value})} rows={2} className={`${inp} resize-none`} placeholder="검색 결과 설명문" />
                </div>
              </div>
          </div>
        </div>

        {/* ── Right: Live Preview (togglable) ── */}
        {showLivePreview && (
          <div className="flex-1 min-w-0 bg-gray-50 overflow-y-auto">
            <div className="max-w-4xl mx-auto">
              <PreviewRenderer
                form={form}
                onUpdateSections={(arr) => setForm(prev => ({ ...prev, sections_json: arr }))}
                onHeroImageDelete={() => setForm(f => ({ ...f, hero_image: '' }))}
                onSectionClick={(target) => {
                  if (target === 'hero') {
                    const heroEl = document.getElementById('section-hero');
                    if (heroEl) heroEl.scrollIntoView({ behavior: 'smooth' });
                  } else if (typeof target === 'number') {
                    const secEl = document.getElementById(`section-editor-${target}`);
                    if (secEl) secEl.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* ── Section editing drawer ── */}
      {editingSectionDrawerIdx !== null && (form.sections_json || [])[editingSectionDrawerIdx] && (
        <div className="fixed inset-0 z-[9998] flex">
          <div className="flex-1 bg-black/40" onClick={() => setEditingSectionDrawerIdx(null)} />
          <div className="w-full max-w-[480px] bg-white shadow-2xl overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between z-10">
              <div>
                <h3 className="font-bold text-gray-800 text-sm">섹션 상세 편집</h3>
                <p className="text-[10px] text-gray-500">#{editingSectionDrawerIdx + 1} · {SECTION_LABELS[(form.sections_json || [])[editingSectionDrawerIdx]?.type] || (form.sections_json || [])[editingSectionDrawerIdx]?.type}</p>
              </div>
              <button onClick={() => setEditingSectionDrawerIdx(null)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">✕</button>
            </div>
            <div className="p-4">
              <SectionEditorInline
                sec={(form.sections_json || [])[editingSectionDrawerIdx]}
                idx={editingSectionDrawerIdx}
                onUpdate={(idx, patch) => {
                  setForm(prev => {
                    const arr = [...(prev.sections_json || [])];
                    arr[idx] = { ...arr[idx], ...patch };
                    return { ...prev, sections_json: arr };
                  });
                }}
                inp={inp}
                card={card}
                onSyncRental={syncRentalToOptions}
                onSyncOptionsToRental={syncOptionsToRental}
                onSyncPlans={syncPlansToShowcase}
                onGenerateRental={generateRentalFromOptions}
                onGenerateOptionsFromRental={generateOptionsFromRental}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Presentation Mode ── */}
      {presenting && (
        <div className="fixed inset-0 z-[9999] bg-white overflow-y-auto" style={{ scrollBehavior: "smooth" }}>
          <div className="fixed top-4 right-4 z-[10000] flex gap-2 opacity-0 hover:opacity-100 transition-opacity duration-300">
            <button onClick={() => {
              const el = document.documentElement;
              if (el.requestFullscreen) el.requestFullscreen();
              else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
            }}
              className="px-3 py-2 bg-black/60 text-white rounded-xl text-xs font-medium backdrop-blur-sm hover:bg-black/80 transition">
              🖥️ 전체화면
            </button>
            <button onClick={() => { if (document.fullscreenElement) document.exitFullscreen(); setPresenting(false); }}
              className="px-3 py-2 bg-red-600/80 text-white rounded-xl text-xs font-medium backdrop-blur-sm hover:bg-red-700 transition">
              ✕ 닫기
            </button>
          </div>
          <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[10000] opacity-0 hover:opacity-100 transition-opacity duration-300">
            <span className="text-xs text-gray-400 bg-black/40 backdrop-blur-sm px-3 py-1.5 rounded-full text-white">
              ESC 또는 마우스를 아래로 → 닫기
            </span>
          </div>
          <PreviewRenderer form={form} onUpdateSections={(arr) => setForm(prev => ({ ...prev, sections_json: arr }))} onHeroImageDelete={() => setForm(f => ({ ...f, hero_image: '' }))} onSectionClick={() => {}} />
        </div>
      )}
      {presenting && <PresentationKeyHandler onExit={() => { if (document.fullscreenElement) document.exitFullscreen(); setPresenting(false); }} />}

      {/* ── Design Canvas Modal ── */}
      {showDesignCanvas && (
        <div className="fixed inset-0 z-[9999] bg-white">
        <React.Suspense fallback={<div className="flex items-center justify-center h-full"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>}>
        <DesignCanvas
          onClose={() => { setShowDesignCanvas(false); setEditingCanvasIdx(null); }}
          initialJSON={editingCanvasIdx !== null ? (form.sections_json || [])[editingCanvasIdx]?.canvas_json : canvasJSON}
          existingSections={(form.sections_json || []).filter((_, i) => i !== editingCanvasIdx)}
          quickTemplates={QUICK_TEMPLATES}
          modeLabels={MODE_LABELS}
          sectionTemplates={SECTION_TEMPLATES}
          onApplyPreset={(tmpl) => {
            const secs = (tmpl.preset_sections || []).map(
              type => JSON.parse(JSON.stringify(SECTION_TEMPLATES[type] || SECTION_TEMPLATES.text))
            );
            setForm(prev => ({
              ...prev,
              category: tmpl.category,
              sale_type: tmpl.sale_type,
              store_category: tmpl.store_category || "",
              theme: suggestTheme(tmpl.category, tmpl.mode),
              sections_json: [...(prev.sections_json || []), ...secs],
            }));
            if (setToast) setToast(`${tmpl.label} 프리셋 적용 (${secs.length}개 섹션)`);
          }}
          onExportSections={(newSecs) => {
            setForm(prev => ({
              ...prev,
              sections_json: [...(prev.sections_json || []), ...newSecs]
            }));
            if (setToast) setToast(`${newSecs.length}개 섹션 추가`);
          }}
          onSave={({ json, dataUrl }) => {
            setCanvasJSON(json);
            if (editingCanvasIdx !== null) {
              setForm(prev => {
                const arr = [...(prev.sections_json || [])];
                arr[editingCanvasIdx] = { ...arr[editingCanvasIdx], canvas_json: json, dataUrl };
                return { ...prev, sections_json: arr };
              });
              if (setToast) setToast("디자인이 섹션에 저장되었습니다");
            } else {
              setForm(prev => ({
                ...prev,
                sections_json: [...(prev.sections_json || []), { type: "design_canvas", title: "디자인 캔버스", canvas_json: json, dataUrl }]
              }));
              if (setToast) setToast("디자인이 섹션으로 삽입되었습니다");
            }
            setShowDesignCanvas(false);
            setEditingCanvasIdx(null);
          }}
        />
        </React.Suspense>
        </div>
      )}
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════
   Section Editor — renders fields for each section type
   ══════════════════════════════════════════════════════════════ */
function SectionEditor({ sec, index: i, updateSection, form, onOpenCanvas, onSyncRental, onSyncOptionsToRental, onSyncPlans, onGenerateRental, onGenerateOptionsFromRental, setToast }) {
  const [dbBusy, setDbBusy] = useState(false);
  const [presets, setPresets] = useState([]);
  const [showPresetPanel, setShowPresetPanel] = useState(false);
  const [presetName, setPresetName] = useState("");
  const [editingPreset, setEditingPreset] = useState(null);
  const [presetLoading, setPresetLoading] = useState(false);
  // DB 불러오기 멀티 선택
  const [showDbImport, setShowDbImport] = useState(false);
  const [dbPlansList, setDbPlansList] = useState([]);
  const [selectedPlanIds, setSelectedPlanIds] = useState(new Set());
  const [dbImportSearch, setDbImportSearch] = useState("");
  // 섹션 프리셋 (heading_text, cta 등 범용)
  const [secPresets, setSecPresets] = useState([]);
  const [showSecPresetPanel, setShowSecPresetPanel] = useState(false);
  const [secPresetName, setSecPresetName] = useState("");
  const [editingSecPreset, setEditingSecPreset] = useState(null);
  const [secPresetLoading, setSecPresetLoading] = useState(false);
  switch (sec.type) {
    case "design_canvas":
      return (
        <>
          <input value={sec.title || ""} onChange={e => updateSection(i, { title: e.target.value })}
            placeholder="섹션 제목 (선택사항)" className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none" />
          {sec.dataUrl ? (
            <div className="relative group">
              <img src={sec.dataUrl} alt="디자인 미리보기" className="w-full rounded-xl border border-gray-200 max-h-80 object-contain bg-gray-50" />
              <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition rounded-xl">
                <button onClick={onOpenCanvas}
                  className="opacity-0 group-hover:opacity-100 transition px-6 py-3 bg-white/95 rounded-xl shadow-lg text-sm font-bold text-violet-700 hover:bg-violet-50">
                  🎨 디자인 편집하기
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
              <span className="text-4xl mb-3">🎨</span>
              <p className="text-sm text-gray-500 mb-3">디자인 캔버스가 비어 있습니다</p>
              <button onClick={onOpenCanvas}
                className="px-5 py-2.5 bg-gradient-to-r from-pink-500 to-violet-500 text-white rounded-xl text-sm font-semibold hover:from-pink-600 hover:to-violet-600 shadow">
                캔버스 열기
              </button>
            </div>
          )}
        </>
      );
    case "rich_html":
      return (
        <>
          <input value={sec.title || ""} onChange={e => updateSection(i, { title: e.target.value })}
            placeholder="섹션 제목 (선택사항)" className={inp} />
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs text-blue-700 flex items-center gap-2">
            <span>✏️</span>
            <span>자유 편집 모드 — 텍스트, 이미지, 표, 목록 등 모든 내용을 자유롭게 편집할 수 있습니다. 이미지를 드래그하여 삽입하세요.</span>
          </div>
          <MarkdownEditor
            value={sec.content || ""} onChange={(v) => updateSection(i, { content: v })}
            contentFormat="html" onFormatChange={() => {}} />
        </>
      );

    case "text":
      return (
        <>
          <input value={sec.title || ""} onChange={e => updateSection(i, { title: e.target.value })}
            placeholder="제목" className={inp} />
          <MarkdownEditor
            value={sec.content || ""} onChange={(v) => updateSection(i, { content: v })}
            contentFormat="html" onFormatChange={() => {}} />
        </>
      );

    case "product_details":
    case "feature_highlight":
      return (
        <>
          <input value={sec.title || ""} onChange={e => updateSection(i, { title: e.target.value })}
            placeholder="섹션 제목" className={inp} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {(sec.items || []).map((item, j) => (
              <div key={j} className="border border-gray-200 rounded-lg p-3 space-y-2">
                <div className="flex gap-2">
                  <input value={item.icon || ""} onChange={e => {
                    const items = [...(sec.items||[])]; items[j] = {...items[j], icon: e.target.value}; updateSection(i, { items });
                  }} placeholder="아이콘" className="w-16 rounded border px-2 py-1 text-center text-sm" />
                  <input value={item.title || ""} onChange={e => {
                    const items = [...(sec.items||[])]; items[j] = {...items[j], title: e.target.value}; updateSection(i, { items });
                  }} placeholder="제목" className="flex-1 rounded border px-2 py-1 text-sm" />
                  <button onClick={() => { updateSection(i, { items: (sec.items||[]).filter((_,k) => k!==j) }); }}
                    className="text-red-400 hover:text-red-600 text-sm">✕</button>
                </div>
                <input value={item.highlight_number || ""} onChange={e => {
                  const items = [...(sec.items||[])]; items[j] = {...items[j], highlight_number: e.target.value}; updateSection(i, { items });
                }} placeholder="강조 숫자 (예: 50ppm)" className="w-full rounded border px-2 py-1 text-sm" />
                <input value={item.description || ""} onChange={e => {
                  const items = [...(sec.items||[])]; items[j] = {...items[j], description: e.target.value}; updateSection(i, { items });
                }} placeholder="설명" className="w-full rounded border px-2 py-1 text-sm" />
              </div>
            ))}
          </div>
          <button onClick={() => updateSection(i, { items: [...(sec.items||[]), { icon: "⚡", title: "", description: "", highlight_number: "" }] })}
            className="text-[12px] text-blue-600 font-medium hover:underline">+ 항목 추가</button>
        </>
      );

    case "spec_table":
      return (
        <SpecTableForm sec={sec} onPatch={(patch) => updateSection(i, patch)} />
      );

    case "product_card":
      return (
        <>
          <input value={sec.product_name || ""} onChange={e => updateSection(i, { product_name: e.target.value })}
            placeholder="제품명" className={inp} />
          <input value={sec.tagline || ""} onChange={e => updateSection(i, { tagline: e.target.value })}
            placeholder="태그라인 (한 줄 요약)" className={inp} />
          <textarea value={sec.description || ""} onChange={e => updateSection(i, { description: e.target.value })}
            placeholder="설명" rows={2} className={`${inp} resize-none`} />
          <ItemImageUploader
            imageUrl={sec.image_url || ""}
            onUrlChange={(url) => updateSection(i, { image_url: url })}
          />
          {/* Features list */}
          <div>
            <label className={label}>주요 기능 목록</label>
            {(sec.features || []).map((f, j) => (
              <div key={j} className="flex gap-2 mb-1">
                <input value={f} onChange={e => {
                  const feats = [...(sec.features||[])]; feats[j] = e.target.value; updateSection(i, { features: feats });
                }} className="flex-1 rounded border px-2 py-1 text-sm" placeholder="기능 설명" />
                <button onClick={() => updateSection(i, { features: (sec.features||[]).filter((_,k) => k!==j) })}
                  className="text-red-400 text-sm">✕</button>
              </div>
            ))}
            <button onClick={() => updateSection(i, { features: [...(sec.features||[]), ""] })}
              className="text-[12px] text-blue-600 font-medium hover:underline">+ 기능 추가</button>
          </div>
          {/* Highlight spec */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={label}>강조 수치</label>
              <input value={sec.highlight_spec?.value || ""} onChange={e => updateSection(i, { highlight_spec: { ...sec.highlight_spec, value: e.target.value } })}
                className="w-full rounded border px-2 py-1 text-sm" placeholder="50ppm" />
            </div>
            <div>
              <label className={label}>수치 라벨</label>
              <input value={sec.highlight_spec?.label || ""} onChange={e => updateSection(i, { highlight_spec: { ...sec.highlight_spec, label: e.target.value } })}
                className="w-full rounded border px-2 py-1 text-sm" placeholder="분당 출력 속도" />
            </div>
          </div>
        </>
      );

    case "rental_plans": {
      const OPTION_PRESETS = ["팩스", "추가트레이", "자동양면", "후처리기", "무선랜", "급지대", "스테이플", "대용량급지", "팩스별도", "팩스포함"];
      const updatePlan = (j, patch) => {
        const plans = [...(sec.plans||[])]; plans[j] = {...plans[j], ...patch}; updateSection(i, { plans });
      };
      const getOptionTags = (optStr) => (optStr || "").split(/[,，]\ */).map(s => s.trim()).filter(Boolean);
      const setOptionTags = (j, tags) => updatePlan(j, { options: tags.join(", ") });
      const addOption = (j, tag) => { const tags = getOptionTags((sec.plans||[])[j]?.options); if (!tags.includes(tag)) setOptionTags(j, [...tags, tag]); };
      const removeOption = (j, tag) => { const tags = getOptionTags((sec.plans||[])[j]?.options); setOptionTags(j, tags.filter(t => t !== tag)); };

      /* DB 약정 불러오기 — 멀티 선택 모달 열기 */
      const importFromDB = async () => {
        if (dbBusy) return;
        setDbBusy(true);
        try {
          const modelName = form?.model_name || form?.title || "";
          const { data } = await api.get("/api/admin/rental-plans");
          if (!data || data.length === 0) { setToast("등록된 약정이 없습니다."); return; }
          setDbPlansList(data);
          // 모델명 매칭되는 것 자동 선택
          if (modelName) {
            const matched = new Set();
            data.forEach(p => {
              const pname = (p.product_name || p.model_name || "").toLowerCase();
              if (pname.includes(modelName.toLowerCase())) matched.add(p.id);
            });
            setSelectedPlanIds(matched);
            setDbImportSearch(modelName);
          } else {
            setSelectedPlanIds(new Set());
            setDbImportSearch("");
          }
          setShowDbImport(true);
        } catch (e) { setToast("약정 불러오기 실패: " + (e.response?.data?.error || e.message)); }
        finally { setDbBusy(false); }
      };

      /* 선택한 약정 가져오기 */
      const applySelectedPlans = (mode) => {
        const selected = dbPlansList.filter(p => selectedPlanIds.has(p.id));
        if (selected.length === 0) { setToast("선택된 약정이 없습니다."); return; }
        const plans = selected.map((p, idx) => ({
          plan_no: String(idx + 1).padStart(2, "0"),
          plan_name: p.plan_name || "기본",
          model_label: p.product_name || p.model_name || "",
          monthly_price: p.base_price || 0,
          deposit: p.deposit || 200000,
          included_pages: `흑백 ${(p.bw_pages || 0).toLocaleString()}매`,
          color_pages: `컬러 ${(p.color_pages || 0).toLocaleString()}매`,
          contract_period: `${Math.round((p.contract_months || 36) / 12)}년 (${p.contract_months || 36}개월)`,
          options: p.fax_option || "",
          extra_bw_price: p.extra_bw_price ? Math.round(p.extra_bw_price / 1000 * 1) : 15,
          extra_color_price: p.extra_color_price ? Math.round(p.extra_color_price / 100 * 1) : 80,
          note: p.note || "",
          _db_id: p.id,
        }));
        if (mode === "replace") {
          updateSection(i, { plans });
        } else {
          // append 모드: 기존 플랜에 추가, plan_no 재정렬
          const existing = [...(sec.plans || [])];
          const merged = [...existing, ...plans];
          merged.forEach((p, idx) => { p.plan_no = String(idx + 1).padStart(2, "0"); });
          updateSection(i, { plans: merged });
        }
        setShowDbImport(false);
        setToast(`✅ ${plans.length}개 약정을 ${mode === "replace" ? "불러왔습니다" : "추가했습니다"}.`);
      };

      /* 현재 요금제 → DB 약정으로 저장 */
      const saveToDB = async () => {
        if (dbBusy) return;
        const plans = sec.plans || [];
        if (plans.length === 0) { setToast("저장할 요금제가 없습니다."); return; }
        setDbBusy(true);
        try {
          const payload = {
            model_name: form?.model_name || plans[0]?.model_label || "",
            brand: form?.brand || "",
            category: form?.category || "",
            sale_type: form?.sale_type || "rental",
            plans: plans.map(p => ({
              _db_id: p._db_id || null,
              plan_name: p.plan_name || "기본",
              model_label: p.model_label || "",
              monthly_price: parseInt(p.monthly_price) || 0,
              deposit: parseInt(p.deposit) || 200000,
              included_pages: p.included_pages || "",
              color_pages: p.color_pages || "",
              contract_period: p.contract_period || "3년",
              options: p.options || "",
              extra_bw_price: parseInt(p.extra_bw_price) || 15,
              extra_color_price: parseInt(p.extra_color_price) || 80,
              note: p.note || "",
            }))
          };
          const { data } = await api.post("/api/admin/rental-plans/save-from-section", payload);
          if (data.plan_ids) {
            const updated = plans.map((p, idx) => ({ ...p, _db_id: data.plan_ids[idx] || p._db_id }));
            updateSection(i, { plans: updated });
          }
          setToast(`✅ ${plans.length}개 요금제가 DB에 저장되었습니다.`);
        } catch (e) { setToast("DB 저장 실패: " + (e.response?.data?.error || e.message)); }
        finally { setDbBusy(false); }
      };

      const movePlan = (from, to) => {
        const plans = [...(sec.plans||[])]; const [item] = plans.splice(from, 1); plans.splice(to, 0, item);
        plans.forEach((p, idx) => { p.plan_no = String(idx+1).padStart(2,"0"); });
        updateSection(i, { plans });
      };
      const clonePlan = (j) => {
        const plans = [...(sec.plans||[])]; const clone = { ...plans[j], _db_id: null, plan_no: String(plans.length+1).padStart(2,"0") };
        updateSection(i, { plans: [...plans, clone] });
      };

      /* ── 프리셋 CRUD ── */
      const loadPresets = async () => {
        setPresetLoading(true);
        try {
          const { data } = await api.get("/api/admin/rental-plan-presets");
          setPresets(data || []);
        } catch (e) { setToast("프리셋 목록 로드 실패"); }
        finally { setPresetLoading(false); }
      };
      const openPresetPanel = () => { setShowPresetPanel(true); loadPresets(); };
      const saveAsPreset = async () => {
        const name = presetName.trim();
        if (!name) { setToast("프리셋 이름을 입력하세요"); return; }
        const plans = sec.plans || [];
        if (plans.length === 0) { setToast("저장할 요금제가 없습니다"); return; }
        setPresetLoading(true);
        try {
          await api.post("/api/admin/rental-plan-presets", { name, plans });
          setToast(`✅ 프리셋 "${name}" 저장 완료`);
          setPresetName("");
          loadPresets();
        } catch (e) { setToast("프리셋 저장 실패: " + (e.response?.data?.error || e.message)); }
        finally { setPresetLoading(false); }
      };
      const loadPreset = (preset) => {
        const plans = (preset.plans_json || []).map((p, idx) => ({ ...p, _db_id: null, plan_no: String(idx + 1).padStart(2, "0") }));
        if (plans.length === 0) { setToast("프리셋에 요금제가 없습니다"); return; }
        updateSection(i, { plans });
        setToast(`✅ 프리셋 "${preset.name}" 적용 완료 (${plans.length}개 요금제)`);
      };
      const updatePreset = async (preset) => {
        const plans = sec.plans || [];
        if (plans.length === 0) { setToast("저장할 요금제가 없습니다"); return; }
        if (!window.confirm(`프리셋 "${preset.name}"을(를) 현재 요금제로 덮어쓰시겠습니까?`)) return;
        setPresetLoading(true);
        try {
          await api.put(`/api/admin/rental-plan-presets/${preset.id}`, { plans });
          setToast(`✅ 프리셋 "${preset.name}" 업데이트 완료`);
          loadPresets();
        } catch (e) { setToast("프리셋 업데이트 실패"); }
        finally { setPresetLoading(false); }
      };
      const renamePreset = async (preset, newName) => {
        if (!newName?.trim()) return;
        setPresetLoading(true);
        try {
          await api.put(`/api/admin/rental-plan-presets/${preset.id}`, { name: newName.trim() });
          setToast(`✅ 이름 변경 완료`);
          setEditingPreset(null);
          loadPresets();
        } catch (e) { setToast("이름 변경 실패"); }
        finally { setPresetLoading(false); }
      };
      const deletePreset = async (preset) => {
        if (!window.confirm(`프리셋 "${preset.name}"을(를) 삭제하시겠습니까?`)) return;
        setPresetLoading(true);
        try {
          await api.delete(`/api/admin/rental-plan-presets/${preset.id}`);
          setToast(`🗑️ 프리셋 "${preset.name}" 삭제 완료`);
          loadPresets();
        } catch (e) { setToast("프리셋 삭제 실패"); }
        finally { setPresetLoading(false); }
      };

      return (
        <>
          <input value={sec.title || ""} onChange={e => updateSection(i, { title: e.target.value })}
            placeholder="섹션 제목 (예: 렌탈 요금제)" className={inp} />

          {/* 약정 연동 버튼 */}
          <div className="flex flex-wrap items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-xl p-3">
            <span className="text-xs text-indigo-700 font-medium flex-1 min-w-[100px]">💾 약정 DB 연동</span>
            <div className="flex flex-wrap gap-1.5">
              <button onClick={() => onSyncPlans?.(i)}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-purple-600 text-white hover:bg-purple-700 transition">
                🔗 옵션 연동
              </button>
              <button onClick={saveToDB} disabled={dbBusy}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-green-600 text-white hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed">
                {dbBusy ? "⏳ 처리 중..." : "💾 DB에 저장"}
              </button>
              <button onClick={importFromDB} disabled={dbBusy}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-indigo-600 text-white hover:bg-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed">
                {dbBusy ? "⏳ 처리 중..." : "📥 DB에서 불러오기"}
              </button>
              <button onClick={openPresetPanel}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-500 text-white hover:bg-amber-600 transition">
                📋 프리셋 관리
              </button>
            </div>
          </div>

          {/* ── 프리셋 관리 패널 ── */}
          {showPresetPanel && (
            <div className="border border-amber-200 rounded-xl bg-amber-50/50 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-amber-800">📋 요금제 프리셋</span>
                <button onClick={() => setShowPresetPanel(false)} className="text-gray-400 hover:text-gray-600 text-lg leading-none">&times;</button>
              </div>

              {/* 새 프리셋 저장 */}
              <div className="flex gap-2">
                <input value={presetName} onChange={e => setPresetName(e.target.value)}
                  placeholder="프리셋 이름 입력..." className="flex-1 rounded-lg border border-amber-300 px-3 py-1.5 text-xs focus:ring-2 focus:ring-amber-400 focus:outline-none" />
                <button onClick={saveAsPreset} disabled={presetLoading || !presetName.trim()}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-600 text-white hover:bg-amber-700 transition disabled:opacity-50">
                  {presetLoading ? "⏳" : "💾 저장"}
                </button>
              </div>

              {/* 프리셋 목록 */}
              {presetLoading && presets.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-2">로딩 중...</p>
              ) : presets.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-2">저장된 프리셋이 없습니다</p>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {presets.map(pr => (
                    <div key={pr.id} className="flex items-center gap-2 bg-white border border-amber-200 rounded-lg p-2.5">
                      <div className="flex-1 min-w-0">
                        {editingPreset === pr.id ? (
                          <input autoFocus defaultValue={pr.name}
                            onBlur={e => renamePreset(pr, e.target.value)}
                            onKeyDown={e => { if (e.key === "Enter") renamePreset(pr, e.target.value); if (e.key === "Escape") setEditingPreset(null); }}
                            className="w-full rounded border border-amber-300 px-2 py-1 text-xs focus:ring-2 focus:ring-amber-400 focus:outline-none" />
                        ) : (
                          <>
                            <p className="text-xs font-semibold text-gray-700 truncate">{pr.name}</p>
                            <p className="text-[10px] text-gray-400">{(pr.plans_json || []).length}개 요금제 · {pr.updated_at ? new Date(pr.updated_at).toLocaleDateString() : ""}</p>
                          </>
                        )}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button onClick={() => loadPreset(pr)} title="적용"
                          className="w-7 h-7 flex items-center justify-center rounded text-xs bg-green-100 text-green-700 hover:bg-green-200 transition">📥</button>
                        <button onClick={() => updatePreset(pr)} title="현재 요금제로 덮어쓰기"
                          className="w-7 h-7 flex items-center justify-center rounded text-xs bg-blue-100 text-blue-700 hover:bg-blue-200 transition">🔄</button>
                        <button onClick={() => setEditingPreset(pr.id)} title="이름 변경"
                          className="w-7 h-7 flex items-center justify-center rounded text-xs bg-gray-100 text-gray-600 hover:bg-gray-200 transition">✏️</button>
                        <button onClick={() => deletePreset(pr)} title="삭제"
                          className="w-7 h-7 flex items-center justify-center rounded text-xs bg-red-100 text-red-600 hover:bg-red-200 transition">🗑️</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── DB 약정 불러오기 (멀티 선택) 패널 ── */}
          {showDbImport && (
            <div className="border border-indigo-200 rounded-xl bg-indigo-50/50 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-indigo-800">📥 DB 약정 불러오기</span>
                <button onClick={() => setShowDbImport(false)} className="text-gray-400 hover:text-gray-600 text-lg leading-none">&times;</button>
              </div>

              {/* 검색 */}
              <input value={dbImportSearch} onChange={e => setDbImportSearch(e.target.value)}
                placeholder="모델명/제품명으로 검색..." className="w-full rounded-lg border border-indigo-300 px-3 py-1.5 text-xs focus:ring-2 focus:ring-indigo-400 focus:outline-none" />

              {/* 전체 선택/해제 + 카운트 */}
              {(() => {
                const searchLower = dbImportSearch.trim().toLowerCase();
                const filtered = searchLower
                  ? dbPlansList.filter(p => (p.product_name || "").toLowerCase().includes(searchLower) || (p.model_name || "").toLowerCase().includes(searchLower) || (p.plan_name || "").toLowerCase().includes(searchLower))
                  : dbPlansList;
                const allFilteredSelected = filtered.length > 0 && filtered.every(p => selectedPlanIds.has(p.id));
                // 제품별 그룹핑
                const groups = {};
                filtered.forEach(p => {
                  const key = p.product_name || p.model_name || "기타";
                  if (!groups[key]) groups[key] = [];
                  groups[key].push(p);
                });

                return (
                  <>
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input type="checkbox" checked={allFilteredSelected}
                          onChange={() => {
                            const next = new Set(selectedPlanIds);
                            if (allFilteredSelected) { filtered.forEach(p => next.delete(p.id)); }
                            else { filtered.forEach(p => next.add(p.id)); }
                            setSelectedPlanIds(next);
                          }}
                          className="w-3.5 h-3.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                        <span className="text-xs text-gray-600">전체 선택</span>
                      </label>
                      <span className="text-xs text-indigo-600 font-medium">{selectedPlanIds.size}개 선택 / {filtered.length}개</span>
                    </div>

                    {/* 약정 목록 (제품별 그룹) */}
                    <div className="max-h-72 overflow-y-auto space-y-2 pr-1">
                      {Object.entries(groups).map(([groupName, plans]) => {
                        const allGroupSelected = plans.every(p => selectedPlanIds.has(p.id));
                        return (
                          <div key={groupName} className="border border-indigo-100 rounded-lg bg-white overflow-hidden">
                            <div className="flex items-center gap-2 px-3 py-2 bg-indigo-100/50 cursor-pointer"
                              onClick={() => {
                                const next = new Set(selectedPlanIds);
                                if (allGroupSelected) { plans.forEach(p => next.delete(p.id)); }
                                else { plans.forEach(p => next.add(p.id)); }
                                setSelectedPlanIds(next);
                              }}>
                              <input type="checkbox" checked={allGroupSelected} readOnly
                                className="w-3.5 h-3.5 rounded border-gray-300 text-indigo-600 pointer-events-none" />
                              <span className="text-xs font-semibold text-indigo-800">{groupName}</span>
                              <span className="text-[10px] text-gray-400 ml-auto">{plans.length}개</span>
                            </div>
                            {plans.map(p => (
                              <label key={p.id} className={`flex items-center gap-2 px-3 py-2 border-t border-indigo-50 cursor-pointer hover:bg-indigo-50/50 transition ${selectedPlanIds.has(p.id) ? "bg-indigo-50" : ""}`}>
                                <input type="checkbox" checked={selectedPlanIds.has(p.id)}
                                  onChange={() => {
                                    const next = new Set(selectedPlanIds);
                                    next.has(p.id) ? next.delete(p.id) : next.add(p.id);
                                    setSelectedPlanIds(next);
                                  }}
                                  className="w-3.5 h-3.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                                <div className="flex-1 min-w-0">
                                  <span className="text-xs font-medium text-gray-700">{p.plan_name || "기본"}</span>
                                  <span className="text-[10px] text-gray-400 ml-2">
                                    월 {(p.base_price || 0).toLocaleString()}원 · 흑백 {(p.bw_pages || 0).toLocaleString()}매 · 컬러 {(p.color_pages || 0).toLocaleString()}매
                                  </span>
                                </div>
                                <span className="text-[10px] text-gray-400 flex-shrink-0">#{p.id}</span>
                              </label>
                            ))}
                          </div>
                        );
                      })}
                      {filtered.length === 0 && <p className="text-xs text-gray-400 text-center py-3">검색 결과가 없습니다</p>}
                    </div>

                    {/* 적용 버튼 */}
                    <div className="flex gap-2">
                      <button onClick={() => applySelectedPlans("replace")} disabled={selectedPlanIds.size === 0}
                        className="flex-1 py-2 rounded-lg text-xs font-semibold bg-indigo-600 text-white hover:bg-indigo-700 transition disabled:opacity-40 disabled:cursor-not-allowed">
                        🔄 대체 ({selectedPlanIds.size}개)
                      </button>
                      <button onClick={() => applySelectedPlans("append")} disabled={selectedPlanIds.size === 0}
                        className="flex-1 py-2 rounded-lg text-xs font-semibold bg-green-600 text-white hover:bg-green-700 transition disabled:opacity-40 disabled:cursor-not-allowed">
                        ➕ 추가 ({selectedPlanIds.size}개)
                      </button>
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          <div className="space-y-3">
            {(sec.plans || []).map((plan, j) => (
              <div key={j} className="border border-purple-200 rounded-xl p-4 bg-purple-50/30 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="bg-purple-600 text-white text-[10px] font-bold w-6 h-6 rounded flex items-center justify-center">{plan.plan_no || j+1}</span>
                    <span className="text-sm font-bold text-gray-700">{plan.plan_name || `요금제 ${j+1}`}</span>
                    {plan._db_id && <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">DB #{plan._db_id}</span>}
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => movePlan(j, j-1)} disabled={j===0}
                      className="w-6 h-6 flex items-center justify-center rounded text-gray-400 hover:bg-purple-100 hover:text-purple-600 disabled:opacity-30 disabled:hover:bg-transparent text-xs" title="위로">↑</button>
                    <button onClick={() => movePlan(j, j+1)} disabled={j>=(sec.plans||[]).length-1}
                      className="w-6 h-6 flex items-center justify-center rounded text-gray-400 hover:bg-purple-100 hover:text-purple-600 disabled:opacity-30 disabled:hover:bg-transparent text-xs" title="아래로">↓</button>
                    <button onClick={() => clonePlan(j)}
                      className="w-6 h-6 flex items-center justify-center rounded text-gray-400 hover:bg-blue-100 hover:text-blue-600 text-xs" title="복제">📋</button>
                    <button onClick={() => updateSection(i, { plans: (sec.plans||[]).filter((_,k) => k!==j) })}
                      className="w-6 h-6 flex items-center justify-center rounded text-red-400 hover:bg-red-100 hover:text-red-600 text-xs" title="삭제">✕</button>
                  </div>
                </div>
                {/* 기본 정보 */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {[
                    ["plan_no", "요금제 번호", "01"],
                    ["plan_name", "요금제명", "기본형"],
                    ["model_label", "모델 라벨", "Apeos C3070"],
                  ].map(([key, lbl, ph]) => (
                    <div key={key}>
                      <label className="text-[10px] text-gray-500">{lbl}</label>
                      <input value={plan[key] || ""} onChange={e => updatePlan(j, { [key]: e.target.value })}
                        placeholder={ph} className="w-full rounded border px-2 py-1 text-sm" />
                    </div>
                  ))}
                  {[
                    ["monthly_price", "월 렌탈료 (원)"],
                    ["deposit", "보증금 (원)"],
                  ].map(([key, lbl]) => (
                    <div key={key}>
                      <label className="text-[10px] text-gray-500">{lbl}</label>
                      <input type="number" value={plan[key] ?? ""} onChange={e => updatePlan(j, { [key]: e.target.value === "" ? "" : parseInt(e.target.value)||0 })}
                        className="w-full rounded border px-2 py-1 text-sm" />
                    </div>
                  ))}
                  <div>
                    <label className="text-[10px] text-gray-500">약정기간</label>
                    <input value={plan.contract_period || ""} onChange={e => updatePlan(j, { contract_period: e.target.value })}
                      placeholder="3년" className="w-full rounded border px-2 py-1 text-sm" />
                  </div>
                </div>
                {/* 출력매수 + 초과단가 */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div className="sm:col-span-2">
                    <label className="text-[10px] text-gray-500">기본 출력매수 (흑백)</label>
                    <input value={plan.included_pages || ""} onChange={e => updatePlan(j, { included_pages: e.target.value })}
                      placeholder="흑백 2,000매" className="w-full rounded border px-2 py-1 text-sm" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-[10px] text-gray-500">기본 출력매수 (컬러)</label>
                    <input value={plan.color_pages || ""} onChange={e => updatePlan(j, { color_pages: e.target.value })}
                      placeholder="컬러 200매" className="w-full rounded border px-2 py-1 text-sm" />
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500">흑백 초과단가 (원/장)</label>
                    <input type="number" value={plan.extra_bw_price ?? 15} onChange={e => updatePlan(j, { extra_bw_price: e.target.value === "" ? "" : parseInt(e.target.value)||0 })}
                      className="w-full rounded border px-2 py-1 text-sm" />
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500">컬러 초과단가 (원/장)</label>
                    <input type="number" value={plan.extra_color_price ?? 80} onChange={e => updatePlan(j, { extra_color_price: e.target.value === "" ? "" : parseInt(e.target.value)||0 })}
                      className="w-full rounded border px-2 py-1 text-sm" />
                  </div>
                </div>
                {/* 선택사항 (태그 에디터) */}
                <div>
                  <label className="text-[10px] text-gray-500 mb-1 block">선택사항 (옵션)</label>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {getOptionTags(plan.options).map(tag => (
                      <span key={tag} className="inline-flex items-center gap-1 bg-purple-100 text-purple-700 text-xs font-medium px-2.5 py-1 rounded-full">
                        {tag}
                        <button onClick={() => removeOption(j, tag)} className="text-purple-400 hover:text-red-500 text-[10px] font-bold ml-0.5">✕</button>
                      </span>
                    ))}
                    {getOptionTags(plan.options).length === 0 && <span className="text-xs text-gray-400">옵션 없음</span>}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {OPTION_PRESETS.filter(p => !getOptionTags(plan.options).includes(p)).map(preset => (
                      <button key={preset} onClick={() => addOption(j, preset)}
                        className="text-[11px] px-2 py-0.5 rounded-full border border-purple-300 text-purple-600 hover:bg-purple-100 transition">
                        + {preset}
                      </button>
                    ))}
                  </div>
                  <input className="w-full rounded border px-2 py-1 text-sm mt-1.5" placeholder="직접 입력 후 Enter"
                    onKeyDown={e => {
                      if (e.key === "Enter" && e.target.value.trim()) {
                        addOption(j, e.target.value.trim()); e.target.value = "";
                      }
                    }} />
                </div>
                {/* 비고/메모 */}
                <div>
                  <label className="text-[10px] text-gray-500">비고 (메모)</label>
                  <input value={plan.note || ""} onChange={e => updatePlan(j, { note: e.target.value })}
                    placeholder="예: VAT 별도, 설치비 무료 등" className="w-full rounded border px-2 py-1 text-sm" />
                </div>
              </div>
            ))}
          </div>
          <button onClick={() => {
            const plans = [...(sec.plans||[]), {
              plan_no: String((sec.plans||[]).length+1).padStart(2,"0"), plan_name: "", model_label: "",
              monthly_price: 0, deposit: 200000, included_pages: "", color_pages: "",
              contract_period: "3년", options: "", extra_bw_price: 15, extra_color_price: 80, note: ""
            }];
            updateSection(i, { plans });
          }} className="w-full py-3 rounded-xl border-2 border-dashed border-purple-300 text-sm text-purple-600 font-medium hover:border-purple-500 hover:bg-purple-50 transition">+ 요금제 추가</button>
        </>
      );
    }

    case "rental_calculator":
      return (
        <>
          <input value={sec.title || ""} onChange={e => updateSection(i, { title: e.target.value })}
            placeholder="계산기 제목" className={inp} />
          <textarea value={sec.description || ""} onChange={e => updateSection(i, { description: e.target.value })}
            placeholder="설명" rows={2} className={`${inp} resize-none`} />
          <input value={sec.note || ""} onChange={e => updateSection(i, { note: e.target.value })}
            placeholder="참고사항" className={inp} />
        </>
      );

    case "cta":
      return (
        <>
          <input value={sec.title || ""} onChange={e => updateSection(i, { title: e.target.value })}
            placeholder="CTA 제목" className={inp} />
          <textarea value={sec.description || ""} onChange={e => updateSection(i, { description: e.target.value })}
            placeholder="설명 문구" rows={2} className={`${inp} resize-none`} />
          <div className="grid grid-cols-2 gap-3">
            <input value={sec.button_text || ""} onChange={e => updateSection(i, { button_text: e.target.value })}
              placeholder="버튼 텍스트" className={inp} />
            <input value={sec.button_link || ""} onChange={e => updateSection(i, { button_link: e.target.value })}
              placeholder="버튼 링크 (/consultation)" className={inp} />
          </div>
        </>
      );

    /* ══════════════════════════════════════════════════
       판매 가격표 (Sale Pricing) — 엑셀: 판매 복사기
       ═══════════════════════════════════════════════════ */
    case "sale_pricing": {
      const products = sec.products || [];
      const updateProduct = (j, patch) => {
        const arr = [...products]; arr[j] = { ...arr[j], ...patch }; updateSection(i, { products: arr });
      };
      return (
        <>
          <input value={sec.title || ""} onChange={e => updateSection(i, { title: e.target.value })}
            placeholder="섹션 제목 (예: 판매 가격표)" className={inp} />
          <div className="space-y-3">
            {products.map((p, j) => (
              <div key={j} className="border border-green-200 rounded-xl p-4 bg-green-50/30 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold text-gray-700">#{j + 1} {p.product_name || "새 제품"}</span>
                  <button onClick={() => updateSection(i, { products: products.filter((_, k) => k !== j) })}
                    className="text-red-400 hover:text-red-600 text-sm">✕ 삭제</button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className="text-[10px] text-gray-500">제품명</label>
                    <input value={p.product_name || ""} onChange={e => updateProduct(j, { product_name: e.target.value })}
                      placeholder="[FUJIXEROX] A4 컬러 복합기 Apeos C325z" className="w-full rounded border px-2 py-1.5 text-sm" />
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500">브랜드</label>
                    <BrandSelect value={p.brand || ""} onChange={brand => updateProduct(j, { brand })}
                      className="w-full rounded border px-2 py-1.5 text-sm" />
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500">본체 기계값 (원)</label>
                    <input type="number" value={p.body_price ?? ""} onChange={e => updateProduct(j, { body_price: e.target.value === "" ? "" : parseInt(e.target.value)||0 })}
                      className="w-full rounded border px-2 py-1.5 text-sm" />
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500">서울/경기 배송비 (원)</label>
                    <input type="number" value={p.delivery_seoul ?? ""} onChange={e => updateProduct(j, { delivery_seoul: e.target.value === "" ? "" : parseInt(e.target.value)||0 })}
                      className="w-full rounded border px-2 py-1.5 text-sm" />
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500">경기외 지역 배송비 (원)</label>
                    <input type="number" value={p.delivery_other ?? ""} onChange={e => updateProduct(j, { delivery_other: e.target.value === "" ? "" : parseInt(e.target.value)||0 })}
                      className="w-full rounded border px-2 py-1.5 text-sm" />
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500">FAX KIT (원)</label>
                    <input type="number" value={p.fax_kit ?? ""} onChange={e => updateProduct(j, { fax_kit: e.target.value === "" ? "" : parseInt(e.target.value)||0 })}
                      className="w-full rounded border px-2 py-1.5 text-sm" />
                  </div>
                </div>
                {/* 소계 표시 */}
                <div className="bg-white rounded-lg p-3 text-sm space-y-1">
                  <div className="flex justify-between text-gray-500">
                    <span>서울/경기 합계</span>
                    <span className="font-semibold text-gray-900">{((p.body_price || 0) + (p.delivery_seoul || 0)).toLocaleString()}원</span>
                  </div>
                  <div className="flex justify-between text-gray-500">
                    <span>경기외 합계</span>
                    <span className="font-semibold text-gray-900">{((p.body_price || 0) + (p.delivery_other || 0)).toLocaleString()}원</span>
                  </div>
                  {!!(p.fax_kit) && <div className="flex justify-between text-gray-500">
                    <span>FAX KIT 포함 (서울/경기)</span>
                    <span className="font-semibold text-blue-700">{((p.body_price || 0) + (p.delivery_seoul || 0) + (p.fax_kit || 0)).toLocaleString()}원</span>
                  </div>}
                </div>
                {/* 제품 이미지 */}
                <div>
                  <label className="text-[10px] text-gray-500">제품 이미지</label>
                  <ItemImageUploader
                    imageUrl={p.image_url || ""}
                    onUrlChange={(url) => updateProduct(j, { image_url: url })}
                  />
                </div>
              </div>
            ))}
          </div>
          <button onClick={() => updateSection(i, { products: [...products, { product_name: "", brand: "", body_price: 0, delivery_seoul: 0, delivery_other: 0, fax_kit: 0, image_url: "" }] })}
            className="text-[12px] text-green-600 font-medium hover:underline">+ 제품 추가</button>
        </>
      );
    }

    /* ══════════════════════════════════════════════════
       렌탈 가격표 (Rental Pricing) — 엑셀: 렌탈 복사기
       ═══════════════════════════════════════════════════ */
    case "rental_pricing": {
      const products = sec.products || [];
      const updateProduct = (j, patch) => {
        const arr = [...products]; arr[j] = { ...arr[j], ...patch }; updateSection(i, { products: arr });
      };
      const moveProduct = (j, dir) => {
        const arr = [...products];
        const to = j + dir;
        if (to < 0 || to >= arr.length) return;
        [arr[j], arr[to]] = [arr[to], arr[j]];
        updateSection(i, { products: arr });
      };
      /* 열(column) 관리 */
      const defaultCols = [
        { key: "deposit_text", label: "보증금" },
        { key: "output_desc", label: "기본 출력매수" },
        { key: "contract_period", label: "약정기간" },
        { key: "options_desc", label: "선택사항" },
      ];
      const columns = sec.custom_columns || defaultCols;
      const addColumn = () => {
        const name = prompt("추가할 열 이름을 입력하세요");
        if (!name || !name.trim()) return;
        const key = "col_" + Date.now();
        updateSection(i, { custom_columns: [...columns, { key, label: name.trim() }] });
      };
      const removeColumn = (idx) => {
        if (columns.length <= 1) return;
        updateSection(i, { custom_columns: columns.filter((_, ci) => ci !== idx) });
      };
      const renameColumn = (idx, label) => {
        const arr = [...columns]; arr[idx] = { ...arr[idx], label };
        updateSection(i, { custom_columns: arr });
      };

      /* ── 섹션 프리셋 CRUD (rental_pricing) ── */
      const loadRpPresets = async () => {
        setSecPresetLoading(true);
        try {
          const { data } = await api.get("/api/admin/section-presets", { params: { section_type: "rental_pricing" } });
          setSecPresets(data || []);
        } catch (e) { setToast("프리셋 목록 로드 실패"); }
        finally { setSecPresetLoading(false); }
      };
      const openRpPresetPanel = () => { setShowSecPresetPanel(true); loadRpPresets(); };
      const saveRpPreset = async () => {
        const name = secPresetName.trim();
        if (!name) { setToast("프리셋 이름을 입력하세요"); return; }
        if (products.length === 0) { setToast("저장할 플랜이 없습니다"); return; }
        setSecPresetLoading(true);
        try {
          await api.post("/api/admin/section-presets", {
            name, section_type: "rental_pricing",
            section_data: { title: sec.title, footer_note: sec.footer_note, custom_columns: sec.custom_columns, products }
          });
          setToast(`✅ 프리셋 "${name}" 저장 완료`);
          setSecPresetName("");
          loadRpPresets();
        } catch (e) { setToast("프리셋 저장 실패: " + (e.response?.data?.error || e.message)); }
        finally { setSecPresetLoading(false); }
      };
      const applyRpPreset = (preset) => {
        const d = preset.section_json || {};
        const patch = {};
        if (d.title !== undefined) patch.title = d.title;
        if (d.footer_note !== undefined) patch.footer_note = d.footer_note;
        if (d.custom_columns) patch.custom_columns = d.custom_columns;
        if (Array.isArray(d.products)) patch.products = d.products;
        updateSection(i, patch);
        setToast(`✅ 프리셋 "${preset.name}" 적용 완료 (${(d.products || []).length}개 플랜)`);
      };
      const overwriteRpPreset = async (preset) => {
        if (!window.confirm(`프리셋 "${preset.name}"을(를) 현재 내용으로 덮어쓰시겠습니까?`)) return;
        setSecPresetLoading(true);
        try {
          await api.put(`/api/admin/section-presets/${preset.id}`, {
            section_data: { title: sec.title, footer_note: sec.footer_note, custom_columns: sec.custom_columns, products }
          });
          setToast(`✅ 프리셋 "${preset.name}" 업데이트 완료`);
          loadRpPresets();
        } catch (e) { setToast("프리셋 업데이트 실패"); }
        finally { setSecPresetLoading(false); }
      };
      const renameRpPreset = async (preset, newName) => {
        if (!newName?.trim()) return;
        setSecPresetLoading(true);
        try {
          await api.put(`/api/admin/section-presets/${preset.id}`, { name: newName.trim() });
          setToast(`✅ 이름 변경 완료`);
          setEditingSecPreset(null);
          loadRpPresets();
        } catch (e) { setToast("이름 변경 실패"); }
        finally { setSecPresetLoading(false); }
      };
      const deleteRpPreset = async (preset) => {
        if (!window.confirm(`프리셋 "${preset.name}"을(를) 삭제하시겠습니까?`)) return;
        setSecPresetLoading(true);
        try {
          await api.delete(`/api/admin/section-presets/${preset.id}`);
          setToast(`🗑️ 프리셋 "${preset.name}" 삭제 완료`);
          loadRpPresets();
        } catch (e) { setToast("프리셋 삭제 실패"); }
        finally { setSecPresetLoading(false); }
      };

      return (
        <>
          <input value={sec.title || ""} onChange={e => updateSection(i, { title: e.target.value })}
            placeholder="섹션 제목 (예: 맞춤 렌탈 프로그램)" className={inp} />
          <textarea value={sec.footer_note || ""} onChange={e => updateSection(i, { footer_note: e.target.value })}
            className={`${inp} resize-none`} rows={1} placeholder="하단 참고문구 (예: * 사무환경에 알맞게 옵션 추가 협의 가능 (부가세 별도))" />

          {/* 프리셋 버튼 */}
          <div className="flex items-center gap-2 bg-teal-50 border border-teal-200 rounded-xl p-3">
            <span className="text-xs text-teal-700 font-medium flex-1">📋 렌탈가격표 프리셋</span>
            <button onClick={openRpPresetPanel}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-teal-500 text-white hover:bg-teal-600 transition">
              📋 프리셋 관리
            </button>
            <span className="text-[10px] text-gray-400">저장 → 여러 페이지에 적용</span>
          </div>

          {/* 프리셋 패널 */}
          {showSecPresetPanel && (
            <div className="border border-teal-200 rounded-xl bg-teal-50/50 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-teal-800">📋 렌탈가격표 프리셋</span>
                <button onClick={() => setShowSecPresetPanel(false)} className="text-gray-400 hover:text-gray-600 text-lg leading-none">&times;</button>
              </div>
              <div className="flex gap-2">
                <input value={secPresetName} onChange={e => setSecPresetName(e.target.value)}
                  placeholder="프리셋 이름 (예: C325z 기본 렌탈)" className="flex-1 rounded-lg border border-teal-300 px-3 py-1.5 text-xs focus:ring-2 focus:ring-teal-400 focus:outline-none" />
                <button onClick={saveRpPreset} disabled={secPresetLoading || !secPresetName.trim()}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-teal-600 text-white hover:bg-teal-700 transition disabled:opacity-50">
                  {secPresetLoading ? "⏳" : "💾 현재 내용 저장"}
                </button>
              </div>
              {secPresetLoading && secPresets.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-2">로딩 중...</p>
              ) : secPresets.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-2">저장된 프리셋이 없습니다</p>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {secPresets.map(pr => (
                    <div key={pr.id} className="flex items-center gap-2 bg-white border border-teal-200 rounded-lg p-2.5">
                      <div className="flex-1 min-w-0">
                        {editingSecPreset === pr.id ? (
                          <input autoFocus defaultValue={pr.name}
                            onBlur={e => renameRpPreset(pr, e.target.value)}
                            onKeyDown={e => { if (e.key === "Enter") renameRpPreset(pr, e.target.value); if (e.key === "Escape") setEditingSecPreset(null); }}
                            className="w-full rounded border border-teal-300 px-2 py-1 text-xs focus:ring-2 focus:ring-teal-400 focus:outline-none" />
                        ) : (
                          <>
                            <p className="text-xs font-semibold text-gray-700 truncate">{pr.name}</p>
                            <p className="text-[10px] text-gray-400 truncate">
                              {(pr.section_json?.products || []).length}개 플랜 · {pr.updated_at ? new Date(pr.updated_at).toLocaleDateString() : ""}
                            </p>
                          </>
                        )}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button onClick={() => applyRpPreset(pr)} title="적용"
                          className="w-7 h-7 flex items-center justify-center rounded text-xs bg-green-100 text-green-700 hover:bg-green-200 transition">📥</button>
                        <button onClick={() => overwriteRpPreset(pr)} title="현재 내용으로 덮어쓰기"
                          className="w-7 h-7 flex items-center justify-center rounded text-xs bg-blue-100 text-blue-700 hover:bg-blue-200 transition">🔄</button>
                        <button onClick={() => setEditingSecPreset(pr.id)} title="이름 변경"
                          className="w-7 h-7 flex items-center justify-center rounded text-xs bg-gray-100 text-gray-600 hover:bg-gray-200 transition">✏️</button>
                        <button onClick={() => deleteRpPreset(pr)} title="삭제"
                          className="w-7 h-7 flex items-center justify-center rounded text-xs bg-red-100 text-red-600 hover:bg-red-200 transition">🗑️</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* 열 편집 */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] text-gray-700 font-bold">📊 표 열:</span>
              {columns.map((col, ci) => (
                <span key={ci} className="inline-flex items-center gap-0.5 bg-white border border-gray-200 rounded-lg px-1.5 py-0.5 text-xs">
                  <input value={col.label} onChange={e => renameColumn(ci, e.target.value)}
                    className="border-0 bg-transparent text-xs font-medium text-gray-700 w-16 focus:outline-none focus:bg-blue-50 rounded px-0.5" />
                  <button onClick={() => removeColumn(ci)} className="text-red-400 hover:text-red-600 text-[10px]">✕</button>
                </span>
              ))}
              <button onClick={addColumn} className="text-[11px] text-blue-600 hover:text-blue-800 font-medium border border-dashed border-blue-300 rounded-lg px-2 py-0.5">+ 열 추가</button>
            </div>
          </div>

          {/* 플랜 카드들 */}
          <div className="space-y-4">
            {products.map((p, j) => (
              <div key={j} className="border border-purple-200 rounded-xl p-4 bg-purple-50/30 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="bg-purple-700 text-white text-[10px] font-bold w-6 h-6 rounded flex items-center justify-center">{String(j+1).padStart(2,'0')}</span>
                    <span className="text-sm font-bold text-gray-700">{p.product_name || "새 플랜"}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => moveProduct(j, -1)} disabled={j === 0} className="text-gray-400 hover:text-gray-600 text-sm disabled:opacity-30">↑</button>
                    <button onClick={() => moveProduct(j, 1)} disabled={j === products.length - 1} className="text-gray-400 hover:text-gray-600 text-sm disabled:opacity-30">↓</button>
                    <button onClick={() => updateSection(i, { products: products.filter((_, k) => k !== j) })}
                      className="text-red-400 hover:text-red-600 text-sm ml-1">✕</button>
                  </div>
                </div>
                {/* 플랜 기본 정보 */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="col-span-2">
                    <label className="text-[10px] text-gray-500">제품명 (예: Apeos C325z (분당31매))</label>
                    <input value={p.product_name || ""} onChange={e => updateProduct(j, { product_name: e.target.value })}
                      className="w-full rounded border px-2 py-1.5 text-sm" placeholder="Apeos C325z (분당31매)" />
                  </div>
                  <div>
                    <label className="text-[10px] text-purple-700 font-bold">월 렌탈료 (원)</label>
                    <input type="number" value={p.monthly_total ?? ""} onChange={e => updateProduct(j, { monthly_total: e.target.value === "" ? "" : parseInt(e.target.value)||0 })}
                      className="w-full rounded border px-2 py-1.5 text-sm font-bold text-purple-700" placeholder="50000" />
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500">브랜드</label>
                    <BrandSelect value={p.brand || ""} onChange={brand => updateProduct(j, { brand })}
                      className="w-full rounded border px-2 py-1.5 text-sm" />
                  </div>
                </div>
                {/* 표 데이터 — 열별 값 */}
                <div className="bg-white rounded-lg p-3 border border-purple-100">
                  <label className="text-[10px] text-gray-500 font-semibold mb-2 block">📋 상세 항목 (행 = 이 플랜의 값)</label>
                  <div className="grid grid-cols-2 gap-2">
                    {columns.map((col, ci) => (
                      <div key={ci}>
                        <label className="text-[10px] text-gray-500">{col.label}</label>
                        {col.key === "output_desc" ? (
                          <textarea value={(p.col_values || {})[col.key] || ""} onChange={e => {
                            const vals = { ...(p.col_values || {}), [col.key]: e.target.value };
                            updateProduct(j, { col_values: vals });
                          }} className="w-full rounded border px-2 py-1.5 text-sm resize-none" rows={2} placeholder={"흑백 1,000매 (합계 10,000원)\n칼라 500매 (합계 50,000원)"} />
                        ) : (
                          <input value={(p.col_values || {})[col.key] || ""} onChange={e => {
                            const vals = { ...(p.col_values || {}), [col.key]: e.target.value };
                            updateProduct(j, { col_values: vals });
                          }} className="w-full rounded border px-2 py-1.5 text-sm" placeholder={col.label} />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                {/* 제품 이미지 */}
                <div>
                  <label className="text-[10px] text-gray-500">제품 이미지</label>
                  <ItemImageUploader
                    imageUrl={p.image_url || ""}
                    onUrlChange={(url) => updateProduct(j, { image_url: url })}
                  />
                </div>
              </div>
            ))}
          </div>
          <button onClick={() => updateSection(i, { products: [...products, {
            product_name: "", brand: "", monthly_total: 0, col_values: {},
            image_url: "",
          }] })}
            className="text-[12px] text-purple-600 font-medium hover:underline">+ 플랜 추가</button>
          {onSyncRental && (
            <button onClick={() => onSyncRental(i)}
              className="w-full py-2 rounded-lg text-xs font-semibold bg-purple-100 text-purple-700 hover:bg-purple-200 transition">🔗 옵션 선택기에 연동</button>
          )}
          {onSyncOptionsToRental && (
            <button onClick={() => onSyncOptionsToRental(i)}
              className="w-full py-2 rounded-lg text-xs font-semibold bg-orange-100 text-orange-700 hover:bg-orange-200 transition">🔗 옵션 단가 → 출력매수 자동입력</button>
          )}
          {onGenerateOptionsFromRental && (
            <button onClick={() => onGenerateOptionsFromRental(i)}
              className="w-full py-2 rounded-lg text-xs font-semibold bg-teal-100 text-teal-700 hover:bg-teal-200 transition">📊 이 표로 옵션 자동생성</button>
          )}
        
        </>
      );
    }

    /* ══════════════════════════════════════════════════
       토너/소모품 정보 — 엑셀: 토너
       ═══════════════════════════════════════════════════ */
    case "toner_spec": {
      const products = sec.products || [];
      const updateProduct = (j, patch) => {
        const arr = [...products]; arr[j] = { ...arr[j], ...patch }; updateSection(i, { products: arr });
      };
      return (
        <>
          <input value={sec.title || ""} onChange={e => updateSection(i, { title: e.target.value })}
            placeholder="섹션 제목 (예: 토너/소모품 정보)" className={inp} />
          <div className="space-y-3">
            {products.map((p, j) => (
              <div key={j} className="border border-orange-200 rounded-xl p-4 bg-orange-50/30 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold text-gray-700">#{j + 1} {p.product_name || "새 토너/소모품"}</span>
                  <button onClick={() => updateSection(i, { products: products.filter((_, k) => k !== j) })}
                    className="text-red-400 hover:text-red-600 text-sm">✕ 삭제</button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div className="col-span-2 sm:col-span-3">
                    <label className="text-[10px] text-gray-500">제품명</label>
                    <input value={p.product_name || ""} onChange={e => updateProduct(j, { product_name: e.target.value })}
                      placeholder="[삼성전자] 정품토너 MLT-D111S 검정 (SL-M2020/1K)" className="w-full rounded border px-2 py-1.5 text-sm" />
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500">브랜드</label>
                    <BrandSelect value={p.brand || ""} onChange={brand => updateProduct(j, { brand })}
                      className="w-full rounded border px-2 py-1.5 text-sm" />
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500">정품/호환</label>
                    <select value={p.genuine_type || "정품"} onChange={e => updateProduct(j, { genuine_type: e.target.value })}
                      className="w-full rounded border px-2 py-1.5 text-sm">
                      <option value="정품">정품</option>
                      <option value="호환">호환</option>
                      <option value="재생">재생</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500">토너/잉크</label>
                    <select value={p.ink_type || "토너"} onChange={e => updateProduct(j, { ink_type: e.target.value })}
                      className="w-full rounded border px-2 py-1.5 text-sm">
                      <option value="토너">토너</option>
                      <option value="잉크">잉크</option>
                      <option value="드럼">드럼</option>
                      <option value="현상기">현상기</option>
                      <option value="정착기">정착기</option>
                      <option value="부품">부품</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500">색상</label>
                    <select value={p.color || ""} onChange={e => updateProduct(j, { color: e.target.value })}
                      className="w-full rounded border px-2 py-1.5 text-sm">
                      <option value="">미지정</option>
                      <option value="검정">검정</option>
                      <option value="파랑">파랑</option>
                      <option value="빨강">빨강</option>
                      <option value="노랑">노랑</option>
                      <option value="4색세트">4색 세트</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500">모델번호</label>
                    <input value={p.model_number || ""} onChange={e => updateProduct(j, { model_number: e.target.value })}
                      placeholder="MLT-D111S" className="w-full rounded border px-2 py-1.5 text-sm" />
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500">시장가 (원)</label>
                    <input type="number" value={p.market_price ?? ""} onChange={e => updateProduct(j, { market_price: e.target.value === "" ? "" : parseInt(e.target.value)||0 })}
                      className="w-full rounded border px-2 py-1.5 text-sm" />
                  </div>
                </div>
                {/* 컬러 badge + price */}
                <div className="flex items-center gap-2 flex-wrap">
                  {p.genuine_type && (
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${p.genuine_type === "정품" ? "bg-blue-100 text-blue-700" : p.genuine_type === "호환" ? "bg-yellow-100 text-yellow-700" : "bg-green-100 text-green-700"}`}>
                      {p.genuine_type}
                    </span>
                  )}
                  {p.ink_type && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{p.ink_type}</span>}
                  {p.color && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{p.color}</span>}
                  {!!(p.market_price) && <span className="text-xs font-bold text-red-600 ml-auto">{p.market_price.toLocaleString()}원</span>}
                </div>
                {/* 제품 이미지 */}
                <div>
                  <label className="text-[10px] text-gray-500">제품 이미지</label>
                  <ItemImageUploader
                    imageUrl={p.image_url || ""}
                    onUrlChange={(url) => updateProduct(j, { image_url: url })}
                  />
                </div>
              </div>
            ))}
          </div>
          <button onClick={() => updateSection(i, { products: [...products, { product_name: "", brand: "", genuine_type: "정품", ink_type: "토너", color: "", model_number: "", market_price: 0, image_url: "" }] })}
            className="text-[12px] text-orange-600 font-medium hover:underline">+ 토너/소모품 추가</button>
        </>
      );
    }

    /* ══════════════════════════════════════════════════
       이미지 + 텍스트 블록 (비주얼, HTML 불필요)
       ═══════════════════════════════════════════════════ */
    case "image_text_block":
      return (
        <>
          <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-xs text-green-700 flex items-center gap-2">
            <span>🖼️</span>
            <span>이미지와 텍스트를 나란히 배치하는 블록입니다. HTML 지식이 필요 없습니다!</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="text-[10px] text-gray-500">제목</label>
              <input value={sec.title || ""} onChange={e => updateSection(i, { title: e.target.value })}
                placeholder="예: Kyocera MA2100cwfx" className={inp} />
            </div>
            <div className="col-span-2">
              <label className="text-[10px] text-gray-500">설명</label>
              <textarea value={sec.description || ""} onChange={e => updateSection(i, { description: e.target.value })}
                rows={3} placeholder="제품에 대한 설명을 입력하세요..." className={`${inp} resize-none`} />
            </div>
          </div>
          <div>
            <label className="text-[10px] text-gray-500">이미지</label>
            <ItemImageUploader
              imageUrl={sec.image_url || ""}
              onUrlChange={(url) => updateSection(i, { image_url: url })}
            />
          </div>
          <div>
            <label className="text-[10px] text-gray-500">영상 (선택, 무음 자동재생)</label>
            {sec.video_url ? (
              <div className="space-y-2 mt-1">
                <div className="flex items-center gap-2">
                  <video src={sec.video_url} className="h-20 rounded-lg bg-black" muted autoPlay loop playsInline />
                  <button onClick={() => updateSection(i, { _editingVideo: true })} className="text-blue-500 text-xs hover:underline">✂️ 편집</button>
                  <button onClick={() => updateSection(i, { video_url: "" })} className="text-red-400 text-xs hover:underline">삭제</button>
                </div>
                {sec._editingVideo && (
                  <VideoTrimmer
                    videoUrl={sec.video_url}
                    onSave={(url) => updateSection(i, { video_url: url, _editingVideo: false })}
                    onClose={() => updateSection(i, { _editingVideo: false })}
                  />
                )}
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-gray-400">반복:</span>
                  {[["loop","무한 ∞"],["once","1회"],["2","2회"],["3","3회"],["5","5회"]].map(([v,l])=>(
                    <button key={v} onClick={()=>updateSection(i,{video_loop:v})}
                      className={`px-2 py-0.5 rounded text-[10px] font-medium transition ${(sec.video_loop||"loop")===v?"bg-[#0071e3] text-white":"bg-gray-100 text-gray-500 hover:bg-gray-200"}`}>{l}</button>
                  ))}
                  <input type="number" min={1} max={99} value={!["loop","once","2","3","5"].includes(sec.video_loop||"loop") ? sec.video_loop : ""}
                    onChange={e=>{const n=parseInt(e.target.value,10); if(n>0&&n<100) updateSection(i,{video_loop:String(n)});}}
                    placeholder="N" className="w-10 px-1 py-0.5 text-[10px] border border-gray-200 rounded text-center" />
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-gray-400">전환:</span>
                  {[["cut","컷 (즉시)"],["fade-black","페이드 블랙"],["fade-white","페이드 화이트"],["crossfade","크로스페이드"]].map(([v,l])=>(
                    <button key={v} onClick={()=>updateSection(i,{video_transition:v})}
                      className={`px-2 py-0.5 rounded text-[10px] font-medium transition ${(sec.video_transition||"cut")===v?"bg-purple-600 text-white":"bg-gray-100 text-gray-500 hover:bg-gray-200"}`}>{l}</button>
                  ))}
                </div>
              </div>
            ) : (
              <label className="mt-1 flex items-center gap-2 px-3 py-2 border-2 border-dashed border-gray-200 rounded-lg cursor-pointer hover:border-blue-300 hover:bg-blue-50/30 transition">
                <span className="text-gray-400 text-sm">🎬</span>
                <span className="text-xs text-gray-500">MP4/MOV 영상 업로드 (자동 최적화)</span>
                <input type="file" accept="video/mp4,video/quicktime,video/webm" className="hidden" onChange={async (e) => {
                  const file = e.target.files?.[0]; if (!file) return;
                  const fd = new FormData(); fd.append("video", file);
                  try {
                    updateSection(i, { _videoUploading: true });
                    const res = await api.post("/api/admin/upload-video", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
                    updateSection(i, { video_url: res.data.url, _videoUploading: false });
                  } catch (err) { alert("영상 업로드 실패: " + (err.response?.data?.error || err.message)); updateSection(i, { _videoUploading: false }); }
                }} />
              </label>
            )}
            {sec._videoUploading && <p className="text-[10px] text-blue-500 mt-1 animate-pulse">영상 변환 중... (최대 2분 소요)</p>}
          </div>
          <div>
            <label className="text-[10px] text-gray-500">이미지 위치</label>
            <div className="flex gap-1.5 mt-1">
              {[["left","왼쪽 이미지"],["right","오른쪽 이미지"],["top","상단 이미지"]].map(([v,l]) => (
                <button key={v} onClick={() => updateSection(i, { image_position: v })}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition ${(sec.image_position || "right") === v ? "border-blue-500 bg-blue-100 text-blue-700" : "border-gray-200 text-gray-500 hover:bg-gray-50"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          {/* Feature list */}
          <div>
            <label className="text-[10px] text-gray-500">핵심 기능 목록 (선택)</label>
            {(sec.features || []).map((f, j) => (
              <div key={j} className="flex gap-2 mb-1.5">
                <span className="text-green-500 mt-0.5 text-sm">✓</span>
                <input value={f} onChange={e => {
                  const feats = [...(sec.features||[])]; feats[j] = e.target.value; updateSection(i, { features: feats });
                }} className="flex-1 rounded border px-2 py-1 text-sm" placeholder="핵심 기능 설명" />
                <button onClick={() => updateSection(i, { features: (sec.features||[]).filter((_,k) => k!==j) })}
                  className="text-red-400 text-sm">✕</button>
              </div>
            ))}
            <button onClick={() => updateSection(i, { features: [...(sec.features||[]), ""] })}
              className="text-[12px] text-green-600 font-medium hover:underline">+ 기능 추가</button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] text-gray-500">CTA 버튼 텍스트 (선택)</label>
              <input value={sec.cta_text || ""} onChange={e => updateSection(i, { cta_text: e.target.value })}
                placeholder="가격 문의" className="w-full rounded border px-2 py-1.5 text-sm" />
            </div>
            <div>
              <label className="text-[10px] text-gray-500">CTA 링크</label>
              <input value={sec.cta_link || ""} onChange={e => updateSection(i, { cta_link: e.target.value })}
                placeholder="/consultation" className="w-full rounded border px-2 py-1.5 text-sm" />
            </div>
          </div>
        </>
      );

    /* ══════════════════════════════════════════════════
       제목 + 설명 블록 (비주얼, HTML 불필요)
       ═══════════════════════════════════════════════════ */
    case "heading_text": {
      const loadSecPresets = async () => {
        setSecPresetLoading(true);
        try {
          const { data } = await api.get("/api/admin/section-presets", { params: { section_type: "heading_text" } });
          setSecPresets(data || []);
        } catch (e) { setToast("프리셋 목록 로드 실패"); }
        finally { setSecPresetLoading(false); }
      };
      const openSecPresetPanel = () => { setShowSecPresetPanel(true); loadSecPresets(); };
      const saveSecPreset = async () => {
        const name = secPresetName.trim();
        if (!name) { setToast("프리셋 이름을 입력하세요"); return; }
        setSecPresetLoading(true);
        try {
          const { heading, subheading, description, align, bg_style } = sec;
          await api.post("/api/admin/section-presets", { name, section_type: "heading_text", section_data: { heading, subheading, description, align, bg_style } });
          setToast(`✅ 프리셋 "${name}" 저장 완료`);
          setSecPresetName("");
          loadSecPresets();
        } catch (e) { setToast("프리셋 저장 실패: " + (e.response?.data?.error || e.message)); }
        finally { setSecPresetLoading(false); }
      };
      const applySecPreset = (preset) => {
        const d = preset.section_json || {};
        updateSection(i, { heading: d.heading || "", subheading: d.subheading || "", description: d.description || "", align: d.align || "center", bg_style: d.bg_style || "white" });
        setToast(`✅ 프리셋 "${preset.name}" 적용 완료`);
      };
      const overwriteSecPreset = async (preset) => {
        if (!window.confirm(`프리셋 "${preset.name}"을(를) 현재 내용으로 덮어쓰시겠습니까?`)) return;
        setSecPresetLoading(true);
        try {
          const { heading, subheading, description, align, bg_style } = sec;
          await api.put(`/api/admin/section-presets/${preset.id}`, { section_data: { heading, subheading, description, align, bg_style } });
          setToast(`✅ 프리셋 "${preset.name}" 업데이트 완료`);
          loadSecPresets();
        } catch (e) { setToast("프리셋 업데이트 실패"); }
        finally { setSecPresetLoading(false); }
      };
      const renameSecPreset = async (preset, newName) => {
        if (!newName?.trim()) return;
        setSecPresetLoading(true);
        try {
          await api.put(`/api/admin/section-presets/${preset.id}`, { name: newName.trim() });
          setToast(`✅ 이름 변경 완료`);
          setEditingSecPreset(null);
          loadSecPresets();
        } catch (e) { setToast("이름 변경 실패"); }
        finally { setSecPresetLoading(false); }
      };
      const deleteSecPreset = async (preset) => {
        if (!window.confirm(`프리셋 "${preset.name}"을(를) 삭제하시겠습니까?`)) return;
        setSecPresetLoading(true);
        try {
          await api.delete(`/api/admin/section-presets/${preset.id}`);
          setToast(`🗑️ 프리셋 "${preset.name}" 삭제 완료`);
          loadSecPresets();
        } catch (e) { setToast("프리셋 삭제 실패"); }
        finally { setSecPresetLoading(false); }
      };

      return (
        <>
          {/* 프리셋 버튼 */}
          <div className="flex items-center gap-2 bg-teal-50 border border-teal-200 rounded-xl p-3">
            <span className="text-xs text-teal-700 font-medium flex-1">📋 섹션 프리셋</span>
            <button onClick={openSecPresetPanel}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-teal-500 text-white hover:bg-teal-600 transition">
              📋 프리셋 관리
            </button>
            <span className="text-[10px] text-gray-400">저장 → 여러 페이지에 적용</span>
          </div>

          {/* 프리셋 패널 */}
          {showSecPresetPanel && (
            <div className="border border-teal-200 rounded-xl bg-teal-50/50 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-teal-800">📋 섹션 프리셋</span>
                <button onClick={() => setShowSecPresetPanel(false)} className="text-gray-400 hover:text-gray-600 text-lg leading-none">&times;</button>
              </div>
              <div className="flex gap-2">
                <input value={secPresetName} onChange={e => setSecPresetName(e.target.value)}
                  placeholder="프리셋 이름 (예: 교환 안내 기본)" className="flex-1 rounded-lg border border-teal-300 px-3 py-1.5 text-xs focus:ring-2 focus:ring-teal-400 focus:outline-none" />
                <button onClick={saveSecPreset} disabled={secPresetLoading || !secPresetName.trim()}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-teal-600 text-white hover:bg-teal-700 transition disabled:opacity-50">
                  {secPresetLoading ? "⏳" : "💾 현재 내용 저장"}
                </button>
              </div>
              {secPresetLoading && secPresets.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-2">로딩 중...</p>
              ) : secPresets.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-2">저장된 프리셋이 없습니다</p>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {secPresets.map(pr => (
                    <div key={pr.id} className="flex items-center gap-2 bg-white border border-teal-200 rounded-lg p-2.5">
                      <div className="flex-1 min-w-0">
                        {editingSecPreset === pr.id ? (
                          <input autoFocus defaultValue={pr.name}
                            onBlur={e => renameSecPreset(pr, e.target.value)}
                            onKeyDown={e => { if (e.key === "Enter") renameSecPreset(pr, e.target.value); if (e.key === "Escape") setEditingSecPreset(null); }}
                            className="w-full rounded border border-teal-300 px-2 py-1 text-xs focus:ring-2 focus:ring-teal-400 focus:outline-none" />
                        ) : (
                          <>
                            <p className="text-xs font-semibold text-gray-700 truncate">{pr.name}</p>
                            <p className="text-[10px] text-gray-400 truncate">
                              {pr.section_json?.heading || "(제목 없음)"} · {pr.updated_at ? new Date(pr.updated_at).toLocaleDateString() : ""}
                            </p>
                          </>
                        )}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button onClick={() => applySecPreset(pr)} title="적용"
                          className="w-7 h-7 flex items-center justify-center rounded text-xs bg-green-100 text-green-700 hover:bg-green-200 transition">📥</button>
                        <button onClick={() => overwriteSecPreset(pr)} title="현재 내용으로 덮어쓰기"
                          className="w-7 h-7 flex items-center justify-center rounded text-xs bg-blue-100 text-blue-700 hover:bg-blue-200 transition">🔄</button>
                        <button onClick={() => setEditingSecPreset(pr.id)} title="이름 변경"
                          className="w-7 h-7 flex items-center justify-center rounded text-xs bg-gray-100 text-gray-600 hover:bg-gray-200 transition">✏️</button>
                        <button onClick={() => deleteSecPreset(pr)} title="삭제"
                          className="w-7 h-7 flex items-center justify-center rounded text-xs bg-red-100 text-red-600 hover:bg-red-200 transition">🗑️</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="bg-purple-50 border border-purple-200 rounded-lg px-3 py-2 text-xs text-purple-700 flex items-center gap-2">
            <span>📝</span>
            <span>대형 제목과 설명 텍스트 블록입니다. 섹션 구분이나 강조 문구에 사용하세요.</span>
          </div>
          <div>
            <label className="text-[10px] text-gray-500">대형 제목</label>
            <input value={sec.heading || ""} onChange={e => updateSection(i, { heading: e.target.value })}
              placeholder="예: 모든 것을 하나로. 모든 것을 간결하게." className={inp} />
          </div>
          <div>
            <label className="text-[10px] text-gray-500">소제목 (선택)</label>
            <input value={sec.subheading || ""} onChange={e => updateSection(i, { subheading: e.target.value })}
              placeholder="예: 효율의 정석, 컴팩트한 완성." className={inp} />
          </div>
          <div>
            <label className="text-[10px] text-gray-500">설명 (선택)</label>
            <textarea value={sec.description || ""} onChange={e => updateSection(i, { description: e.target.value })}
              rows={3} placeholder="자세한 설명을 입력하세요..." className={`${inp} resize-none`} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] text-gray-500">정렬</label>
              <div className="flex gap-1.5 mt-1">
                {[["center","가운데"],["left","왼쪽"]].map(([v,l]) => (
                  <button key={v} onClick={() => updateSection(i, { align: v })}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition ${(sec.align || "center") === v ? "border-purple-500 bg-purple-100 text-purple-700" : "border-gray-200 text-gray-500"}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[10px] text-gray-500">배경</label>
              <div className="flex gap-1.5 mt-1">
                {[["white","흰색"],["gray","회색"],["dark","다크"]].map(([v,l]) => (
                  <button key={v} onClick={() => updateSection(i, { bg_style: v })}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition ${(sec.bg_style || "white") === v ? "border-purple-500 bg-purple-100 text-purple-700" : "border-gray-200 text-gray-500"}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </>
      );
    }

    /* ══════════════════════════════════════════════════
       옵션 선택기 (프리미엄 스타일 구성기)
       ═══════════════════════════════════════════════════ */
    case "product_options": {
      const groups = sec.option_groups || [];
      const updateGroup = (gi, patch) => {
        const arr = [...groups]; arr[gi] = { ...arr[gi], ...patch }; updateSection(i, { option_groups: arr });
      };
      const updateOption = (gi, oi, patch) => {
        const arr = [...groups];
        const opts = [...(arr[gi].options || [])]; opts[oi] = { ...opts[oi], ...patch };
        arr[gi] = { ...arr[gi], options: opts };
        updateSection(i, { option_groups: arr });
      };
      return (
        <>
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2 text-xs text-indigo-700 flex items-center gap-2">
            <span>⚙️</span>
            <span>프리미엄 스타일 옵션 선택기입니다. 모델, 색상, 약정기간, 추가옵션 등을 사용자가 선택할 수 있습니다.</span>
          </div>
          <input value={sec.title || ""} onChange={e => updateSection(i, { title: e.target.value })}
            placeholder="섹션 제목 (예: 원하는 옵션을 선택하세요)" className={inp} />

          <div className="space-y-4">
            {groups.map((group, gi) => (
              <div key={gi} className="border border-indigo-200 rounded-xl p-4 bg-indigo-50/30 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="bg-indigo-600 text-white text-[10px] font-bold w-6 h-6 rounded flex items-center justify-center">{gi + 1}</span>
                    <span className="text-sm font-bold text-gray-700">{group.group_name || `옵션 그룹 ${gi + 1}`}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button disabled={gi === 0} onClick={() => {
                      const arr = [...groups]; [arr[gi - 1], arr[gi]] = [arr[gi], arr[gi - 1]];
                      updateSection(i, { option_groups: arr });
                    }} className="text-gray-400 hover:text-indigo-600 disabled:opacity-30 text-sm px-1" title="위로">▲</button>
                    <button disabled={gi === groups.length - 1} onClick={() => {
                      const arr = [...groups]; [arr[gi], arr[gi + 1]] = [arr[gi + 1], arr[gi]];
                      updateSection(i, { option_groups: arr });
                    }} className="text-gray-400 hover:text-indigo-600 disabled:opacity-30 text-sm px-1" title="아래로">▼</button>
                    <button onClick={() => updateSection(i, { option_groups: groups.filter((_, k) => k !== gi) })}
                      className="text-red-400 hover:text-red-600 text-sm ml-1">✕ 삭제</button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] text-gray-500">그룹명</label>
                    <input value={group.group_name || ""} onChange={e => updateGroup(gi, { group_name: e.target.value })}
                      placeholder="예: 모델, 색상, 약정기간" className="w-full rounded border px-2 py-1.5 text-sm" />
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500">선택 방식</label>
                    <select value={group.group_type || "card"} onChange={e => updateGroup(gi, { group_type: e.target.value })}
                      className="w-full rounded border px-2 py-1.5 text-sm">
                      <option value="card">카드형 (이미지+설명)</option>
                      <option value="button">버튼형 (텍스트)</option>
                      <option value="color">색상 선택</option>
                      <option value="checkbox">체크박스 (다중선택)</option>
                    </select>
                  </div>
                </div>

                {/* Options list */}
                <div className="space-y-2">
                  <label className="text-[10px] text-gray-500 font-semibold">선택지 목록</label>
                  {(group.options || []).map((opt, oi) => (
                    <div key={oi} className="border border-gray-200 rounded-lg p-3 bg-white space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-500">옵션 {oi + 1}</span>
                        <div className="flex items-center gap-1">
                          <button disabled={oi === 0} onClick={() => {
                            const opts = [...(group.options||[])]; [opts[oi-1], opts[oi]] = [opts[oi], opts[oi-1]];
                            updateGroup(gi, { options: opts });
                          }} className="text-gray-400 hover:text-indigo-600 disabled:opacity-30 text-[10px] px-0.5">▲</button>
                          <button disabled={oi === (group.options||[]).length - 1} onClick={() => {
                            const opts = [...(group.options||[])]; [opts[oi], opts[oi+1]] = [opts[oi+1], opts[oi]];
                            updateGroup(gi, { options: opts });
                          }} className="text-gray-400 hover:text-indigo-600 disabled:opacity-30 text-[10px] px-0.5">▼</button>
                          <button onClick={() => {
                            const opts = (group.options||[]).filter((_,k) => k !== oi);
                            updateGroup(gi, { options: opts });
                          }} className="text-red-400 text-xs ml-1">✕</button>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[10px] text-gray-400">이름 *</label>
                          <input value={opt.name || ""} onChange={e => updateOption(gi, oi, { name: e.target.value })}
                            placeholder="예: 기본형, 24개월, 팩스 KIT" className="w-full rounded border px-2 py-1 text-sm" />
                        </div>
                        <div>
                          <label className="text-[10px] text-gray-400">가격 변동 (원)</label>
                          <input type="number" value={opt.price_modifier ?? ""} onChange={e => updateOption(gi, oi, { price_modifier: e.target.value === "" ? "" : parseInt(e.target.value)||0 })}
                            className="w-full rounded border px-2 py-1 text-sm" />
                        </div>
                        <div className="col-span-2">
                          <label className="text-[10px] text-gray-400">설명</label>
                          <input value={opt.description || ""} onChange={e => updateOption(gi, oi, { description: e.target.value })}
                            placeholder="옵션 설명" className="w-full rounded border px-2 py-1 text-sm" />
                        </div>
                        {group.group_type === "card" && (
                          <div className="col-span-2">
                            <label className="text-[10px] text-gray-400">이미지</label>
                            <ItemImageUploader
                              imageUrl={opt.image_url || ""}
                              onUrlChange={(url) => updateOption(gi, oi, { image_url: url })}
                            />
                          </div>
                        )}
                        {group.group_type === "color" && (
                          <div>
                            <label className="text-[10px] text-gray-400">색상 코드</label>
                            <input value={opt.color_code || ""} onChange={e => updateOption(gi, oi, { color_code: e.target.value })}
                              placeholder="#000000" className="w-full rounded border px-2 py-1 text-sm" />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  <button onClick={() => {
                    const opts = [...(group.options||[]), { name: "", description: "", price_modifier: 0, image_url: "" }];
                    updateGroup(gi, { options: opts });
                  }} className="text-[12px] text-indigo-600 font-medium hover:underline">+ 선택지 추가</button>
                </div>

                {/* Quick-add presets for rental */}
                {(group.group_type === "button" || group.group_type === "checkbox") && (
                  <div className="border-t border-gray-200 pt-2">
                    <span className="text-[10px] text-gray-400">빠른 추가:</span>
                    <div className="flex gap-1 mt-1 flex-wrap">
                      {(group.group_name?.includes("기간") || group.group_name?.includes("약정")
                        ? ["12개월", "24개월", "36개월", "48개월", "60개월"]
                        : ["팩스 KIT", "추가트레이", "무선랜", "자동양면", "후처리기", "대용량급지"]
                      ).filter(p => !(group.options||[]).some(o => o.name === p)).map(preset => (
                        <button key={preset} onClick={() => {
                          updateGroup(gi, { options: [...(group.options||[]), { name: preset, description: "", price_modifier: 0, image_url: "" }] });
                        }} className="text-[10px] px-2 py-0.5 rounded-full border border-indigo-300 text-indigo-600 hover:bg-indigo-100 transition">
                          + {preset}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="flex gap-2 flex-wrap">
            {[
              { name: "모델", type: "card" },
              { name: "약정기간", type: "button" },
              { name: "색상", type: "color" },
              { name: "추가옵션", type: "checkbox" },
            ].map(preset => (
              <button key={preset.name} onClick={() => {
                updateSection(i, { option_groups: [...groups, {
                  group_name: preset.name, group_type: preset.type,
                  options: [{ name: "", description: "", price_modifier: 0, image_url: "" }]
                }] });
              }} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-indigo-100 text-indigo-700 hover:bg-indigo-200 transition">
                + {preset.name} 그룹
              </button>
            ))}
          </div>
        </>
      );
    }

    /* ══════════════════════════════════════════════════
       쇼케이스 (프리미엄 2단: 이미지 좌 + 옵션 우)
       ═══════════════════════════════════════════════════ */
    case "product_showcase": {
      const scImages = sec.images || [""];
      const scGroups = sec.option_groups || [];
      const scSubInfo = sec.sub_info || [];
      const updateScGroup = (gi, patch) => {
        const arr = [...scGroups]; arr[gi] = { ...arr[gi], ...patch }; updateSection(i, { option_groups: arr });
      };
      const updateScOption = (gi, oi, patch) => {
        const arr = [...scGroups]; const opts = [...(arr[gi].options || [])]; opts[oi] = { ...opts[oi], ...patch };
        arr[gi] = { ...arr[gi], options: opts }; updateSection(i, { option_groups: arr });
      };
      const multiplyScGroup = (gi) => {
        const factor = window.prompt("옵션 단가에 곱할 배수를 입력하세요", "1");
        if (factor == null) return;
        const arr = [...scGroups];
        arr[gi] = multiplyOptionGroupPrices(arr[gi], factor);
        updateSection(i, { option_groups: arr });
      };
      return (
        <>
          <div className="bg-sky-50 border border-sky-200 rounded-lg px-3 py-2 text-xs text-sky-700 flex items-center gap-2">
            <span>📱</span>
            <span>프리미엄 2단 쇼케이스: 왼쪽 이미지 갤러리 + 오른쪽 옵션 선택기</span>
          </div>
          <input value={sec.title || ""} onChange={e => updateSection(i, { title: e.target.value })}
            placeholder="제품명 (예: iPhone Air)" className={inp} />
          <input value={sec.price_label || ""} onChange={e => updateSection(i, { price_label: e.target.value })}
            placeholder="가격 라벨 (예: ₩1,590,000부터)" className={inp} />

          {/* 보조 정보 태그 */}
          <div>
            <label className="text-[10px] text-gray-500 font-semibold mb-1 block">보조 정보 (예: 무료 배송, 매장 픽업)</label>
            <div className="flex gap-1 flex-wrap">
              {scSubInfo.map((info, si) => (
                <span key={si} className="inline-flex items-center gap-1 bg-gray-100 rounded-lg px-2 py-1 text-xs">
                  <input value={info} onChange={e => { const arr = [...scSubInfo]; arr[si] = e.target.value; updateSection(i, { sub_info: arr }); }}
                    className="bg-transparent border-0 text-xs w-20 focus:outline-none" placeholder="정보" />
                  <button onClick={() => updateSection(i, { sub_info: scSubInfo.filter((_, k) => k !== si) })} className="text-red-400 text-[10px]">✕</button>
                </span>
              ))}
              <button onClick={() => updateSection(i, { sub_info: [...scSubInfo, ""] })}
                className="text-[10px] text-sky-600 hover:underline">+ 추가</button>
            </div>
          </div>

          {/* 이미지 갤러리 */}
          <div className="space-y-2">
            <label className="text-[10px] text-gray-500 font-semibold">🖼️ 이미지 갤러리 (슬라이드)</label>
            {scImages.map((img, ii) => (
              <div key={ii} className="flex items-center gap-2">
                <ItemImageUploader imageUrl={img || ""} onUrlChange={(url) => { const arr = [...scImages]; arr[ii] = url; updateSection(i, { images: arr }); }} />
                <button onClick={() => updateSection(i, { images: scImages.filter((_, k) => k !== ii) })} className="text-red-400 text-xs shrink-0">✕</button>
              </div>
            ))}
            <button onClick={() => updateSection(i, { images: [...scImages, ""] })}
              className="text-[11px] text-sky-600 hover:underline">+ 이미지 추가</button>
          </div>

          {/* 옵션 그룹 */}
          <div className="space-y-3">
            <label className="text-[10px] text-gray-500 font-semibold">⚙️ 옵션 그룹 (오른쪽 패널)</label>
            <div className="max-h-[500px] overflow-y-auto space-y-3 pr-1">
            {scGroups.map((group, gi) => (
              <div key={gi} className="border border-sky-200 rounded-xl p-3 bg-sky-50/30 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-gray-700">{group.group_name || `그룹 ${gi + 1}`}</span>
                  <button onClick={() => updateSection(i, { option_groups: scGroups.filter((_, k) => k !== gi) })} className="text-red-400 text-xs">✕</button>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <input value={group.group_name || ""} onChange={e => updateScGroup(gi, { group_name: e.target.value })}
                    placeholder="그룹명 (예: 색상)" className="rounded border px-2 py-1 text-sm" />
                  <select value={group.group_type || "swatch"} onChange={e => updateScGroup(gi, { group_type: e.target.value })}
                    className="rounded border px-2 py-1 text-sm">
                    <option value="swatch">컬러 스와치</option>
                    <option value="list">리스트 (이름+가격)</option>
                    <option value="button">버튼</option>
                  </select>
                  <select value={getOptionGroupPriceType(group)} onChange={e => updateScGroup(gi, { price_type: e.target.value })}
                    className="rounded border px-2 py-1 text-sm">
                    <option value="per_unit">단가×수량</option>
                    <option value="fixed">고정금액</option>
                  </select>
                </div>
                <div className="rounded-lg border border-orange-100 bg-orange-50 p-2 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[10px] font-semibold text-orange-700">
                      {getOptionGroupPriceType(group) === "per_unit" ? "매수당 단가로 계산됩니다." : "선택한 옵션 금액이 월 기본료로 들어갑니다."}
                    </span>
                    <button onClick={() => multiplyScGroup(gi)}
                      className="shrink-0 px-3 py-1 rounded-lg text-[10px] font-semibold bg-orange-100 text-orange-700 hover:bg-orange-200 transition">
                      일괄 곱하기
                    </button>
                  </div>
                  {(group.group_type === "button" || group.group_type === "list") && getOptionGroupPriceType(group) === "per_unit" && (
                    <label className="block">
                      <span className="text-[10px] font-semibold text-orange-700">매수당 단가(원/매)</span>
                      <input
                        type="number"
                        value={(group.options || []).find(o => Number(o?.unit_price) > 0)?.unit_price ?? ""}
                        onChange={e => updateScGroup(gi, updateOptionUnitPrices(group, e.target.value))}
                        className="mt-1 w-full rounded border border-orange-200 px-2 py-1 text-sm text-right text-orange-700 focus:outline-none"
                        placeholder="예: 흑백 10 / 칼라 100"
                      />
                    </label>
                  )}
                </div>
                <div className="text-[10px] text-gray-400 font-semibold">설명 문구</div>
                <input value={group.group_description || ""} onChange={e => updateScGroup(gi, { group_description: e.target.value })}
                  placeholder="맘에 드는 색상을 선택하세요." className="w-full rounded border px-2 py-1 text-xs" />
                {(group.options || []).map((opt, oi) => (
                  <div key={oi} className="flex items-center gap-2 bg-white rounded-lg px-2 py-1 border border-gray-200">
                    {group.group_type === "swatch" && (
                      <input type="color" value={opt.color_hex || "#000000"} onChange={e => updateScOption(gi, oi, { color_hex: e.target.value })}
                        className="w-6 h-6 rounded-full border-0 cursor-pointer" />
                    )}
                    <input value={opt.name || ""} onChange={e => {
                      const next = { ...opt, name: e.target.value };
                      if (Number(next.unit_price) > 0) next.price_label = formatOptionPriceLabel(group, next, next.unit_price);
                      updateScOption(gi, oi, next);
                    }} placeholder="이름" className="flex-1 border-0 text-xs focus:outline-none" />
                    {group.group_type === "list" && (
                      <input value={opt.price_label || ""} onChange={e => updateScOption(gi, oi, { price_label: e.target.value })} placeholder="₩1,590,000부터" className="w-32 border-0 text-xs text-right focus:outline-none text-gray-500" />
                    )}
                    {(group.group_type === "button" || group.group_type === "list") && (
                      <input type="number" value={opt.unit_price ?? ""} onChange={e => {
                        const value = e.target.value === "" ? null : Number(e.target.value);
                        const next = { ...opt, unit_price: value };
                        if (value > 0) next.price_label = formatOptionPriceLabel(group, next, value);
                        const arr = [...scGroups];
                        const opts = [...(arr[gi].options || [])];
                        opts[oi] = next;
                        arr[gi] = { ...arr[gi], price_type: getOptionGroupPriceType(arr[gi]), options: opts };
                        updateSection(i, { option_groups: arr });
                      }} placeholder={getOptionGroupPriceType(group) === "fixed" ? "월 기본료" : "단가"} className="w-24 border rounded px-1 py-0.5 text-[10px] text-right focus:outline-none text-orange-600" />
                    )}
                    <button onClick={() => updateScGroup(gi, { options: (group.options || []).filter((_, k) => k !== oi) })} className="text-red-400 text-[10px]">✕</button>
                  </div>
                ))}
                <button onClick={() => updateScGroup(gi, { options: [...(group.options || []), { name: "", color_hex: "#cccccc", price_label: "", image_url: "", unit_price: null }] })}
                  className="text-[10px] text-sky-600 hover:underline">+ 선택지</button>
              </div>
            ))}
            </div>
            <div className="flex gap-1 flex-wrap">
              {[{ n: "🎨 컬러스와치", gn: "색상", t: "swatch" }, { n: "📋 리스트", gn: "옵션", t: "list" }, { n: "💰 가격", gn: "가격", t: "button" }].map(p => (
                <button key={p.n} onClick={() => updateSection(i, { option_groups: [...scGroups, { group_name: p.gn, group_type: p.t, group_description: "", options: [{ name: "", color_hex: "#000", price_label: "", unit_price: null }] }] })}
                  className="px-2 py-1 rounded-lg text-[10px] font-medium bg-sky-100 text-sky-700 hover:bg-sky-200">{p.n}</button>
              ))}
            </div>
            {onGenerateRental && (
              <button onClick={() => onGenerateRental(i)}
                className="w-full py-2 rounded-lg text-xs font-semibold bg-purple-100 text-purple-700 hover:bg-purple-200 transition">📊 이 옵션으로 렌탈표 자동생성</button>
            )}
          </div>
        </>
      );
    }

    /* ══════════════════════════════════════════════════
       그리드 레이아웃 (4분면 + 드래그 드롭 + 멀티이미지 + 레이어)
       ═══════════════════════════════════════════════════ */
    case "product_grid":
      return (
        <ProductGridEditor
          section={sec}
          onChange={(updated) => updateSection(i, updated)}
        />
      );

    default:
      return (
        <div className="text-sm text-gray-400">
          <p>알 수 없는 섹션 타입: {sec.type}</p>
          <textarea value={JSON.stringify(sec, null, 2)} onChange={e => {
            try { updateSection(i, JSON.parse(e.target.value)); } catch { /* ignore parse error */ }
          }} rows={8} className="w-full font-mono text-xs border rounded p-2 mt-2" />
        </div>
      );
  }
}

/* ── Presentation keyboard handler ── */
function PresentationKeyHandler({ onExit }) {
  React.useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onExit(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onExit]);
  return null;
}


/* ══════════════════════════════════════════════════════════════
   Preview Renderer — Premium-style live preview with drag/resize
   (Used for Presentation mode)
   ══════════════════════════════════════════════════════════════ */

/* ── Resizable Image ── */
function ResizableImage({ src, alt, className, maxH = 224, onResize }) {
  const [height, setHeight] = useState(maxH);
  const [dragging, setDragging] = useState(false);
  const startRef = useRef({ y: 0, h: 0 });

  const onMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
    startRef.current = { y: e.clientY, h: height };
    const onMove = (ev) => {
      const delta = ev.clientY - startRef.current.y;
      const next = Math.max(80, Math.min(600, startRef.current.h + delta));
      setHeight(next);
    };
    const onUp = () => {
      setDragging(false);
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      onResize?.(height);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  return (
    <div className="relative inline-block group">
      <img
        src={src}
        alt={alt || ""}
        className={`object-contain rounded-xl ${className || ""}`}
        style={{ maxHeight: height, transition: dragging ? "none" : "max-height 0.2s" }}
        onError={(e) => { e.target.style.display = "none"; }}
      />
      {/* Resize handle */}
      <div
        onMouseDown={onMouseDown}
        className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-8 h-3 bg-blue-500 rounded-full opacity-0 group-hover:opacity-80 cursor-ns-resize flex items-center justify-center transition-opacity"
        title="드래그하여 크기 조절"
      >
        <div className="w-4 h-[2px] bg-white rounded" />
      </div>
      {/* Corner resize handles */}
      <div
        onMouseDown={onMouseDown}
        className="absolute bottom-0 right-0 w-4 h-4 opacity-0 group-hover:opacity-80 cursor-nwse-resize transition-opacity"
      >
        <svg viewBox="0 0 16 16" className="w-full h-full text-blue-500">
          <path d="M14 2v12H2" fill="none" stroke="currentColor" strokeWidth="2" />
        </svg>
      </div>
      {dragging && (
        <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-black/80 text-white text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap">
          {Math.round(height)}px
        </div>
      )}
    </div>
  );
}

/* ── Section spacing control ── */
function SpacingControl({ value, onChange }) {
  return (
    <div className="flex items-center justify-center gap-1 py-0.5 opacity-0 hover:opacity-100 transition-opacity group/spacing">
      <div className="flex items-center gap-0.5 bg-gray-100 rounded-full px-1.5 py-0.5">
        <button
          onClick={(e) => { e.stopPropagation(); onChange(Math.max(0, (value || 8) - 2)); }}
          className="w-4 h-4 rounded-full text-[10px] text-gray-500 hover:bg-gray-200 flex items-center justify-center"
          title="간격 줄이기"
        >−</button>
        <span className="text-[9px] text-gray-400 w-6 text-center">{value || 8}</span>
        <button
          onClick={(e) => { e.stopPropagation(); onChange((value || 8) + 2); }}
          className="w-4 h-4 rounded-full text-[10px] text-gray-500 hover:bg-gray-200 flex items-center justify-center"
          title="간격 늘리기"
        >+</button>
      </div>
    </div>
  );
}

function PreviewRenderer({ form, onSectionClick, onUpdateSections, onHeroImageDelete }) {
  const sections = Array.isArray(form.sections_json) ? form.sections_json : [];
  const heroVisual = form.specs_json?._hero_visual || {};
  const theme = form.theme || "minimal";
  const [dragSec, setDragSec] = useState(null);
  const [dragOverSec, setDragOverSec] = useState(null);
  const [selectedSec, setSelectedSec] = useState(null);

  /* ── Theme style maps ── */
  const THEME_STYLES = {
    minimal:   { bg: "bg-white",    heroBg: "bg-gradient-to-b from-gray-50 to-white", text: "text-gray-900", sub: "text-gray-500", accent: "text-blue-600", brandColor: "text-blue-600",  priceBg: "", ctaBg: "bg-gray-900 hover:bg-gray-800", ctaText: "text-white" },
    corporate: { bg: "bg-white",    heroBg: "bg-gradient-to-b from-blue-50 to-white",  text: "text-gray-900", sub: "text-gray-600", accent: "text-blue-700", brandColor: "text-blue-700",  priceBg: "bg-blue-50", ctaBg: "bg-blue-700 hover:bg-blue-800", ctaText: "text-white" },
    bold:      { bg: "bg-white",    heroBg: "bg-gradient-to-b from-red-50 to-white",   text: "text-gray-900", sub: "text-gray-600", accent: "text-red-600",  brandColor: "text-red-600",   priceBg: "bg-red-50",  ctaBg: "bg-red-600 hover:bg-red-700", ctaText: "text-white" },
    warm:      { bg: "bg-amber-50/30", heroBg: "bg-gradient-to-b from-amber-50 to-white", text: "text-gray-800", sub: "text-amber-700", accent: "text-amber-600", brandColor: "text-amber-700", priceBg: "bg-amber-50", ctaBg: "bg-amber-600 hover:bg-amber-700", ctaText: "text-white" },
    tech:      { bg: "bg-gray-950", heroBg: "bg-gradient-to-b from-gray-900 to-gray-950", text: "text-white",   sub: "text-gray-400", accent: "text-cyan-400", brandColor: "text-cyan-400",  priceBg: "bg-gray-800", ctaBg: "bg-cyan-500 hover:bg-cyan-400", ctaText: "text-gray-900" },
    catalog:   { bg: "bg-gray-50",  heroBg: "bg-white",                                 text: "text-gray-900", sub: "text-gray-600", accent: "text-indigo-600", brandColor: "text-indigo-600", priceBg: "bg-gray-100", ctaBg: "bg-indigo-600 hover:bg-indigo-700", ctaText: "text-white" },
    luxury:    { bg: "bg-stone-50", heroBg: "bg-gradient-to-b from-stone-100 to-white", text: "text-stone-900", sub: "text-stone-500", accent: "text-amber-700", brandColor: "text-amber-700", priceBg: "bg-stone-100", ctaBg: "bg-stone-900 hover:bg-stone-800", ctaText: "text-amber-200" },
  };
  const ts = THEME_STYLES[theme] || THEME_STYLES.minimal;

  const handleDragEnd = () => {
    if (dragSec !== null && dragOverSec !== null && dragSec !== dragOverSec) {
      const arr = [...sections];
      const [item] = arr.splice(dragSec, 1);
      const to = dragOverSec > dragSec ? dragOverSec - 1 : dragOverSec;
      arr.splice(to, 0, item);
      onUpdateSections?.(arr);
    }
    setDragSec(null);
    setDragOverSec(null);
  };

  const updateSpacing = (idx, py) => {
    const arr = [...sections];
    arr[idx] = { ...arr[idx], _spacing: py };
    onUpdateSections?.(arr);
  };

  return (
    <div data-preview-renderer className={`font-sans ${ts.text} ${ts.bg} relative transition-colors duration-300`}>
      {/* ── Hero ── */}
      <div className={`${ts.heroBg} px-6 py-10 text-center cursor-pointer hover:ring-2 hover:ring-blue-300 hover:ring-inset transition-all`}
        onClick={() => { setSelectedSec(null); onSectionClick?.('hero'); }} title="클릭하여 Hero 편집으로 이동">
        {form.brand && (
          <p className={`text-xs font-semibold ${ts.brandColor} tracking-widest uppercase mb-2`}>{form.brand}</p>
        )}
        <h1 className={`text-2xl sm:text-3xl font-bold ${ts.text} leading-tight`}>
          {form.model_name || heroVisual.headline || form.title || "페이지 제목"}
        </h1>
        {(heroVisual.description || form.subtitle) && (
          <p className={`text-base ${ts.sub} mt-2 max-w-lg mx-auto`}>{heroVisual.description || form.subtitle}</p>
        )}
        {form.hero_image && (
          <div className="mt-6 flex justify-center relative group">
            <ResizableImage src={form.hero_image} alt={form.title} maxH={320} />
            <button onClick={(e) => { e.stopPropagation(); onHeroImageDelete?.(); }}
              className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 text-xs opacity-0 group-hover:opacity-100 transition flex items-center justify-center hover:bg-red-600" title="대표 이미지 삭제">✕</button>
          </div>
        )}
        {/* Price */}
        {!heroVisual.hide_price && (
        <div className={`mt-4 flex items-center justify-center gap-6 ${ts.priceBg ? ts.priceBg + " rounded-xl py-3 px-4 inline-flex" : ""}`}>
          {!!form.price && (
            <div>
              <span className={`text-[11px] ${ts.sub}`}>판매가</span>
              <p className={`text-xl font-bold ${theme === "tech" ? "text-cyan-400" : "text-red-600"}`}>{form.price.toLocaleString()}원</p>
            </div>
          )}
          {!!form.rental_price && (
            <div>
              <span className={`text-[11px] ${ts.sub}`}>렌탈</span>
              <p className={`text-xl font-bold ${theme === "tech" ? "text-purple-400" : "text-purple-600"}`}>월 {form.rental_price.toLocaleString()}원</p>
            </div>
          )}
        </div>
        )}
        {/* CTA Buttons */}
        {!heroVisual.hide_cta && (
        <div className="mt-4 flex items-center justify-center gap-3">
          {heroVisual.cta_text ? (
            <span className={`inline-block px-6 py-2.5 ${ts.ctaBg} ${ts.ctaText} rounded-full text-sm font-semibold cursor-pointer transition`}>
              {heroVisual.cta_text}
            </span>
          ) : (
            <span className="inline-block px-5 py-2 bg-blue-600 text-white rounded-full text-sm font-semibold cursor-pointer hover:bg-blue-700 transition">
              📞 견적문의
            </span>
          )}
        </div>
        )}
      </div>

      {/* ── Sections (draggable) ── */}
      {sections.map((sec, i) => (
        <React.Fragment key={i}>
          {/* Drop indicator line */}
          {dragOverSec === i && dragSec !== i && (
            <div className="h-1 bg-blue-500 rounded-full mx-4 transition-all" />
          )}
          <div
            draggable
            onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; setDragSec(i); setSelectedSec(null); }}
            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOverSec(i); }}
            onDragEnd={handleDragEnd}
            onClick={(e) => { e.stopPropagation(); setSelectedSec(i); onSectionClick?.(i); }}
            title="클릭하여 편집 · 드래그하여 순서 변경"
            className={`border-t border-gray-100 transition-all relative group/sec
              ${dragSec === i ? "opacity-40 scale-[0.98]" : ""}
              ${selectedSec === i ? "ring-2 ring-blue-500 ring-inset bg-blue-50/30" : "hover:ring-2 hover:ring-blue-300 hover:ring-inset"}
              cursor-grab active:cursor-grabbing`}
            style={{ paddingTop: `${(sec._spacing || 8) * 4}px`, paddingBottom: `${(sec._spacing || 8) * 4}px` }}
          >
            {/* Drag handle overlay */}
            <div className="absolute top-1 left-1/2 -translate-x-1/2 z-10 opacity-0 group-hover/sec:opacity-100 transition-opacity">
              <div className="bg-white/90 backdrop-blur-sm shadow-sm border border-gray-200 rounded-full px-2 py-0.5 flex items-center gap-1.5 text-[10px] text-gray-500">
                <span className="cursor-grab">☰</span>
                <span>{i + 1}</span>
              </div>
            </div>
            <SectionErrorBoundary key={`eb-${i}`}>
              <PreviewSection sec={sec} index={i} />
            </SectionErrorBoundary>
          </div>
          {/* Spacing control between sections */}
          <SpacingControl value={sec._spacing || 8} onChange={(v) => updateSpacing(i, v)} />
        </React.Fragment>
      ))}

      {/* ── No sections placeholder ── */}
      {sections.length === 0 && (
        <div className="py-12 text-center text-gray-300 text-sm">
          콘텐츠 섹션을 추가하면 여기에 미리보기가 표시됩니다
        </div>
      )}
    </div>
  );
}


/* ── Product Options Preview (separate component for hook compliance) ── */
function ProductOptionsPreview({ sec: rawSec }) {
  const sec = sanitizeSec(rawSec) || rawSec;
  const [selectedOpts, setSelectedOpts] = useState({});
  const groups = sec.option_groups || [];
  return (
    <div className="px-6 py-8">
      {sec.title && <h2 className="text-xl font-bold text-gray-900 mb-6">{sec.title}</h2>}
      <div className="space-y-6">
        {groups.map((group, gi) => (
          <div key={gi}>
            <h3 className="text-base font-bold text-gray-900 mb-1">
              {group.group_name}. <span className="font-normal text-gray-500 text-sm">
                {group.group_type === "checkbox" ? "원하는 항목을 선택하세요." : `원하는 ${group.group_name}을 선택하세요.`}
              </span>
            </h3>
            {group.group_type === "card" && (
              <div className="space-y-2 mt-3">
                {(group.options || []).map((opt, oi) => (
                  <div key={oi} onClick={() => setSelectedOpts(prev => ({...prev, [gi]: oi}))}
                    className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition ${selectedOpts[gi] === oi ? "border-blue-600 bg-blue-50/50" : "border-gray-200 hover:border-gray-300"}`}>
                    {opt.image_url && <img src={opt.image_url} alt="" className="w-12 h-12 object-contain shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-gray-900">{opt.name || "옵션"}</div>
                      {opt.description && <div className="text-xs text-gray-500 mt-0.5">{opt.description}</div>}
                    </div>
                    {opt.price_modifier !== 0 && (
                      <div className="text-sm font-semibold text-gray-900 shrink-0">
                        {opt.price_modifier > 0 ? "+" : ""}{(opt.price_modifier || 0).toLocaleString()}원
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            {group.group_type === "button" && (
              <div className="flex flex-wrap gap-2 mt-3">
                {(group.options || []).map((opt, oi) => (
                  <button key={oi} onClick={() => setSelectedOpts(prev => ({...prev, [gi]: oi}))}
                    className={`px-4 py-2.5 rounded-xl border-2 text-sm font-medium transition ${selectedOpts[gi] === oi ? "border-blue-600 bg-blue-600 text-white" : "border-gray-200 text-gray-700 hover:border-gray-300"}`}>
                    {opt.name}
                    {opt.price_modifier !== 0 && <span className="ml-1 text-xs opacity-80">({opt.price_modifier > 0 ? "+" : ""}{(opt.price_modifier||0).toLocaleString()})</span>}
                  </button>
                ))}
              </div>
            )}
            {group.group_type === "color" && (
              <div className="flex gap-3 mt-3">
                {(group.options || []).map((opt, oi) => (
                  <button key={oi} onClick={() => setSelectedOpts(prev => ({...prev, [gi]: oi}))}
                    className="flex flex-col items-center gap-1">
                    <div className={`w-8 h-8 rounded-full border-2 transition ${selectedOpts[gi] === oi ? "border-blue-600 ring-2 ring-blue-200" : "border-gray-300"}`}
                      style={{ backgroundColor: opt.color_code || "#666" }} />
                    <span className="text-[10px] text-gray-500">{opt.name}</span>
                  </button>
                ))}
              </div>
            )}
            {group.group_type === "checkbox" && (
              <div className="space-y-2 mt-3">
                {(group.options || []).map((opt, oi) => (
                  <label key={oi} className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 hover:bg-gray-50 cursor-pointer transition">
                    <input type="checkbox" className="w-4 h-4 text-blue-600 rounded border-gray-300" />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">{opt.name}</div>
                      {opt.description && <div className="text-xs text-gray-500">{opt.description}</div>}
                    </div>
                    {opt.price_modifier !== 0 && (
                      <span className="text-sm font-semibold text-gray-700">+{(opt.price_modifier||0).toLocaleString()}원</span>
                    )}
                  </label>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Showcase Preview (프리미엄 2단: image gallery + options) ── */
function ProductShowcasePreview({ sec: rawSec }) {
  const sec = sanitizeSec(rawSec) || rawSec;
  const images = (sec.images || []).filter(u => u && typeof u === "string" && u.trim());
  const groups = sec.option_groups || [];
  const [activeImg, setActiveImg] = useState(0);
  const [selectedOpts, setSelectedOpts] = useState({});

  // 예상 가격 계산: 선택된 옵션의 unit_price 합산
  const estimatedPrice = React.useMemo(() => {
    let total = 0;
    let hasPrice = false;
    for (const [gi, oi] of Object.entries(selectedOpts)) {
      const group = groups[gi];
      if (!group) continue;
      const opt = (group.options || [])[oi];
      if (!opt) continue;
      const up = Number(opt.unit_price) || 0;
      const pm = Number(opt.price_modifier) || 0;
      if (up > 0) {
        total += getOptionGroupPriceType(group) === "per_unit" ? up * parseOptionQuantity(opt.name) : up;
        hasPrice = true;
      }
      if (pm !== 0) { total += pm; hasPrice = true; }
    }
    return hasPrice ? total : null;
  }, [selectedOpts, groups]);
  const allSelected = groups.length > 0 && Object.keys(selectedOpts).length === groups.length;

  return (
    <div className="px-4 py-6">
      {/* Top bar */}
      {(sec.title || sec.price_label) && (
        <div className="flex items-center justify-between mb-1 px-2">
          <h2 className="text-base font-semibold text-gray-900">{sec.title}</h2>
          {sec.price_label && <span className="text-sm text-gray-500">{sec.price_label}</span>}
        </div>
      )}
      {/* Sub info */}
      {(sec.sub_info || []).length > 0 && (
        <div className="flex items-center justify-end gap-4 mb-4 px-2">
          {sec.sub_info.map((info, si) => (
            <span key={si} className="text-xs text-gray-500 flex items-center gap-1">
              {si === 0 ? "🚚" : "🏪"} {info}
            </span>
          ))}
        </div>
      )}
      {/* 2-column layout */}
      <div className="flex gap-6">
        {/* LEFT: Image carousel */}
        <div className="flex-[3] min-w-0">
          <div className="bg-gray-50 rounded-2xl aspect-square flex items-center justify-center overflow-hidden relative">
            {images.length > 0 ? (
              <img src={images[activeImg] || images[0]} alt="" className="max-w-full max-h-full object-contain p-4" />
            ) : (
              <span className="text-gray-300 text-sm">이미지 없음</span>
            )}
            {images.length > 1 && (
              <button onClick={() => setActiveImg((activeImg + 1) % images.length)}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/80 rounded-full shadow flex items-center justify-center text-gray-400 hover:text-gray-600">
                ›
              </button>
            )}
          </div>
          {/* Dots */}
          {images.length > 1 && (
            <div className="flex justify-center gap-1.5 mt-3">
              {images.map((_, ii) => (
                <button key={ii} onClick={() => setActiveImg(ii)}
                  className={`w-2 h-2 rounded-full transition ${ii === activeImg ? "bg-gray-800" : "bg-gray-300"}`} />
              ))}
            </div>
          )}
        </div>
        {/* RIGHT: Options — scroll when exceeding image height */}
        <div className="flex-[2] min-w-0 space-y-6 max-h-[500px] overflow-y-auto pr-1">
          {groups.map((group, gi) => (
            <div key={gi}>
              <h3 className="text-sm font-bold text-gray-900">
                {group.group_name}. <span className="font-normal text-gray-500 text-xs">{group.group_description || `맘에 드는 ${group.group_name}을 선택하세요.`}</span>
              </h3>
              <div className="mt-1 mb-1"><span className="text-xs font-semibold text-gray-700">{group.group_name}</span></div>
              {/* swatch */}
              {group.group_type === "swatch" && (
                <div className="flex gap-2 mt-2">
                  {(group.options || []).map((opt, oi) => (
                    <button key={oi} onClick={() => setSelectedOpts(p => ({ ...p, [gi]: oi }))}
                      className={`w-7 h-7 rounded-full border-2 transition ${selectedOpts[gi] === oi ? "border-blue-600 ring-2 ring-blue-200" : "border-gray-300"}`}
                      style={{ backgroundColor: opt.color_hex || "#ccc" }} title={opt.name} />
                  ))}
                </div>
              )}
              {/* list */}
              {group.group_type === "list" && (
                <div className="space-y-2 mt-2">
                  {(group.options || []).map((opt, oi) => (
                    <div key={oi} onClick={() => setSelectedOpts(p => ({ ...p, [gi]: oi }))}
                      className={`flex items-center justify-between px-4 py-3 rounded-xl border-2 cursor-pointer transition ${selectedOpts[gi] === oi ? "border-blue-600" : "border-gray-200 hover:border-gray-300"}`}>
                      <span className="text-sm text-gray-900">{opt.name}<sup className="text-[10px] text-gray-400">1</sup></span>
                      <span className="text-xs text-gray-500">{opt.price_label}</span>
                    </div>
                  ))}
                </div>
              )}
              {/* button */}
              {group.group_type === "button" && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {(group.options || []).map((opt, oi) => (
                    <button key={oi} onClick={() => setSelectedOpts(p => ({ ...p, [gi]: oi }))}
                      className={`px-3 py-2 rounded-xl border-2 text-xs font-medium transition ${selectedOpts[gi] === oi ? "border-blue-600 bg-blue-600 text-white" : "border-gray-200 text-gray-700 hover:border-gray-300"}`}>
                      {opt.name}
                    </button>
                  ))}
                </div>
              )}
              {/* fallback: any other group_type → render as list */}
              {!["swatch", "list", "button"].includes(group.group_type) && (
                <div className="space-y-2 mt-2">
                  {(group.options || []).map((opt, oi) => (
                    <div key={oi} onClick={() => setSelectedOpts(p => ({ ...p, [gi]: oi }))}
                      className={`flex items-center justify-between px-4 py-3 rounded-xl border-2 cursor-pointer transition ${selectedOpts[gi] === oi ? "border-blue-600" : "border-gray-200 hover:border-gray-300"}`}>
                      <div className="flex-1 min-w-0">
                        <span className="text-sm text-gray-900">{opt.name || "옵션"}</span>
                        {opt.description && <div className="text-xs text-gray-500 mt-0.5">{opt.description}</div>}
                      </div>
                      <span className="text-xs text-gray-500">{opt.price_label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          {/* 예상 가격 표시 */}
          {allSelected && estimatedPrice != null && (
            <div className="bg-blue-50 rounded-xl p-3 text-center">
              <div className="text-xs text-gray-500">예상 월 렌탈료</div>
              <div className="text-xl font-bold text-blue-700">월 {estimatedPrice.toLocaleString()}원</div>
              <div className="text-[10px] text-gray-400 mt-0.5">VAT 별도 · 정확한 견적은 상담 문의</div>
            </div>
          )}
          {!allSelected && groups.length > 0 && (
            <div className="text-center text-xs text-gray-400 py-1">모든 옵션을 선택하면 예상 가격이 표시됩니다</div>
          )}
          {/* CTA */}
          {sec.cta_text !== "__hide__" && (
          <div className="pt-4 border-t border-gray-100 mt-4">
            <button className="w-full py-3 rounded-xl bg-blue-600 text-white font-semibold text-sm hover:bg-blue-700 transition">
              {sec.cta_text || "📞 견적문의"}
            </button>
          </div>
          )}
        </div>
      </div>
    </div>
  );
}


/* ── Grid block preview (for product_grid sections in PreviewSection) ── */
function GridPreviewBlock({ block }) {
  const [activeImg, setActiveImg] = React.useState(0);
  switch (block.type) {
    case "image": {
      const imgs = block.images || (block.src ? [{ src: block.src }] : []);
      if (imgs.length === 0) return <div className="bg-gray-100 aspect-square rounded-xl flex items-center justify-center text-gray-300 text-sm">이미지 없음</div>;
      return (
        <div style={{ zIndex: block.zIndex || 0, width: `${block.widthPct || 100}%`, margin: block.align === "center" ? "0 auto" : block.align === "right" ? "0 0 0 auto" : undefined }}>
          <img src={imgs[activeImg]?.src} alt="" className="w-full object-contain rounded-xl" onError={e => { e.target.style.display = "none"; }} />
          {imgs.length > 1 && (
            <div className="flex gap-1 mt-2 justify-center">
              {imgs.map((img, ii) => (
                <button key={ii} onClick={() => setActiveImg(ii)}
                  className={`w-10 h-10 rounded-lg overflow-hidden border-2 transition ${activeImg === ii ? "border-blue-500" : "border-gray-200"}`}>
                  <img src={img.src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      );
    }
    case "title":
      return <h2 className={`text-xl font-bold text-gray-900 ${block.align === "center" ? "text-center" : block.align === "right" ? "text-right" : ""}`}>{block.content || "제목"}</h2>;
    case "text":
      return <p className={`text-sm text-gray-600 leading-relaxed ${block.align === "center" ? "text-center" : block.align === "right" ? "text-right" : ""}`}>{block.content || "텍스트"}</p>;
    case "price":
      return (
        <div className={block.priceStyle === "large" ? "text-center" : ""}>
          {block.priceLabel && <span className="text-xs text-gray-400">{block.priceLabel}</span>}
          <p className={`font-bold ${block.priceStyle === "large" ? "text-2xl" : block.priceStyle === "medium" ? "text-lg" : "text-base"}`}>
            {(block.amount || 0).toLocaleString()}원
          </p>
        </div>
      );
    case "options":
      return <OptionCategoryPreview categories={block.categories || []} />;
    case "buttons":
      return (
        <div className={block.layout === "row" ? "flex gap-2" : block.layout === "grid2" ? "grid grid-cols-2 gap-2" : "space-y-2"}>
          {(block.buttons || []).map((btn, bi) => (
            <button key={bi} className={`px-4 py-3 rounded-xl text-sm font-bold w-full transition ${GRID_BUTTON_PRESETS[btn.action]?.color || "bg-gray-200 text-gray-800"}`}>
              {btn.label || "버튼"}
            </button>
          ))}
        </div>
      );
    case "spacer":
      return <div style={{ height: block.height || 24 }} />;
    default:
      return <div className="text-xs text-gray-400">[{block.type}]</div>;
  }
}

/* ── Deep sanitize section data to prevent React error #31 (objects as children) ── */
function sanitizeSec(obj, depth = 0) {
  if (obj == null) return obj;
  if (depth > 10) return safeStr(obj); // prevent infinite recursion
  if (Array.isArray(obj)) return obj.map(item => sanitizeSec(item, depth + 1));
  if (typeof obj !== "object") return obj;
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    // Preserve structured data that editors handle
    if (k === "canvas_json" || k === "_spacing") {
      out[k] = v;
      continue;
    }
    // col_values, custom_columns, highlight_spec: sanitize values but keep structure
    if (k === "col_values" && v && typeof v === "object" && !Array.isArray(v)) {
      out[k] = {};
      for (const [ck, cv] of Object.entries(v)) {
        out[k][ck] = safeStr(cv);
      }
      continue;
    }
    if (k === "custom_columns" && Array.isArray(v)) {
      out[k] = v.map(col => typeof col === "object" && col ? { key: safeStr(col.key), label: safeStr(col.label) } : col);
      continue;
    }
    if (k === "highlight_spec" && v && typeof v === "object") {
      out[k] = { value: safeStr(v.value), label: safeStr(v.label) };
      continue;
    }
    // sub_info: convert to string array (AI may return {name,value} objects)
    if (k === "sub_info" && Array.isArray(v)) {
      out[k] = v.map(si => {
        if (typeof si !== "object" || !si) return safeStr(si);
        if (si.name && si.value) return `${si.name} ${si.value}`;
        return si.text || si.label || si.value || si.name || safeStr(si);
      });
      continue;
    }
    // features: convert to string array
    if (k === "features" && Array.isArray(v)) {
      out[k] = v.map(f => typeof f === "object" && f ? (f.title || f.name || f.text || safeStr(f)) : safeStr(f));
      continue;
    }
    // rows (spec_table): ensure 2D string array
    if (k === "rows" && Array.isArray(v)) {
      out[k] = v.map(row => {
        if (Array.isArray(row)) return row.map(c => safeStr(c));
        if (row && typeof row === "object") return Object.values(row).map(c => safeStr(c));
        return [safeStr(row)];
      });
      continue;
    }
    // columns/headers: string array
    if ((k === "columns" || k === "headers") && Array.isArray(v)) {
      out[k] = v.map(h => safeStr(h));
      continue;
    }
    // Recurse into arrays/objects
    if (Array.isArray(v)) {
      out[k] = v.map(item => {
        if (item == null) return "";
        if (typeof item === "object" && !Array.isArray(item)) return sanitizeSec(item, depth + 1);
        return item;
      });
    } else if (v && typeof v === "object") {
      out[k] = sanitizeSec(v, depth + 1);
    } else {
      out[k] = v;
    }
  }
  return out;
}

/* ── Individual section preview ── */
function PreviewSection({ sec: rawSec }) {
  const sec = sanitizeSec(rawSec) || rawSec;
  switch (sec.type) {
    case "design_canvas":
      return (
        <div className="px-6 py-8">
          {sec.title && <h2 className="text-xl font-bold text-gray-900 mb-3">{sec.title}</h2>}
          {sec.dataUrl ? (
            <img src={sec.dataUrl} alt={sec.title || "디자인"} className="w-full rounded-lg" style={{ maxWidth: "100%" }} />
          ) : (
            <div className="text-center text-gray-400 py-8">🎨 디자인 캔버스 (비어있음)</div>
          )}
        </div>
      );

    case "rich_html":
      return (
        <div className="px-6 py-8">
          {sec.title && <h2 className="text-xl font-bold text-gray-900 mb-3">{sec.title}</h2>}
          {sec.content && (
            <div className="prose prose-sm max-w-none product-detail-html" dangerouslySetInnerHTML={{ __html: sec.content }} />
          )}
        </div>
      );

    case "text":
      return (
        <div className="px-6 py-8">
          {sec.title && <h2 className="text-xl font-bold text-gray-900 mb-3">{sec.title}</h2>}
          {sec.content && (
            <div className="prose prose-sm max-w-none text-gray-600" dangerouslySetInnerHTML={{ __html: sec.content }} />
          )}
        </div>
      );

    case "product_details":
    case "feature_highlight":
      return (
        <div className="px-6 py-8 bg-gray-50">
          {sec.title && <h2 className="text-xl font-bold text-gray-900 text-center mb-6">{safeStr(sec.title)}</h2>}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {(sec.items || []).map((rawItem, j) => {
              const item = (rawItem && typeof rawItem === "object") ? rawItem : { title: safeStr(rawItem) };
              return (
                <div key={j} className="bg-white rounded-xl p-4 text-center shadow-sm">
                  <div className="text-2xl mb-2">{safeStr(item.icon) || "⚡"}</div>
                  {item.highlight_number && <div className="text-xl font-bold text-blue-600 mb-1">{safeStr(item.highlight_number)}</div>}
                  <div className="text-sm font-semibold text-gray-900">{safeStr(item.title)}</div>
                  <div className="text-xs text-gray-500 mt-1">{safeStr(item.description)}</div>
                </div>
              );
            })}
          </div>
        </div>
      );

    case "spec_table":
      return (
        <div className="px-6 py-8">
          {sec.title && <h2 className="text-xl font-bold text-gray-900 mb-4">{safeStr(sec.title)}</h2>}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  {(sec.columns || sec.headers || []).map((h, j) => (
                    <th key={j} className="px-4 py-2.5 text-left font-semibold text-gray-700 bg-gray-50">{safeStr(h)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(sec.rows || []).map((rawRow, r) => {
                  const row = Array.isArray(rawRow) ? rawRow : (rawRow && typeof rawRow === "object" ? Object.values(rawRow) : [safeStr(rawRow)]);
                  return (
                    <tr key={r} className="border-b border-gray-100 hover:bg-blue-50/30">
                      <td className="px-4 py-2.5 font-medium text-gray-900">{safeStr(row[0])}</td>
                      {row.slice(1).map((c, k) => <td key={k} className="px-4 py-2.5 text-gray-600">{safeStr(c)}</td>)}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      );

    case "product_card":
      return (
        <div className="px-6 py-8">
          <div className="flex flex-col sm:flex-row gap-6 items-center">
            {sec.image_url && (
              <div className="flex-shrink-0">
                <img src={sec.image_url} alt={sec.product_name} className="h-40 object-contain rounded-xl"
                  onError={e => { e.target.style.display = "none"; }} />
              </div>
            )}
            <div className="flex-1">
              {sec.product_name && <h3 className="text-lg font-bold text-gray-900">{safeStr(sec.product_name)}</h3>}
              {sec.tagline && <p className="text-sm italic text-blue-600 mt-1">"{safeStr(sec.tagline)}"</p>}
              {sec.description && <p className="text-sm text-gray-600 mt-2">{safeStr(sec.description)}</p>}
              {sec.features && sec.features.length > 0 && (
                <ul className="mt-3 space-y-1">
                  {sec.features.map((f, j) => <li key={j} className="text-sm text-gray-600 flex items-start gap-1.5">
                    <span className="text-blue-500 mt-0.5">•</span>{safeStr(f)}
                  </li>)}
                </ul>
              )}
              {sec.highlight_spec?.value && (
                <div className="mt-3 inline-flex items-baseline gap-1.5 bg-blue-50 px-3 py-1.5 rounded-lg">
                  <span className="text-xl font-bold text-blue-600">{safeStr(sec.highlight_spec.value)}</span>
                  <span className="text-xs text-gray-500">{safeStr(sec.highlight_spec.label)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      );

    case "rental_plans":
      return (
        <div className="px-6 py-8 bg-gradient-to-b from-purple-50 to-white">
          {sec.title && <h2 className="text-xl font-bold text-gray-900 text-center mb-6">{sec.title}</h2>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(sec.plans || []).map((plan, j) => {
              const allPrices = (sec.plans||[]).map(p => p.monthly_price||0).sort((a,b) => a-b);
              const midIdx = Math.floor(allPrices.length / 2);
              const isRecommended = allPrices.length >= 3 && (plan.monthly_price||0) === allPrices[midIdx];
              return (
              <div key={j} className={`bg-white border rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow relative ${isRecommended ? "border-purple-400 ring-2 ring-purple-200" : "border-purple-200"}`}>
                {isRecommended && <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-purple-600 text-white text-[10px] font-bold px-3 py-0.5 rounded-full">추천</span>}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="bg-purple-600 text-white text-[10px] font-bold w-6 h-6 rounded flex items-center justify-center">{plan.plan_no || j+1}</span>
                    <span className="text-sm font-bold">{plan.model_label || plan.plan_name}</span>
                  </div>
                  <span className="text-lg font-extrabold text-purple-700">
                    월 {(plan.monthly_price || 0).toLocaleString()}원
                  </span>
                </div>
                <hr className="border-purple-100 mb-3"/>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                  <div><span className="text-gray-400">보증금</span><br/><span className="font-semibold">{(plan.deposit||0).toLocaleString()}원</span></div>
                  <div><span className="text-gray-400">출력매수</span><br/><span className="font-semibold">{safeStr(plan.included_pages)||"-"}{plan.color_pages && <><br/>{safeStr(plan.color_pages)}</>}</span></div>
                  <div><span className="text-gray-400">약정</span><br/><span className="font-semibold">{safeStr(plan.contract_period)||"3년"}</span></div>
                  <div><span className="text-gray-400">옵션</span><br/><span className="font-semibold">{safeStr(plan.options)||"-"}</span></div>
                </div>
                {(plan.extra_bw_price || plan.extra_color_price) && (
                  <div className="mt-3 pt-2 border-t border-gray-100 text-[11px] text-gray-400">
                    초과단가: 흑백 {plan.extra_bw_price||15}원/장 · 컬러 {plan.extra_color_price||80}원/장
                  </div>
                )}
              </div>
            );})}
          </div>
        </div>
      );

    case "rental_calculator":
      return (
        <div className="px-6 py-8 bg-gray-50 text-center">
          {sec.title && <h2 className="text-xl font-bold text-gray-900 mb-3">{sec.title}</h2>}
          <p className="text-sm text-gray-600">{sec.description}</p>
          <div className="mt-4 inline-flex items-center gap-3 bg-white border rounded-xl px-4 py-3">
            <span className="text-sm text-gray-500">월간 출력매수</span>
            <input type="number" disabled className="w-24 text-center border rounded-lg px-2 py-1 text-sm bg-gray-50" placeholder="3,000" />
            <span className="text-sm text-gray-400">매</span>
          </div>
          {sec.note && <p className="text-xs text-gray-400 mt-2">* {sec.note}</p>}
        </div>
      );

    case "cta":
      return (
        <div className="px-6 py-10 bg-gradient-to-b from-blue-50 to-white text-center">
          {sec.title && <h2 className="text-xl font-bold text-gray-900 mb-2">{safeStr(sec.title)}</h2>}
          {sec.description && <p className="text-sm text-gray-500 mb-4">{safeStr(sec.description)}</p>}
          <span className="inline-block px-8 py-3 bg-blue-600 text-white font-semibold rounded-full text-sm shadow-lg">
            {safeStr(sec.button_text) || "문의하기"}
          </span>
        </div>
      );

    /* ── 판매 가격표 미리보기 ── */
    case "sale_pricing":
      return (
        <div className="px-6 py-8">
          {sec.title && <h2 className="text-xl font-bold text-gray-900 mb-4">{sec.title}</h2>}
          <div className="space-y-3">
            {(sec.products || []).map((p, j) => (
              <div key={j} className="flex items-center gap-4 p-3 border border-gray-200 rounded-xl hover:bg-gray-50">
                {p.image_url && (
                  <img src={p.image_url} alt={p.product_name} className="w-16 h-16 object-contain rounded-lg shrink-0"
                    onError={e => { e.target.style.display = "none"; }} />
                )}
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-bold text-gray-900 truncate">{p.product_name || "-"}</h4>
                  <span className="text-[10px] text-gray-500">{p.brand || "-"}</span>
                </div>
                <div className="text-right shrink-0 space-y-0.5">
                  <div className="text-sm font-bold text-red-600">{(p.body_price || 0).toLocaleString()}원</div>
                  <div className="text-[10px] text-gray-400">배송 서울 {(p.delivery_seoul || 0).toLocaleString()} / 기타 {(p.delivery_other || 0).toLocaleString()}</div>
                  {!!(p.fax_kit) && <div className="text-[10px] text-gray-400">FAX {(p.fax_kit || 0).toLocaleString()}원</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      );

    /* ── 렌탈 가격표 미리보기 ── */
    case "rental_pricing": {
      const columns = sec.custom_columns || [
        { key: "deposit_text", label: "보증금" },
        { key: "output_desc", label: "기본 출력매수" },
        { key: "contract_period", label: "약정기간" },
        { key: "options_desc", label: "선택사항" },
      ];
      return (
        <div className="px-6 py-8 bg-gradient-to-b from-purple-50 to-white">
          {sec.title && <h2 className="text-xl font-bold text-gray-900 text-center mb-6">{sec.title}</h2>}
          {(sec.products || []).map((p, j) => (
            <div key={j} className="mb-6">
              {/* Plan header */}
              <div className="bg-gradient-to-r from-purple-700 to-purple-600 rounded-t-xl px-5 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="bg-white/20 text-white text-xs font-bold w-7 h-7 rounded-lg flex items-center justify-center">{String(j+1).padStart(2,'0')}</span>
                  <span className="text-white font-bold text-sm">{p.product_name || "제품명"}</span>
                </div>
                <span className="text-white font-bold text-lg">월{(p.monthly_total || 0).toLocaleString()}원</span>
              </div>
              {/* Plan table */}
              <div className="border border-t-0 border-purple-200 rounded-b-xl overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50 border-b border-purple-200">
                      {columns.map((col, ci) => (
                        <th key={ci} className="px-3 py-2 text-center font-bold text-purple-700 text-[11px]">{col.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      {columns.map((col, ci) => (
                        <td key={ci} className="px-3 py-2.5 text-center text-xs text-gray-700">{safeStr((p.col_values || {})[col.key]) || "-"}</td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
              {sec.footer_note && (
                <p className="text-[10px] text-gray-400 mt-1.5 pl-1">{sec.footer_note}</p>
              )}
            </div>
          ))}
        </div>
      );
    }

    /* ── 토너/소모품 미리보기 ── */
    case "toner_spec":
      return (
        <div className="px-6 py-8">
          {sec.title && <h2 className="text-xl font-bold text-gray-900 mb-4">{sec.title}</h2>}
          <div className="grid grid-cols-1 gap-3">
            {(sec.products || []).map((p, j) => (
              <div key={j} className="flex items-center gap-4 p-3 border border-gray-200 rounded-xl hover:bg-gray-50">
                {p.image_url && (
                  <img src={p.image_url} alt={p.product_name} className="w-14 h-14 object-contain rounded-lg shrink-0"
                    onError={e => { e.target.style.display = "none"; }} />
                )}
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-bold text-gray-900 truncate">{p.product_name || "-"}</h4>
                  <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">{p.brand || "-"}</span>
                    {p.genuine_type && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${p.genuine_type === "정품" ? "bg-blue-100 text-blue-700" : "bg-yellow-100 text-yellow-700"}`}>
                        {p.genuine_type}
                      </span>
                    )}
                    {p.ink_type && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">{p.ink_type}</span>}
                    {p.color && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">{p.color}</span>}
                    {p.model_number && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-50 text-purple-600 font-mono">{p.model_number}</span>}
                  </div>
                </div>
                {!!(p.market_price) && (
                  <div className="text-right shrink-0">
                    <div className="text-base font-bold text-red-600">{p.market_price.toLocaleString()}원</div>
                    <div className="text-[10px] text-gray-400">시장가</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      );

    /* ── 이미지+텍스트 블록 미리보기 ── */
    case "image_text_block": {
      const pos = sec.image_position || "right";
      const fs = FONT_SIZE_MAP[sec.font_size] || FONT_SIZE_MAP.lg;
      const mediaEl = sec.video_url ? (
        <div className="flex-1 flex justify-center">
          <LoopVideo src={sec.video_url} className="max-h-64 rounded-xl object-cover" loop={sec.video_loop || "loop"} transition={sec.video_transition || "cut"} />
        </div>
      ) : (sec.image_url && sec.image_url.trim()) ? (
        <div className="flex-1 flex justify-center">
          <img src={sec.image_url} alt={sec.title} className="max-h-64 object-contain rounded-xl"
            onError={e => { e.target.style.display = "none"; }} />
        </div>
      ) : null;
      const textEl = (
        <div className="flex-1 space-y-3">
          {sec.title && <h2 className={`${fs.title} font-bold text-gray-900`}>{safeStr(sec.title)}</h2>}
          {sec.description && <p className={`${fs.desc} text-gray-600 leading-relaxed`}>{safeStr(sec.description)}</p>}
          {sec.features && sec.features.length > 0 && (
            <ul className="space-y-1.5 mt-2">
              {sec.features.map((f, j) => (
                <li key={j} className="flex items-start gap-2 text-sm text-gray-700">
                  <svg className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                  </svg>
                  {safeStr(f)}
                </li>
              ))}
            </ul>
          )}
          {sec.cta_text && (
            <div className="mt-3">
              <span className="text-sm font-semibold text-blue-600 hover:underline cursor-pointer">{sec.cta_text} ›</span>
            </div>
          )}
        </div>
      );
      return (
        <div className="px-6 py-8">
          {pos === "top" ? (
            <div className="space-y-6 text-center">{mediaEl}{textEl}</div>
          ) : (
            <div className={`flex flex-col sm:flex-row items-center gap-8 ${pos === "left" ? "" : "sm:flex-row-reverse"}`}>
              {mediaEl}{textEl}
            </div>
          )}
        </div>
      );
    }

    /* ── 제목+설명 블록 미리보기 ── */
    case "heading_text": {
      const bgMap = { white: "bg-white", gray: "bg-gray-50", dark: "bg-gray-900" };
      const isDark = sec.bg_style === "dark";
      const align = sec.align === "left" ? "text-left" : "text-center";
      const fs = FONT_SIZE_MAP[sec.font_size] || FONT_SIZE_MAP.xl;
      return (
        <div className={`px-6 py-10 ${bgMap[sec.bg_style] || "bg-white"} ${align}`}>
          {sec.heading && (
            <h2 className={`${fs.title} font-bold leading-tight ${isDark ? "text-white" : "text-gray-900"} ${sec.align !== "left" ? "max-w-3xl mx-auto" : ""}`}>
              {safeStr(sec.heading)}
            </h2>
          )}
          {sec.subheading && (
            <p className={`mt-2 ${fs.desc} font-medium ${isDark ? "text-gray-300" : "text-blue-600"}`}>{safeStr(sec.subheading)}</p>
          )}
          {sec.description && (
            <p className={`mt-3 ${fs.desc} leading-relaxed ${isDark ? "text-gray-400" : "text-gray-600"} ${sec.align !== "left" ? "max-w-2xl mx-auto" : ""}`}>
              {safeStr(sec.description)}
            </p>
          )}
        </div>
      );
    }

    /* ── 옵션 선택기 미리보기 (프리미엄 스타일) ── */
    case "product_options":
      return <ProductOptionsPreview sec={sec} />;

    /* ── 쇼케이스 미리보기 (프리미엄 2단) ── */
    case "product_showcase":
      return <ProductShowcasePreview sec={sec} />;

    /* ── 그리드 레이아웃 미리보기 ── */
    case "product_grid": {
      const { cols = 2, rows = 1 } = { "1x1": { cols: 1, rows: 1 }, "1x2": { cols: 1, rows: 2 }, "2x1": { cols: 2, rows: 1 }, "2x2": { cols: 2, rows: 2 }, "3x1": { cols: 3, rows: 1 }, "1x3": { cols: 1, rows: 3 } }[sec.gridLayout || "2x1"] || {};
      const cells = sec.cells || [];
      const totalCells = cols * rows;
      return (
        <div className="px-6 py-8">
          <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
            {cells.slice(0, totalCells).map((cell, ci) => (
              <div key={ci}>
                {(cell.blocks || []).sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0)).map((block, bi) => (
                  <div key={bi} className="mb-3">
                    <GridPreviewBlock block={block} />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      );
    }

    default: {
      /* 미지원 섹션 — 가능한 모든 필드를 제네릭 렌더링 */
      const hasItems = sec.items && sec.items.length > 0;
      const hasFeatures = sec.features && sec.features.length > 0;
      const hasSteps = sec.steps && sec.steps.length > 0;
      const hasNotices = sec.notices && sec.notices.length > 0;
      const hasProducts = sec.products && sec.products.length > 0;
      const hasContent = !!sec.content;
      const hasDescription = !!sec.description;
      const hasText = !!sec.text;
      const hasAny = hasItems || hasFeatures || hasSteps || hasNotices || hasProducts || hasContent || hasDescription || hasText;

      if (!hasAny) {
        return (
          <div className="px-6 py-4 bg-yellow-50 text-sm text-yellow-700">
            알 수 없는 섹션: {sec.type}
          </div>
        );
      }

      return (
        <div className="px-6 py-8">
          {sec.title && <h2 className="text-xl font-bold text-gray-900 mb-4">{safeStr(sec.title)}</h2>}
          {sec.subtitle && <p className="text-sm text-gray-500 mb-4">{safeStr(sec.subtitle)}</p>}
          {hasDescription && <p className="text-gray-600 mb-4 leading-relaxed">{safeStr(sec.description)}</p>}
          {hasText && <p className="text-gray-600 mb-4 leading-relaxed">{safeStr(sec.text)}</p>}
          {hasContent && <div className="prose prose-sm max-w-none text-gray-600 mb-4" dangerouslySetInnerHTML={{ __html: sec.content }} />}
          {hasFeatures && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              {sec.features.map((f, j) => {
                const ft = typeof f === "object" ? f : { title: safeStr(f) };
                return (
                  <div key={j} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <span className="text-lg">{safeStr(ft.icon) || "✅"}</span>
                    <div>
                      <div className="font-semibold text-sm text-gray-800">{safeStr(ft.title || ft.name)}</div>
                      {ft.description && <div className="text-xs text-gray-500 mt-0.5">{safeStr(ft.description)}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {hasItems && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
              {sec.items.map((rawItem, j) => {
                const item = (rawItem && typeof rawItem === "object") ? rawItem : { title: safeStr(rawItem) };
                return (
                  <div key={j} className="bg-white rounded-xl p-4 text-center shadow-sm border">
                    <div className="text-2xl mb-2">{safeStr(item.icon) || "⚡"}</div>
                    {item.highlight_number && <div className="text-xl font-bold text-blue-600 mb-1">{safeStr(item.highlight_number)}</div>}
                    <div className="font-semibold text-gray-800 text-sm">{safeStr(item.title)}</div>
                    {item.description && <div className="text-xs text-gray-500 mt-1 line-clamp-2">{safeStr(item.description)}</div>}
                  </div>
                );
              })}
            </div>
          )}
          {hasSteps && (
            <div className="space-y-3 mb-4">
              {sec.steps.map((step, j) => {
                const st = typeof step === "object" ? step : { title: safeStr(step) };
                return (
                  <div key={j} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <span className="bg-blue-100 text-blue-700 text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center shrink-0">{j + 1}</span>
                    <div>
                      <div className="font-semibold text-sm text-gray-800">{safeStr(st.title || st.name)}</div>
                      {st.description && <div className="text-xs text-gray-500 mt-0.5">{safeStr(st.description)}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {hasNotices && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <ul className="text-sm text-yellow-800 space-y-1 list-disc list-inside">
                {sec.notices.map((n, j) => <li key={j}>{safeStr(typeof n === "object" ? (n.text || n.title || JSON.stringify(n)) : n)}</li>)}
              </ul>
            </div>
          )}
          {hasProducts && (
            <div className="space-y-2">
              {sec.products.map((p, j) => (
                <div key={j} className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <div className="font-semibold text-sm">{safeStr(p.product_name || p.name)}</div>
                    {p.brand && <div className="text-xs text-gray-500">{safeStr(p.brand)}</div>}
                  </div>
                  <div className="text-right">
                    {!!(p.body_price || p.price) && <div className="text-sm font-bold text-red-600">{(p.body_price || p.price || 0).toLocaleString()}원</div>}
                    {!!p.monthly_total && <div className="text-sm font-bold text-purple-600">월 {(p.monthly_total || 0).toLocaleString()}원</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }
  }
}
